<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import * as THREE from 'three'
import {
  AnimationPlugin,
  CameraPlugin,
  ControlPlugin,
  PickingPlugin,
  RenderPlugin,
  type GaussianSplatPlugin,
  RenderPhase,
  GSPLAT_PARTICLE_MOTION_DEFAULTS,
  createGSplatEffectFromTemplate,
  createSemanticCameraMotion,
  createSemanticCameraMotionPlan,
  isSemanticCameraMotionLeafTemplate,
  parseGSplatEffectTemplate,
  parseSemanticCameraMotionTemplate,
  playSemanticCameraMotionPlan,
  presets,
  type AnimationClipData,
  type AnimationEasing,
  type AnimationInterpolation,
  type AnimationLoopMode,
  type AnimationPlayerHandle,
  type AnimationService,
  type AnimationTrackData,
  type CameraState,
  type CameraStatePatch,
  type GsplatRenderableDiagnostics,
  type GSplatEffectTemplate,
  type GSplatEffectTemplateBox3Like,
  type GSplatEffectTemplateColorControl,
  type GSplatEffectTemplateRuntimeContext,
  type GSplatParticleSizeMode,
  type GSplatParticleMotionType,
  type GSplatShaderEffectLike,
  type ManualFrameRequest,
  type RenderContributor,
  type SemanticCameraMotionTemplate,
  type SemanticCameraMotionLeafTemplate,
  type SemanticCameraMotionSequenceTemplate,
  type SemanticCameraMotionPlanPlayer,
  type SemanticCameraMotionPlan,
  type VideoRenderEngineLike,
  ViewPlugin,
} from '@sharefe/share3d-engine'
import { CAMERA_MOTION_TEMPLATES } from '../assets/cameraMotionTemplates/cameraMotionTemplates'
import { GSPLAT_EFFECT_TEMPLATES } from '../assets/effectTemplates/effectTemplates'
import { getDefaultPresetModelUrl, PRESET_MODELS } from '../examples/presetModels'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'
import { use3DGS } from '../hooks/use3DGS'
import {
  createConfiguredGsplatPlugin,
  configureGsplatPlugins,
  getGsplatGraphicsConfig,
  readGsplatBackendControlState,
} from '../utils/gsplatBackendControl'
import {
  GS3D_EFFECT_ANIMATION_PARTICLE_SCATTER_DEFAULTS,
  GS3D_EFFECT_ANIMATION_PRESET_DEFAULTS,
  GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS,
  cloneParticleMotion,
  cloneParticleScatterParams,
  createGS3DEffectAnimationPreset,
  parseAndApplyGS3DEffectAnimationPresetImport,
  presetColorControlToColorTuple,
  stringifyGS3DEffectAnimationPreset,
  type GS3DEffectAnimationPresetColorControl,
  type GS3DEffectAnimationPresetColorControlInput,
  type GS3DEffectAnimationPresetParticleScatter,
  type GS3DEffectAnimationPresetVisualShowcaseState,
  type GS3DEffectAnimationPresetState,
  type GS3DEffectAnimationPresetV1,
} from '../utils/gs3dEffectAnimationPreset'
import {
  applyGSplatEffectTemplateToPresetEffectState,
  getGSplatEffectTemplateBoxCropEdgeScaleFactor,
  parseGSplatEffectTemplateImportJson,
  type GSplatEffectTemplatePresetEffectState,
} from '../utils/gsplatEffectTemplateState'
import {
  GS3D_VIDEO_STREAM_MAX_DURATION_SECONDS,
  GS3D_VIDEO_STREAMING_THRESHOLD_SECONDS,
  createGs3dEffectAnimationClipTemplateDetails,
  exportGs3dEffectAnimationVideo,
  normalizeGs3dVideoExportSettings,
  pickGs3dVideoExportFileHandle,
  revokeGs3dVideoExportResult,
  type Gs3dMemoryVideoExportResult,
  type Gs3dVideoExportProgress,
  type Gs3dVideoExportResult,
  type Gs3dVideoExportSettings,
  type Gs3dVideoExportStage,
} from '../utils/gs3dVideoExport'
import {
  createGs3dClipTemplateApplier,
  type Gs3dClipTemplateDetails,
} from '../utils/gs3dClipTemplateApplier'
import {
  computeSceneBackgroundUvTransform,
  type SceneBackgroundImageFit,
} from '../utils/sceneBackgroundTexture'
import { downloadBlob, downloadUrl } from '../utils/downloadBlob'
import SceneAssetLoaderCard from './SceneAssetLoaderCard.vue'
import GSplatBackendControlCard from './GSplatBackendControlCard.vue'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'
import SceneBackgroundCard, { type SceneBackgroundMode } from './SceneBackgroundCard.vue'
import CameraMotionTemplateNodeEditor from './CameraMotionTemplateNodeEditor.vue'
import SceneControlPanel from './SceneControlPanel.vue'
import SceneGroupCard from './SceneGroupCard.vue'
import SceneObjectTransformCard from './SceneObjectTransformCard.vue'

type Vector3Tuple = [number, number, number]
type ColorTuple = [number, number, number]
type CameraEasingMode = 'none' | AnimationEasing
type CameraVectorField = 'position' | 'target'
type CameraVectorAxis = 0 | 1 | 2
type BoxSweepDirectionKey = 'z-up' | 'z-down' | 'x-plus' | 'x-minus' | 'y-plus' | 'y-minus'
type RevealPanelKey = 'radial' | 'rain' | 'grid'
type VisualShowcaseEffectKey =
  | 'visual-electronic'
  | 'visual-deep-meditation'
  | 'visual-waves'
  | 'visual-flare'
  | 'visual-disintegrate'
type ShowcaseRevealEffectKey =
  | 'reveal-magic'
  | 'reveal-spread'
  | 'reveal-unroll'
  | 'reveal-twister'
type DissolveEffectKey = 'dissolve-drift'
type ParticleEffectKey = 'particle-scatter-reassemble'
type CameraMotionSemanticKey = SemanticCameraMotionTemplate['semantic']

interface CameraInterpolationOption {
  value: AnimationInterpolation
  label: string
  detail: string
}

interface CameraEasingOption {
  value: CameraEasingMode
  label: string
  detail: string
}

interface CameraLoopModeOption {
  value: AnimationLoopMode
  label: string
  detail: string
}

interface CameraEasingGroup {
  title: string
  options: CameraEasingOption[]
}

interface CameraKeyframe {
  id: number
  time: number
  position: Vector3Tuple
  target: Vector3Tuple
  fov: number
  roll: number
}

type CameraFramePose = Pick<CameraKeyframe, 'position' | 'target' | 'fov'> & { roll?: number }
type ImportedCameraPathSide = 'left' | 'right'

interface ImportedCameraPathFrame {
  filePath: string
  timestamp: number | null
  matrix: number[][]
}

interface ImportedCameraPathState {
  fileName: string
  left: ImportedCameraPathFrame[]
  right: ImportedCameraPathFrame[]
}

interface CameraMotionTemplateOption {
  source: unknown
  template: SemanticCameraMotionTemplate | null
  key: string
  label: string
  detail: string
  error: string | null
}

interface CameraMotionSemanticOption {
  key: CameraMotionSemanticKey
  label: string
  detail: string
}

interface CameraMotionParamField {
  key: string
  label: string
  type: 'number' | 'select'
  min?: number
  max?: number
  step?: number
  rangeByDirection?: Record<string, { min: number; max: number }>
  options?: Array<{ value: string; label: string }>
}

interface CameraPlaybackOptions {
  autoplay?: boolean
  lockControls?: boolean
  resetToFirstFrameOnComplete?: boolean
  clearShaderEffectOnComplete?: boolean
}

interface CameraMotionPlanPlaybackOptions {
  clearShaderEffectOnComplete?: boolean
}

interface EffectTemplateOption {
  source: unknown
  template: GSplatEffectTemplate | null
  key: string
  label: string
  detail: string
  error: string | null
}

interface EffectTemplateOptionGroup {
  title: string
  options: EffectTemplateOption[]
}

type ShaderEffectOption = {
  key: string
  label: string
  detail: string
}

type RevealNumberField = {
  key: string
  label: string
  min: number
  max: number
  step: number
}

type RevealColorField = {
  key: string
  label: string
}

type RevealColorControl = {
  hex: string
  intensity: number
}

// 默认加载预设 model1
const DEFAULT_GS3D_MODEL = getDefaultPresetModelUrl()

const KEYFRAME_TIME_STEP = 2
const MIN_KEYFRAME_TIME_GAP = 0.01
const IMPORTED_CAMERA_TARGET_DISTANCE = 1
const CAMERA_FOV_MIN = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.cameraFov.min
const CAMERA_FOV_MAX = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.cameraFov.max
const DEFAULT_BOX_SIZE: number = GS3D_EFFECT_ANIMATION_PRESET_DEFAULTS.boxSize
const DEFAULT_SCENE_RADIUS = 32
const MIN_REVEAL_RADIUS = 10
const DEFAULT_BOX_DURATION: number = GS3D_EFFECT_ANIMATION_PRESET_DEFAULTS.boxDuration
const DEFAULT_BOX_CROP_EDGE_SCALE_FACTOR = 0.45
const BOX_CROP_EDGE_SCALE_FACTOR_MIN = 0
const BOX_CROP_EDGE_SCALE_FACTOR_MAX = 2
const DEFAULT_CAMERA_MOTION_TEMPLATE_DURATION = 6
const CAMERA_MOTION_TEMPLATE_DURATION_MIN = 0.5
const CAMERA_MOTION_TEMPLATE_DURATION_MAX = GS3D_VIDEO_STREAM_MAX_DURATION_SECONDS
const CAMERA_MOTION_SEMANTIC_OPTIONS: CameraMotionSemanticOption[] = [
  { key: 'dolly', label: 'Dolly 推进', detail: '沿视线拉近或拉远' },
  {
    key: 'truck',
    label: 'Truck 左右平移',
    detail: '相机与目标点同步左右移动并保持构图',
  },
  { key: 'orbit', label: 'Orbit 环绕', detail: '围绕目标点左右环绕' },
  { key: 'rise-overlook', label: 'Rise Overlook 上升俯看', detail: '上升并保持看向目标点' },
  { key: 'spiral', label: 'Spiral 螺旋', detail: '环绕、升降和半径变化组合' },
  { key: 'figure-eight', label: '8 字环绕', detail: '环绕中形成两瓣俯仰轨迹' },
  { key: 'sine-wave', label: '正弦高度起伏', detail: '按波次数上下起伏并环绕' },
  { key: 'pan-zoom-scan', label: '平移缩放扫描', detail: '横扫、俯仰与推拉组合' },
  {
    key: 'dolly-zoom',
    label: '希区柯克变焦（滑动变焦）',
    detail: '经典希区柯克变焦：距离与 FOV 反向联动以保持主体构图',
  },
  { key: 'butterfly', label: '球面蝴蝶', detail: '闭合双翼球面扫拂' },
  { key: 'turbulence', label: '谐波流体飞行', detail: '确定性多通道谐波环绕' },
  { key: 'dolly-roll', label: '推进滚转', detail: '沿视线推进并滚转画面' },
  { key: 'sequence', label: '组合运镜', detail: '按权重顺序播放多个可嵌套子运镜' },
]
const CAMERA_MOTION_PARAM_FIELDS: Record<string, CameraMotionParamField[]> = {
  dolly: [
    {
      key: 'direction',
      label: '方向',
      type: 'select',
      options: [
        { value: 'in', label: '拉近' },
        { value: 'out', label: '拉远' },
      ],
    },
    {
      key: 'distanceScale',
      label: '距离倍率',
      type: 'number',
      min: 0,
      max: 0.99,
      step: 0.01,
      rangeByDirection: { out: { min: 1.01, max: 10 } },
    },
  ],
  truck: [
    {
      key: 'direction',
      label: '平移方向',
      type: 'select',
      options: [
        { value: 'left', label: '向左' },
        { value: 'right', label: '向右' },
      ],
    },
    { key: 'translationScale', label: '平移倍率', type: 'number', min: 0, max: 5, step: 0.01 },
  ],
  orbit: [
    {
      key: 'direction',
      label: '方向',
      type: 'select',
      options: [
        { value: 'left', label: '左环绕' },
        { value: 'right', label: '右环绕' },
      ],
    },
    { key: 'angle', label: '角度', type: 'number', min: 1, max: 360, step: 1 },
    { key: 'keyframeCount', label: '关键帧数', type: 'number', min: 2, max: 24, step: 1 },
  ],
  'rise-overlook': [
    { key: 'distanceScale', label: '距离倍率', type: 'number', min: 0.25, max: 10, step: 0.01 },
    { key: 'heightScale', label: '高度倍率', type: 'number', min: 0, max: 5, step: 0.01 },
    { key: 'keyframeCount', label: '关键帧数', type: 'number', min: 2, max: 24, step: 1 },
  ],
  spiral: [
    {
      key: 'verticalDirection',
      label: '垂直方向',
      type: 'select',
      options: [
        { value: 'up', label: '上升' },
        { value: 'down', label: '下降' },
      ],
    },
    {
      key: 'rotationDirection',
      label: '旋转方向',
      type: 'select',
      options: [
        { value: 'clockwise', label: '顺时针' },
        { value: 'counterclockwise', label: '逆时针' },
      ],
    },
    {
      key: 'radiusMode',
      label: '半径模式',
      type: 'select',
      options: [
        { value: 'fixed', label: '固定' },
        { value: 'in', label: '收拢' },
        { value: 'out', label: '外扩' },
      ],
    },
    { key: 'angle', label: '角度', type: 'number', min: 1, max: 1440, step: 1 },
    { key: 'radiusScale', label: '半径倍率', type: 'number', min: 0.01, max: 10, step: 0.01 },
    { key: 'heightScale', label: '高度倍率', type: 'number', min: 0, max: 5, step: 0.01 },
    { key: 'keyframeCount', label: '关键帧数', type: 'number', min: 2, max: 24, step: 1 },
  ],
  'figure-eight': [
    {
      key: 'direction',
      label: '环绕方向',
      type: 'select',
      options: [
        { value: 'left', label: '向左' },
        { value: 'right', label: '向右' },
      ],
    },
    { key: 'angle', label: '环绕角度（°）', type: 'number', min: 1, max: 1440, step: 1 },
    {
      key: 'pitchAmplitude',
      label: '俯仰幅度（°）',
      type: 'number',
      min: 0,
      max: 60,
      step: 0.1,
    },
    { key: 'keyframeCount', label: '最低采样关键帧', type: 'number', min: 2, max: 120, step: 1 },
  ],
  'sine-wave': [
    {
      key: 'direction',
      label: '环绕方向',
      type: 'select',
      options: [
        { value: 'left', label: '向左' },
        { value: 'right', label: '向右' },
      ],
    },
    { key: 'angle', label: '环绕角度（°）', type: 'number', min: 1, max: 1440, step: 1 },
    {
      key: 'pitchAmplitude',
      label: '俯仰幅度（°）',
      type: 'number',
      min: 0,
      max: 60,
      step: 0.1,
    },
    { key: 'waveCount', label: '高度波次数', type: 'number', min: 1, max: 12, step: 1 },
    { key: 'keyframeCount', label: '最低采样关键帧', type: 'number', min: 2, max: 120, step: 1 },
  ],
  'pan-zoom-scan': [
    {
      key: 'direction',
      label: '扫描方向',
      type: 'select',
      options: [
        { value: 'left', label: '向左起扫' },
        { value: 'right', label: '向右起扫' },
      ],
    },
    {
      key: 'distanceAmplitude',
      label: '推拉幅度',
      type: 'number',
      min: 0,
      max: 0.75,
      step: 0.01,
    },
    {
      key: 'pitchAmplitude',
      label: '俯仰幅度（°）',
      type: 'number',
      min: 0,
      max: 60,
      step: 0.1,
    },
    {
      key: 'yawAmplitude',
      label: '横扫幅度（°）',
      type: 'number',
      min: 0,
      max: 180,
      step: 0.1,
    },
    { key: 'cycleCount', label: '扫描循环数', type: 'number', min: 1, max: 8, step: 1 },
    { key: 'keyframeCount', label: '最低采样关键帧', type: 'number', min: 2, max: 120, step: 1 },
  ],
  'dolly-zoom': [
    {
      key: 'direction',
      label: '环绕方向',
      type: 'select',
      options: [
        { value: 'left', label: '向左' },
        { value: 'right', label: '向右' },
      ],
    },
    { key: 'angle', label: '环绕角度（°）', type: 'number', min: 0, max: 1440, step: 1 },
    {
      key: 'distanceAmplitude',
      label: '推拉幅度',
      type: 'number',
      min: 0,
      max: 0.75,
      step: 0.01,
    },
    { key: 'pulseCount', label: '推拉脉冲数', type: 'number', min: 1, max: 8, step: 1 },
    {
      key: 'fovCompensation',
      label: 'FOV 补偿强度',
      type: 'number',
      min: 0,
      max: 1,
      step: 0.01,
    },
    { key: 'keyframeCount', label: '最低采样关键帧', type: 'number', min: 2, max: 120, step: 1 },
  ],
  butterfly: [
    {
      key: 'direction',
      label: '镜像方向',
      type: 'select',
      options: [
        { value: 'left', label: '向左展开' },
        { value: 'right', label: '向右展开' },
      ],
    },
    {
      key: 'pitchAmplitude',
      label: '俯仰幅度（°）',
      type: 'number',
      min: 0,
      max: 60,
      step: 0.1,
    },
    {
      key: 'yawAmplitude',
      label: '翼展幅度（°）',
      type: 'number',
      min: 0,
      max: 180,
      step: 0.1,
    },
    { key: 'wingCount', label: '翼对数', type: 'number', min: 1, max: 8, step: 1 },
    { key: 'keyframeCount', label: '最低采样关键帧', type: 'number', min: 2, max: 120, step: 1 },
  ],
  turbulence: [
    {
      key: 'direction',
      label: '环绕方向',
      type: 'select',
      options: [
        { value: 'left', label: '向左' },
        { value: 'right', label: '向右' },
      ],
    },
    { key: 'angle', label: '环绕角度（°）', type: 'number', min: 1, max: 1440, step: 1 },
    {
      key: 'distanceAmplitude',
      label: '距离扰动幅度',
      type: 'number',
      min: 0,
      max: 0.75,
      step: 0.01,
    },
    {
      key: 'pitchAmplitude',
      label: '俯仰扰动（°）',
      type: 'number',
      min: 0,
      max: 60,
      step: 0.1,
    },
    {
      key: 'yawAmplitude',
      label: '偏航扰动（°）',
      type: 'number',
      min: 0,
      max: 180,
      step: 0.1,
    },
    { key: 'harmonicCount', label: '谐波数', type: 'number', min: 2, max: 8, step: 1 },
    { key: 'keyframeCount', label: '最低采样关键帧', type: 'number', min: 2, max: 120, step: 1 },
  ],
  'dolly-roll': [
    { key: 'distanceScale', label: '结束距离倍率', type: 'number', min: 0.25, max: 1, step: 0.01 },
    {
      key: 'rollDirection',
      label: '滚转方向',
      type: 'select',
      options: [
        { value: 'clockwise', label: '顺时针' },
        { value: 'counterclockwise', label: '逆时针' },
      ],
    },
    { key: 'rollTurns', label: '滚转圈数', type: 'number', min: 0, max: 4, step: 0.05 },
    { key: 'keyframeCount', label: '最低采样关键帧', type: 'number', min: 2, max: 120, step: 1 },
  ],
}
const COLOR_INTENSITY_MAX = GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.colorIntensity.max
const COLOR_INTENSITY_STEP = 0.05
const DEFAULT_COLOR_INTENSITY = 1
const DEFAULT_VIDEO_EXPORT_SETTINGS = normalizeGs3dVideoExportSettings({
  width: 1280,
  height: 720,
  fps: 24,
  durationSeconds: 4,
  pixelRatio: 1,
  quality: 0.82,
})
const VIDEO_EXPORT_CAMERA_PLAYER_ID = 'gs3d-effect-animation-video-export-camera'
const VIDEO_EXPORT_MIN_STABLE_DELAY_MS = 120

const CAMERA_VECTOR_AXES: { label: string; axis: CameraVectorAxis }[] = [
  { label: 'X', axis: 0 },
  { label: 'Y', axis: 1 },
  { label: 'Z', axis: 2 },
]

const DEFAULT_CAMERA_KEYFRAMES: Omit<CameraKeyframe, 'id'>[] = [
  {
    time: 0,
    position: [4, -8, 5],
    target: [0, 0, 1],
    fov: 65,
    roll: 0,
  },
  {
    time: 2,
    position: [-6, -6, 4.5],
    target: [0, 0, 1.2],
    fov: 58,
    roll: 0,
  },
  {
    time: 4,
    position: [-7, 3, 4],
    target: [0, 0, 1],
    fov: 62,
    roll: 0,
  },
  {
    time: 6,
    position: [3, 7, 5],
    target: [0, 0, 1.1],
    fov: 70,
    roll: 0,
  },
  {
    time: 8,
    position: [4, -8, 5],
    target: [0, 0, 1],
    fov: 65,
    roll: 0,
  },
]

const CAMERA_INTERPOLATION_OPTIONS: CameraInterpolationOption[] = [
  {
    value: 'linear',
    label: '线性插值',
    detail: 'linear',
  },
  {
    value: 'spline',
    label: '样条插值',
    detail: 'spline smoothness 1',
  },
  {
    value: 'step',
    label: '阶梯切换',
    detail: 'step',
  },
]

const CAMERA_LOOP_MODE_OPTIONS: CameraLoopModeOption[] = [
  {
    value: 'once',
    label: '播放一次',
    detail: '末帧停止',
  },
  {
    value: 'repeat',
    label: '循环播放',
    detail: '回到起点继续',
  },
  {
    value: 'pingpong',
    label: '往返播放',
    detail: '正放后倒放',
  },
]

const CAMERA_EASING_GROUPS: CameraEasingGroup[] = [
  {
    title: '基础',
    options: [
      {
        value: 'none',
        label: '无缓动',
        detail: '原始时间进度',
      },
      {
        value: 'linear',
        label: '匀速',
        detail: 'linear',
      },
    ],
  },
  {
    title: '二次 Quadratic',
    options: [
      {
        value: 'quadraticIn',
        label: '缓入',
        detail: 'quadraticIn',
      },
      {
        value: 'quadraticOut',
        label: '缓出',
        detail: 'quadraticOut',
      },
      {
        value: 'quadraticInOut',
        label: '缓入缓出',
        detail: 'quadraticInOut',
      },
    ],
  },
  {
    title: '三次 Cubic',
    options: [
      {
        value: 'cubicIn',
        label: '缓入',
        detail: 'cubicIn',
      },
      {
        value: 'cubicOut',
        label: '缓出',
        detail: 'cubicOut',
      },
      {
        value: 'cubicInOut',
        label: '缓入缓出',
        detail: 'cubicInOut',
      },
    ],
  },
  {
    title: '四次 Quartic',
    options: [
      {
        value: 'quarticIn',
        label: '缓入',
        detail: 'quarticIn',
      },
      {
        value: 'quarticOut',
        label: '缓出',
        detail: 'quarticOut',
      },
      {
        value: 'quarticInOut',
        label: '缓入缓出',
        detail: 'quarticInOut',
      },
    ],
  },
  {
    title: '五次 Quintic',
    options: [
      {
        value: 'quinticIn',
        label: '缓入',
        detail: 'quinticIn',
      },
      {
        value: 'quinticOut',
        label: '缓出',
        detail: 'quinticOut',
      },
      {
        value: 'quinticInOut',
        label: '缓入缓出',
        detail: 'quinticInOut',
      },
    ],
  },
  {
    title: '正弦 Sine',
    options: [
      {
        value: 'sineIn',
        label: '柔和入',
        detail: 'sineIn',
      },
      {
        value: 'sineOut',
        label: '柔和出',
        detail: 'sineOut',
      },
      {
        value: 'sineInOut',
        label: '柔和进出',
        detail: 'sineInOut',
      },
    ],
  },
  {
    title: '指数 Expo',
    options: [
      {
        value: 'expoIn',
        label: '急加速入',
        detail: 'expoIn',
      },
      {
        value: 'expoOut',
        label: '急减速出',
        detail: 'expoOut',
      },
      {
        value: 'expoInOut',
        label: '指数进出',
        detail: 'expoInOut',
      },
    ],
  },
  {
    title: '圆形 Circ',
    options: [
      {
        value: 'circIn',
        label: '圆弧入',
        detail: 'circIn',
      },
      {
        value: 'circOut',
        label: '圆弧出',
        detail: 'circOut',
      },
      {
        value: 'circInOut',
        label: '圆弧进出',
        detail: 'circInOut',
      },
    ],
  },
  {
    title: '弹性 Elastic',
    options: [
      {
        value: 'elasticIn',
        label: '弹性入',
        detail: 'elasticIn',
      },
      {
        value: 'elasticOut',
        label: '弹性出',
        detail: 'elasticOut',
      },
      {
        value: 'elasticInOut',
        label: '弹性进出',
        detail: 'elasticInOut',
      },
    ],
  },
  {
    title: '回弹 Back',
    options: [
      {
        value: 'backIn',
        label: '回拉入',
        detail: 'backIn',
      },
      {
        value: 'backOut',
        label: '回弹出',
        detail: 'backOut',
      },
      {
        value: 'backInOut',
        label: '回弹进出',
        detail: 'backInOut',
      },
    ],
  },
  {
    title: '弹跳 Bounce',
    options: [
      {
        value: 'bounceIn',
        label: '弹跳入',
        detail: 'bounceIn',
      },
      {
        value: 'bounceOut',
        label: '弹跳出',
        detail: 'bounceOut',
      },
      {
        value: 'bounceInOut',
        label: '弹跳进出',
        detail: 'bounceInOut',
      },
    ],
  },
]

const CAMERA_EASING_OPTIONS = CAMERA_EASING_GROUPS.flatMap((group) => group.options)
const CAMERA_MOTION_EASING_OPTIONS = CAMERA_EASING_OPTIONS.filter((option) =>
  option.value !== 'none'
)
const CAMERA_MOTION_OPTIONAL_EASING_OPTIONS = CAMERA_EASING_OPTIONS

const BOX_SWEEP_DIRECTIONS: Array<{
  key: BoxSweepDirectionKey
  label: string
  vector: Vector3Tuple
}> = [
  { key: 'z-up', label: '上', vector: [0, 0, 1] },
  { key: 'z-down', label: '下', vector: [0, 0, -1] },
  { key: 'x-plus', label: 'X+', vector: [1, 0, 0] },
  { key: 'x-minus', label: 'X-', vector: [-1, 0, 0] },
  { key: 'y-plus', label: 'Y+', vector: [0, 1, 0] },
  { key: 'y-minus', label: 'Y-', vector: [0, -1, 0] },
]

const BOX_COLOR_PRESETS = [
  { label: '青', value: '#00ffff' },
  { label: '黄', value: '#ffff00' },
  { label: '橙', value: '#ff8a24' },
  { label: '蓝', value: '#4aa3ff' },
  { label: '粉', value: '#ff6ad5' },
]

const VISUAL_SHOWCASE_EFFECT_KEYS = [
  'visual-electronic',
  'visual-deep-meditation',
  'visual-waves',
  'visual-flare',
  'visual-disintegrate',
] as const satisfies readonly VisualShowcaseEffectKey[]
const SHOWCASE_REVEAL_EFFECT_KEYS = [
  'reveal-magic',
  'reveal-spread',
  'reveal-unroll',
  'reveal-twister',
] as const satisfies readonly ShowcaseRevealEffectKey[]
const DISSOLVE_EFFECT_KEYS = ['dissolve-drift'] as const satisfies readonly DissolveEffectKey[]
const PARTICLE_EFFECT_KEYS = [
  'particle-scatter-reassemble',
] as const satisfies readonly ParticleEffectKey[]

const containerRef = useTemplateRef('containerRef')
const presetInputRef = ref<HTMLInputElement | null>(null)
const effectTemplateInputRef = ref<HTMLInputElement | null>(null)
const cameraMotionTemplateInputRef = ref<HTMLInputElement | null>(null)
const cameraPathInputRef = ref<HTMLInputElement | null>(null)
const backendControlState = readGsplatBackendControlState()

const graphicsCard = useExampleGraphicsCard({
  presetKey: 'pointCloudAndGSplat',
  extras: ['AnimationPlugin'],
})
const plugins =
  backendControlState.backend === 'webgpu'
    ? [
        new ViewPlugin(),
        new CameraPlugin(),
        new PickingPlugin(),
        new ControlPlugin(),
        createConfiguredGsplatPlugin(backendControlState),
        new RenderPlugin(),
        new AnimationPlugin(),
      ]
    : [...configureGsplatPlugins(presets.pointCloudAndGSplat(), backendControlState), new AnimationPlugin()]

const {
  engine,
  isLoading,
  modelInput,
  stats,
  init,
  loadModel,
  removeAllModels,
  autoLoad,
} = use3DGS(containerRef, {
  plugins,
  defaultModelUrl: DEFAULT_GS3D_MODEL,
  autoLoadFromUrl: true,
  graphics: getGsplatGraphicsConfig(backendControlState),
  fov: 65,
  background: 'black',
  fitOnLoad: false,
})

const loadError = ref('')
const sceneBackgroundMode = ref<SceneBackgroundMode>('color')
const sceneBackgroundColor = ref('#000000')
const sceneBackgroundImageName = ref('')
const sceneBackgroundImageLoading = ref(false)
const sceneBackgroundMessage = ref('')
const sceneBackgroundImageFit = ref<SceneBackgroundImageFit>('cover')
const sceneBackgroundImageZoom = ref(1)
const sceneBackgroundImageOffsetX = ref(0)
const sceneBackgroundImageOffsetY = ref(0)
const sceneBackgroundImageRotation = ref(0)
const sceneBackgroundImageFillColor = ref('#000000')
let sceneBackgroundSourceTexture: THREE.Texture | null = null
let sceneBackgroundTexture: THREE.Texture | null = null
let sceneBackgroundDerivedTexture: THREE.CanvasTexture | null = null
let sceneBackgroundDerivedKey = ''
let sceneBackgroundTransformKey = ''
let sceneBackgroundLoadToken = 0
let sceneBackgroundResizeObserver: ResizeObserver | null = null
const presetIoMessage = ref('')
const videoExportMessage = ref('')
const videoExportStage = ref<Gs3dVideoExportStage>('idle')
const videoExportProgress = ref(0)
const videoExportCurrentFrame = ref(0)
const videoExportTotalFrames = ref(0)
const videoExportResult = ref<Gs3dVideoExportResult | null>(null)
const videoExportViewportSnapshotUrl = ref('')
const videoExportAbortController = ref<AbortController | null>(null)
const videoExportSettings = ref<Gs3dVideoExportSettings>({ ...DEFAULT_VIDEO_EXPORT_SETTINGS })
const importedCameraPath = ref<ImportedCameraPathState | null>(null)
const cameraPathImportMessage = ref('')
const cameraMotionTemplateDuration = ref(DEFAULT_CAMERA_MOTION_TEMPLATE_DURATION)
const cameraMotionTemplateMessage = ref('')
const selectedCameraMotionSemantic = ref<CameraMotionSemanticKey>('dolly')
const importedCameraMotionTemplate = ref<SemanticCameraMotionTemplate | null>(null)
const cameraMotionSequenceTemplate = ref<SemanticCameraMotionSequenceTemplate | null>(null)
const cameraMotionTemplateName = ref('dolly')
const cameraMotionTemplateDescription = ref('')
const cameraMotionTemplateParams = ref<Record<string, string | number>>({})
const cameraMotionTemplateInterpolation = ref<AnimationInterpolation>('spline')
const cameraMotionTemplateEasing = ref<CameraEasingMode>('linear')
const cameraMotionTemplateSegmentEasingFirst = ref<CameraEasingMode>('quadraticIn')
const cameraMotionTemplateSegmentEasingLast = ref<CameraEasingMode>('quadraticOut')
const cameraMotionTemplateHoldDuration = ref(1)
const effectTemplateMessage = ref('')
const player = ref<AnimationPlayerHandle | null>(null)
const cameraMotionPlanPlayer = shallowRef<SemanticCameraMotionPlanPlayer | null>(null)
const playerState = ref<'idle' | 'playing' | 'paused' | 'completed'>('idle')
const cameraControlsInputLocked = ref(false)
const cameraPlaybackFallbackTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const cameraPlaybackReturnState = ref<CameraState | null>(null)
let cameraPlaybackToken = 0
const interpolationMode = ref<AnimationInterpolation>('linear')
const easingMode = ref<CameraEasingMode>('sineInOut')
const loopMode = ref<AnimationLoopMode>('once')
const currentCameraFov = ref(65)
const boxSize = ref(DEFAULT_BOX_SIZE)
const boxDirectionKey = ref<BoxSweepDirectionKey>('z-up')
const boxDurationSeconds = ref(DEFAULT_BOX_DURATION)
const boxTintColor = ref('#00ffff')
const boxEdgeColor = ref('#ffff00')
const boxCropEdgeScaleFactor = ref(DEFAULT_BOX_CROP_EDGE_SCALE_FACTOR)
const selectedEffectKey = ref('reveal-radial')
const selectedRevealAdaptive = ref(true)
const activeEffectKey = ref<string | null>(null)
const activeEffectName = ref('未启用')

const radialRevealParams = ref({
  duration: 4,
  dotTint: [0, 1, 1] as ColorTuple,
  waveTint: [1, 0.5, 0] as ColorTuple,
  intensity: 1,
})
const obbRevealParams = ref({
  duration: 4,
  dotTint: [0, 1, 1] as ColorTuple,
  waveTint: [1, 0.5, 0] as ColorTuple,
  intensity: 1,
})
const rainRevealParams = ref({
  duration: 4,
  fallTint: [0, 1, 1] as ColorTuple,
  fallTintIntensity: 0.2,
  hitTint: [2, 0, 0] as ColorTuple,
  intensity: 1,
})
const gridRevealParams = ref({
  duration: 3,
  moveTint: [1, 0, 1] as ColorTuple,
  moveTintIntensity: 0.2,
  landTint: [2, 2, 0] as ColorTuple,
  intensity: 1,
})
const radialRevealManualParams = ref({
  speed: 3.5,
  acceleration: 0,
  delay: 0.15,
  dotTint: [0, 1, 1] as ColorTuple,
  waveTint: [1, 0.5, 0] as ColorTuple,
  oscillationIntensity: 0.2,
  bandWidth: 1,
})
const obbRevealManualParams = ref({
  speed: 3.5,
  acceleration: 0,
  delay: 0.15,
  dotTint: [0, 1, 1] as ColorTuple,
  waveTint: [1, 0.5, 0] as ColorTuple,
  oscillationIntensity: 0.2,
  bandWidth: 1,
})
const rainRevealManualParams = ref({
  distance: 8,
  speed: 8,
  acceleration: 0,
  flightTime: 2,
  rainSize: 0.015,
  rotation: 0.9,
  fallTint: [0, 1, 1] as ColorTuple,
  fallTintIntensity: 0.2,
  hitTint: [2, 0, 0] as ColorTuple,
  hitDuration: 0.5,
})
const gridRevealManualParams = ref({
  blockSize: 2,
  delay: 0.04,
  duration: 1,
  dotSize: 0.01,
  moveTint: [1, 0, 1] as ColorTuple,
  moveTintIntensity: 0.2,
  landTint: [2, 2, 0] as ColorTuple,
  landDuration: 0.6,
})
const visualShowcaseParams = ref<Record<
  VisualShowcaseEffectKey,
  GS3DEffectAnimationPresetVisualShowcaseState
>>({
  'visual-electronic': { intensity: 1.15, timeScale: 1.2, swayAmplitude: 0.2, swaySpeed: 2, flickerColor: { hex: '#0080b3', intensity: 1 } },
  'visual-deep-meditation': { intensity: 0.85, timeScale: 0.8 },
  'visual-waves': { intensity: 1, timeScale: 1 },
  'visual-flare': { intensity: 1.1, timeScale: 1 },
  'visual-disintegrate': { intensity: 0.9, timeScale: 0.9 },
})
const showcaseRevealParams = ref<Record<
  ShowcaseRevealEffectKey,
  { timeScale: number; motionScale: number; duration: number }
>>({
  'reveal-magic': { timeScale: 1, motionScale: 1, duration: 6 },
  'reveal-spread': { timeScale: 1, motionScale: 1, duration: 6 },
  'reveal-unroll': { timeScale: 1, motionScale: 1, duration: 15 },
  'reveal-twister': { timeScale: 1, motionScale: 1.2, duration: 6 },
})
const dissolveParams = ref<Record<DissolveEffectKey, { speed: number; motionScale: number }>>({
  'dissolve-drift': { speed: 1, motionScale: 1 },
})
const particleParams = ref<Record<ParticleEffectKey, GS3DEffectAnimationPresetParticleScatter>>({
  'particle-scatter-reassemble': cloneParticleScatterParams(
    GS3D_EFFECT_ANIMATION_PARTICLE_SCATTER_DEFAULTS,
  ),
})

const revealColorControls = ref<Record<RevealPanelKey, Record<string, RevealColorControl>>>({
  radial: {
    dotTint: createColorControl(radialRevealParams.value.dotTint),
    waveTint: createColorControl(radialRevealParams.value.waveTint),
  },
  rain: {
    fallTint: createColorControl(rainRevealParams.value.fallTint),
    hitTint: createColorControl(rainRevealParams.value.hitTint),
  },
  grid: {
    moveTint: createColorControl(gridRevealParams.value.moveTint),
    landTint: createColorControl(gridRevealParams.value.landTint),
  },
})
const obbRevealColorControls = ref<Record<string, RevealColorControl>>({
  dotTint: createColorControl(obbRevealParams.value.dotTint),
  waveTint: createColorControl(obbRevealParams.value.waveTint),
})
const revealManualColorControls = ref<Record<RevealPanelKey, Record<string, RevealColorControl>>>({
  radial: {
    dotTint: createColorControl(radialRevealManualParams.value.dotTint),
    waveTint: createColorControl(radialRevealManualParams.value.waveTint),
  },
  rain: {
    fallTint: createColorControl(rainRevealManualParams.value.fallTint),
    hitTint: createColorControl(rainRevealManualParams.value.hitTint),
  },
  grid: {
    moveTint: createColorControl(gridRevealManualParams.value.moveTint),
    landTint: createColorControl(gridRevealManualParams.value.landTint),
  },
})
const obbRevealManualColorControls = ref<Record<string, RevealColorControl>>({
  dotTint: createColorControl(obbRevealManualParams.value.dotTint),
  waveTint: createColorControl(obbRevealManualParams.value.waveTint),
})

let nextKeyframeId = 1
let nextCameraPlayerId = 1
let activeShaderEffect: GSplatShaderEffectLike | null = null
let particleEffectCompletionTimer: ReturnType<typeof setTimeout> | null = null
let worldAxesScene: THREE.Scene | null = null
let worldAxesHelper: THREE.AxesHelper | null = null
let worldAxesContributor: RenderContributor | null = null

const cameraKeyframes = ref(DEFAULT_CAMERA_KEYFRAMES.map(createKeyframe))
const cameraSegmentEasing = ref<AnimationEasing[] | null>(null)
const selectedKeyframeId = ref<number | null>(cameraKeyframes.value[0]?.id ?? null)
const cameraMotionPlan = shallowRef<SemanticCameraMotionPlan | null>(null)
const selectedCameraMotionPlanSegmentIndex = ref(0)

const selectedBoxDirection = computed(() => {
  return BOX_SWEEP_DIRECTIONS.find((direction) => direction.key === boxDirectionKey.value) ?? BOX_SWEEP_DIRECTIONS[0]
})
const normalizedBoxSize = computed(() => {
  const size = Number(boxSize.value)
  return Number.isFinite(size) ? size : DEFAULT_BOX_SIZE
})
const normalizedBoxDuration = computed(() => {
  const duration = Number(boxDurationSeconds.value)
  return Number.isFinite(duration) ? duration : DEFAULT_BOX_DURATION
})
const normalizedCameraMotionTemplateDuration = computed(() => {
  const duration = Number(cameraMotionTemplateDuration.value)
  return Number.isFinite(duration) ? duration : DEFAULT_CAMERA_MOTION_TEMPLATE_DURATION
})
const effectTemplateOptions = computed<EffectTemplateOption[]>(() => {
  return GSPLAT_EFFECT_TEMPLATES.map((source, index) => {
    try {
      const template = parseGSplatEffectTemplate(source)
      return {
        source,
        template,
        key: template.id,
        label: template.name,
        detail: template.description ?? findEffectOption(template.effectKey)?.label ?? template.effectKey,
        error: null,
      }
    } catch (error) {
      return {
        source,
        template: null,
        key: `invalid-effect-${index}`,
        label: `特效模板 ${index + 1}`,
        detail: 'JSON 解析失败',
        error: getErrorMessage(error),
      }
    }
  })
})
const effectTemplateSummary = computed(() => {
  const total = effectTemplateOptions.value.length
  const validCount = effectTemplateOptions.value.filter((option) => !option.error).length
  return `${validCount}/${total} 个`
})
const effectTemplateGroups = computed<EffectTemplateOptionGroup[]>(() => {
  const groups: EffectTemplateOptionGroup[] = [
    { title: '显现动画', options: [] },
    { title: '展示显现', options: [] },
    { title: '视觉变形', options: [] },
    { title: '粒子生命周期', options: [] },
    { title: '消散', options: [] },
    { title: '其他', options: [] },
  ]

  for (const option of effectTemplateOptions.value) {
    groups[getEffectTemplateGroupIndex(option.template)].options.push(option)
  }

  return groups.filter((group) => group.options.length > 0)
})
const cameraMotionTemplateOptions = computed<CameraMotionTemplateOption[]>(() => {
  return CAMERA_MOTION_TEMPLATES.map((source, index) => {
    try {
      const template = parseSemanticCameraMotionTemplate(source)
      return {
        source,
        template,
        key: template.id,
        label: template.name,
        detail: template.description ?? template.semantic,
        error: null,
      }
    } catch (error) {
      return {
        source,
        template: null,
        key: `invalid-${index}`,
        label: `运镜模板 ${index + 1}`,
        detail: 'JSON 解析失败',
        error: getErrorMessage(error),
      }
    }
  })
})
const cameraMotionLeafTemplates = computed<SemanticCameraMotionLeafTemplate[]>(() => {
  return cameraMotionTemplateOptions.value.flatMap((option) => {
    const template = option.template
    return template && isSemanticCameraMotionLeafTemplate(template) ? [template] : []
  })
})
const selectedCameraMotionSemanticOption = computed(() => {
  return CAMERA_MOTION_SEMANTIC_OPTIONS.find((option) =>
    option.key === selectedCameraMotionSemantic.value
  ) ?? CAMERA_MOTION_SEMANTIC_OPTIONS[0]
})
const selectedCameraMotionTemplate = computed(() => {
  return importedCameraMotionTemplate.value?.semantic === selectedCameraMotionSemantic.value ?
    importedCameraMotionTemplate.value :
    cameraMotionTemplateOptions.value.find((option) =>
      option.template?.semantic === selectedCameraMotionSemantic.value && !option.error
    )?.template ?? null
})
const cameraMotionTemplateSummary = computed(() => {
  const duration = normalizedCameraMotionTemplateDuration.value.toFixed(1)
  return `${selectedCameraMotionSemanticOption.value.label} / ${duration}s`
})
const isSelectedCameraMotionSequence = computed(() =>
  selectedCameraMotionSemantic.value === 'sequence'
)
const selectedCameraMotionParamFields = computed(() => {
  return CAMERA_MOTION_PARAM_FIELDS[selectedCameraMotionSemantic.value] ?? []
})
const selectedCameraMotionTemplateMeta = computed(() => {
  const template = selectedCameraMotionTemplate.value
  if (!template) {
    return []
  }

  return [
    { label: '类型', value: formatCameraMotionSemantic(template.semantic) },
    { label: '来源', value: importedCameraMotionTemplate.value === template ? '导入模板' : '内置模板' },
  ]
})
const boxBoundsLabel = computed(() => {
  return `目标盒 ${normalizedBoxSize.value.toFixed(1)}m`
})
const boxSettingsSummary = computed(() => {
  return `${boxBoundsLabel.value} / ${selectedBoxDirection.value.label} / ${normalizedBoxDuration.value.toFixed(1)}s`
})
const obbSettingsSummary = computed(() => {
  return '视角前方 / 模型范围'
})
const activeInterpolationOption = computed(() => {
  return CAMERA_INTERPOLATION_OPTIONS.find((item) => item.value === interpolationMode.value)!
})
const effectiveEasingMode = computed<CameraEasingMode>(() => {
  return interpolationMode.value === 'step' ? 'none' : easingMode.value
})
const activeEasingOption = computed(() => {
  return CAMERA_EASING_OPTIONS.find((item) => item.value === effectiveEasingMode.value)!
})
const activeLoopModeOption = computed(() => {
  return CAMERA_LOOP_MODE_OPTIONS.find((item) => item.value === loopMode.value)!
})
const cameraClipDuration = computed(() => {
  const last = cameraKeyframes.value[cameraKeyframes.value.length - 1]
  return last ? toFiniteNumber(last.time) : 0
})
const cameraExportTimelineDuration = computed(() =>
  cameraMotionPlan.value?.duration ?? cameraClipDuration.value
)
const hasCameraPlaybackSource = computed(() => {
  return cameraMotionPlan.value !== null || cameraKeyframes.value.length > 0
})
const selectedCameraMotionPlanSegment = computed(() => {
  return cameraMotionPlan.value?.segments[selectedCameraMotionPlanSegmentIndex.value] ?? null
})
const cameraTimelineCardTitle = computed(() => {
  return cameraMotionPlan.value ? '组合镜头编排' : '关键帧编辑'
})
const cameraTimelineCardSubtitle = computed(() => {
  return cameraMotionPlan.value ?
    '按独立 clip 检查组合片段与局部关键帧，片段边界保持硬切' :
    '从当前相机捕获关键帧，或直接编辑时间、位置、目标和 FOV'
})
const cameraTimelineCardBadge = computed(() => {
  const plan = cameraMotionPlan.value
  return plan ? `${plan.segments.length} 个片段` : `${cameraKeyframes.value.length} 帧`
})
const selectedKeyframeIndex = computed(() => {
  return selectedKeyframeId.value === null ?
    -1 :
    cameraKeyframes.value.findIndex((frame) => frame.id === selectedKeyframeId.value)
})
const selectedKeyframe = computed(() => {
  const index = selectedKeyframeIndex.value
  return index >= 0 ? cameraKeyframes.value[index] : null
})
const selectedEffectOption = computed(() => {
  return findEffectOption(selectedEffectKey.value) ?? revealEffectGroup.effects[0]
})
const activeOrSelectedEffectOption = computed(() => {
  return (activeEffectKey.value ? findEffectOption(activeEffectKey.value) : null) ?? selectedEffectOption.value
})
const animationModeMeta = computed(() => {
  const plan = cameraMotionPlan.value
  if (plan) {
    return [selectedEffectOption.value.label, '组合运镜', `${plan.segments.length} 个独立 clip`].join(' / ')
  }
  return [
    selectedEffectOption.value.label,
    activeInterpolationOption.value.label,
    activeEasingOption.value.label,
  ].join(' / ')
})
const panelMeta = computed(() => {
  return `${stats.value.totalModels} 个模型 / ${animationStatus.value}`
})
const firstGsplatObject = ref<THREE.Object3D | null>(null)
const firstGsplatPivot = ref<THREE.Vector3 | null>(null)
const worldAxesVisible = ref(true)
const modelLoaderHint = computed(() => {
  if (loadError.value) {
    return `加载失败：${loadError.value}`
  }

  return '可选择包含 lod-meta.json / meta.json 的本地目录，也可直接选择 .ply 高斯模型文件。'
})
const animationStatus = computed(() => {
  switch (playerState.value) {
    case 'playing':
      return '播放中'
    case 'paused':
      return '已暂停'
    case 'completed':
      return '已完成'
    default:
      return '未播放'
  }
})
const videoExportStatus = computed(() => {
  if (videoExportStage.value === 'idle') {
    return '待导出'
  }
  if (videoExportStage.value === 'complete') {
    return '已完成'
  }
  if (videoExportStage.value === 'cancelled') {
    return '已取消'
  }
  if (videoExportStage.value === 'error') {
    return '失败'
  }
  return `${videoExportCurrentFrame.value}/${videoExportTotalFrames.value || 0} 帧`
})
const isVideoExporting = computed(() => {
  return videoExportAbortController.value !== null
})
const videoExportProgressPercent = computed(() => {
  return Math.round(videoExportProgress.value * 100)
})
const combinedStatusItems = computed(() => {
  const plan = cameraMotionPlan.value
  return [
    {
      label: '选中特效',
      value: selectedEffectOption.value.label,
    },
    {
      label: '运行特效',
      value: activeEffectName.value,
    },
    {
      label: '镜头状态',
      value: animationStatus.value,
    },
    plan ? {
      label: '组合镜头',
      value: `${plan.diagnostics.leafCount} 个子运镜 / ${plan.segments.length} 个片段 / ${formatNumber(plan.duration)}s`,
    } : {
      label: '关键帧',
      value: `${cameraKeyframes.value.length} 帧 / ${formatNumber(cameraClipDuration.value)}s`,
    },
  ]
})

const effectSelectGroups = computed(() => [
  boxEffectGroup,
  revealEffectGroup,
  {
    title: '展示显现',
    mode: '循环展示',
    effects: showcaseRevealEffects,
  },
  visualEffectGroup,
  particleEffectGroup,
  dissolveEffectGroup,
  {
    title: '视角推进',
    mode: '模型范围',
    effects: [ADAPTIVE_OBB_EFFECT],
  },
])

const selectedBoxEffect = computed(() => {
  return selectedEffectKey.value.startsWith('box-') ? selectedEffectOption.value : null
})
const selectedRevealPanel = computed(() => {
  return REVEAL_PARAM_PANELS.find((panel) => panel.effect.key === selectedEffectKey.value) ?? null
})
const selectedShowcaseRevealEffect = computed(() => {
  return showcaseRevealEffects.find((effect) => effect.key === selectedEffectKey.value) ?? null
})
const selectedVisualDissolveEffect = computed(() => {
  if (isVisualShowcaseEffectKey(selectedEffectKey.value) || isDissolveEffectKey(selectedEffectKey.value)) {
    return selectedEffectOption.value
  }
  return null
})
const selectedParticleEffect = computed(() => {
  return particleEffectGroup.effects.find((effect) => effect.key === selectedEffectKey.value) ?? null
})
const isSelectedObbEffect = computed(() => {
  return selectedEffectKey.value === ADAPTIVE_OBB_EFFECT.key
})

const RADIAL_REVEAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'duration', label: 'duration（总时长）', min: 1.2, max: 20, step: 0.1 },
  { key: 'intensity', label: 'intensity（整体强度）', min: 0, max: 3, step: 0.01 },
]
const RADIAL_REVEAL_MANUAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'speed', label: 'speed（速度）', min: 0, max: 20, step: 0.01 },
  { key: 'acceleration', label: 'acceleration（加速度）', min: 0, max: 10, step: 0.01 },
  { key: 'delay', label: 'delay（延迟）', min: 0, max: 10, step: 0.01 },
  { key: 'oscillationIntensity', label: 'oscillationIntensity（振荡强度）', min: 0, max: 1, step: 0.01 },
  { key: 'bandWidth', label: 'bandWidth（波带宽度）', min: 0.1, max: 5, step: 0.01 },
]
const RADIAL_REVEAL_COLOR_FIELDS: RevealColorField[] = [
  { key: 'dotTint', label: 'dotTint（点颜色）' },
  { key: 'waveTint', label: 'waveTint（波峰颜色）' },
]
const OBB_REVEAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'duration', label: 'duration（总时长）', min: 1.2, max: 20, step: 0.1 },
  { key: 'intensity', label: 'intensity（整体强度）', min: 0, max: 3, step: 0.01 },
]
const OBB_REVEAL_MANUAL_NUMBER_FIELDS = RADIAL_REVEAL_MANUAL_NUMBER_FIELDS
const OBB_REVEAL_COLOR_FIELDS: RevealColorField[] = [
  { key: 'dotTint', label: 'dotTint（点颜色）' },
  { key: 'waveTint', label: 'waveTint（波峰颜色）' },
]
const RAIN_REVEAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'duration', label: 'duration（总时长）', min: 1.2, max: 20, step: 0.1 },
  { key: 'fallTintIntensity', label: 'fallTintIntensity（下落染色强度）', min: 0, max: 1, step: 0.01 },
  { key: 'intensity', label: 'intensity（整体强度）', min: 0, max: 3, step: 0.01 },
]
const RAIN_REVEAL_MANUAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'distance', label: 'distance（下落距离）', min: 0, max: 120, step: 0.1 },
  { key: 'speed', label: 'speed（扩散速度）', min: 0, max: 20, step: 0.01 },
  { key: 'acceleration', label: 'acceleration（扩散加速度）', min: 0, max: 10, step: 0.01 },
  { key: 'flightTime', label: 'flightTime（飞行时间）', min: 0.1, max: 8, step: 0.01 },
  { key: 'rainSize', label: 'rainSize（雨滴尺寸）', min: 0.001, max: 0.2, step: 0.001 },
  { key: 'rotation', label: 'rotation（旋转）', min: 0, max: 2, step: 0.01 },
  { key: 'fallTintIntensity', label: 'fallTintIntensity（下落染色强度）', min: 0, max: 1, step: 0.01 },
  { key: 'hitDuration', label: 'hitDuration（命中高亮时间）', min: 0.1, max: 4, step: 0.01 },
]
const RAIN_REVEAL_COLOR_FIELDS: RevealColorField[] = [
  { key: 'fallTint', label: 'fallTint（下落颜色）' },
  { key: 'hitTint', label: 'hitTint（落地高亮颜色）' },
]
const GRID_REVEAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'duration', label: 'duration（总时长）', min: 1.2, max: 20, step: 0.1 },
  { key: 'moveTintIntensity', label: 'moveTintIntensity（移动染色强度）', min: 0, max: 1, step: 0.01 },
  { key: 'intensity', label: 'intensity（整体强度）', min: 0, max: 3, step: 0.01 },
]
const GRID_REVEAL_MANUAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'blockSize', label: 'blockSize（网格尺寸）', min: 0.1, max: 10, step: 0.01 },
  { key: 'delay', label: 'delay（分块延迟）', min: 0, max: 2, step: 0.01 },
  { key: 'duration', label: 'duration（单块移动时长）', min: 0.1, max: 5, step: 0.01 },
  { key: 'dotSize', label: 'dotSize（点尺寸）', min: 0.001, max: 0.2, step: 0.001 },
  { key: 'moveTintIntensity', label: 'moveTintIntensity（移动染色强度）', min: 0, max: 1, step: 0.01 },
  { key: 'landDuration', label: 'landDuration（落地高亮时间）', min: 0.1, max: 4, step: 0.01 },
]
const GRID_REVEAL_COLOR_FIELDS: RevealColorField[] = [
  { key: 'moveTint', label: 'moveTint（移动颜色）' },
  { key: 'landTint', label: 'landTint（落地高亮颜色）' },
]

const EFFECT_GROUPS: Array<{
  title: string
  mode: string
  effects: ShaderEffectOption[]
}> = [
  {
    title: '区域扫描预设',
    mode: '范围 / 方向 / 颜色 / 时间',
    effects: [
      {
        key: 'box-reveal',
        label: '盒体揭示',
        detail: '按参数揭示',
      },
      {
        key: 'box-hide',
        label: '盒体隐藏',
        detail: '按参数隐藏',
      },
      {
        key: 'box-tint',
        label: '盒体染色',
        detail: '按参数染色',
      },
      {
        key: 'box-untint',
        label: '盒体退色',
        detail: '按参数退色',
      },
      {
        key: 'box-crop',
        label: '盒体裁切',
        detail: '保留盒内',
      },
    ],
  },
  {
    title: '显现动画',
    mode: '时间驱动 / 显现参数',
    effects: [
      {
        key: 'reveal-radial',
        label: '径向揭示',
        detail: '波前扩散',
      },
      {
        key: 'reveal-rain',
        label: '雨滴揭示',
        detail: '下落回弹',
      },
      {
        key: 'reveal-grid',
        label: '网格爆发',
        detail: '分块展开',
      },
    ],
  },
]

const boxEffectGroup = EFFECT_GROUPS[0]
const revealEffectGroup = EFFECT_GROUPS[1]
const showcaseRevealEffects: ShaderEffectOption[] = [
  {
    key: 'reveal-magic',
    label: '魔法显现',
    detail: '旋转聚合',
  },
  {
    key: 'reveal-spread',
    label: '扩散显现',
    detail: '中心向外扩散',
  },
  {
    key: 'reveal-unroll',
    label: '卷轴显现',
    detail: '环向展开',
  },
  {
    key: 'reveal-twister',
    label: '扭转显现',
    detail: '垂直旋入',
  },
]
const visualEffectGroup: {
  title: string
  mode: string
  effects: ShaderEffectOption[]
} = {
  title: '视觉变形',
  mode: '强度 / 时间倍率',
  effects: [
    {
      key: 'visual-electronic',
      label: '电子脉冲',
      detail: '霓虹抖动',
    },
    {
      key: 'visual-deep-meditation',
      label: '深度冥想',
      detail: '呼吸起伏',
    },
    {
      key: 'visual-waves',
      label: '波浪扰动',
      detail: '径向水波',
    },
    {
      key: 'visual-flare',
      label: '光焰扫过',
      detail: '外扩高亮',
    },
    {
      key: 'visual-disintegrate',
      label: '碎散漂移',
      detail: '颗粒散开',
    },
  ],
}
const dissolveEffectGroup: {
  title: string
  mode: string
  effects: ShaderEffectOption[]
} = {
  title: '消散',
  mode: '速度 / 位移幅度',
  effects: [
    {
      key: 'dissolve-drift',
      label: '漂移消散',
      detail: '逐点散出',
    },
  ],
}
const particleEffectGroup: {
  title: string
  mode: string
  effects: ShaderEffectOption[]
} = {
  title: '粒子特效',
  mode: '粒子化 / 炸裂 / 聚合 / 恢复',
  effects: [
    {
      key: 'particle-scatter-reassemble',
      label: '粒子炸裂与重组',
      detail: '选择成对的炸裂与聚合方式',
    },
  ],
}
const visualDissolveGroups = [visualEffectGroup, dissolveEffectGroup]
const ADAPTIVE_OBB_EFFECT: ShaderEffectOption = {
  key: 'obb-double-wave',
  label: '视角推进揭示',
  detail: '沿镜头前方双波推进',
}

const REVEAL_PARAM_PANELS: Array<{
  key: RevealPanelKey
  effect: ShaderEffectOption
  numberFields: RevealNumberField[]
  colorFields: RevealColorField[]
}> = [
  {
    key: 'radial',
    effect: revealEffectGroup.effects[0],
    numberFields: RADIAL_REVEAL_NUMBER_FIELDS,
    colorFields: RADIAL_REVEAL_COLOR_FIELDS,
  },
  {
    key: 'rain',
    effect: revealEffectGroup.effects[1],
    numberFields: RAIN_REVEAL_NUMBER_FIELDS,
    colorFields: RAIN_REVEAL_COLOR_FIELDS,
  },
  {
    key: 'grid',
    effect: revealEffectGroup.effects[2],
    numberFields: GRID_REVEAL_NUMBER_FIELDS,
    colorFields: GRID_REVEAL_COLOR_FIELDS,
  },
]

const VISUAL_SHOWCASE_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'intensity', label: 'intensity（强度）', min: 0, max: 3, step: 0.01 },
  { key: 'timeScale', label: 'timeScale（时间倍率）', min: 0, max: 4, step: 0.01 },
]
const SHOWCASE_REVEAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'timeScale', label: 'timeScale（时间倍率）', min: 0, max: 4, step: 0.01 },
  { key: 'motionScale', label: 'motionScale（位移幅度）', min: 0, max: 5, step: 0.01 },
  { key: 'duration', label: 'duration（显现时长）', min: 0.5, max: 20, step: 0.1 },
]
const ELECTRONIC_NUMBER_FIELDS: RevealNumberField[] = [
  ...VISUAL_SHOWCASE_NUMBER_FIELDS,
  { key: 'swayAmplitude', label: 'swayAmplitude（扭摆幅度）', min: 0, max: 1, step: 0.01 },
  { key: 'swaySpeed', label: 'swaySpeed（扭摆速度）', min: 0, max: 8, step: 0.05 },
]
const DISSOLVE_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'speed', label: 'speed（速度）', min: 0.05, max: 5, step: 0.01 },
  { key: 'motionScale', label: 'motionScale（位移幅度）', min: 0, max: 5, step: 0.01 },
]
const PARTICLE_STYLE_NUMBER_FIELDS: RevealNumberField[] = [
  {
    key: 'opacity',
    label: 'opacity（粒子不透明度）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleOpacity.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleOpacity.max,
    step: 0.01,
  },
  {
    key: 'highlightStrength',
    label: 'highlightStrength（晶莹高光）',
    min:
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter
        .particleHighlightStrength.min,
    max:
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter
        .particleHighlightStrength.max,
    step: 0.05,
  },
  {
    key: 'glowStrength',
    label: 'glowStrength（粒子发光）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleGlowStrength.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleGlowStrength.max,
    step: 0.05,
  },
  {
    key: 'glowRadius',
    label: 'glowRadius（光晕范围）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleGlowRadius.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleGlowRadius.max,
    step: 0.05,
  },
]
const PARTICLE_JITTER_NUMBER_FIELDS: RevealNumberField[] = [
  {
    key: 'amplitude',
    label: 'amplitude（随机扰动范围）',
    min:
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter
        .particleJitterAmplitude.min,
    max:
      GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter
        .particleJitterAmplitude.max,
    step: 0.0001,
  },
  {
    key: 'speed',
    label: 'speed（随机扰动速度）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleJitterSpeed.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleJitterSpeed.max,
    step: 0.05,
  },
]
const PARTICLE_TIMING_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'duration', label: 'duration（总时长）', min: 0.1, max: 60, step: 0.1 },
  {
    key: 'preScatterHoldDuration',
    label: 'preScatterHoldDuration（原位扰动停留）',
    min: 0,
    max: 60,
    step: 0.1,
  },
  {
    key: 'peakHoldDuration',
    label: 'peakHoldDuration（峰值扰动停留）',
    min: 0,
    max: 60,
    step: 0.1,
  },
]
const PARTICLE_FIXED_SIZE_FIELD: RevealNumberField = {
  key: 'targetSize',
  label: 'targetSize（固定目标尺寸上限）',
  min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleTargetSize.min,
  max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleTargetSize.max,
  step: 0.0001,
}
const PARTICLE_ADAPTIVE_SIZE_FIELD: RevealNumberField = {
  key: 'targetSizeRatio',
  label: 'targetSizeRatio（观察参考尺度占比）',
  min:
    GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleTargetSizeRatio.min,
  max:
    GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.particleTargetSizeRatio.max,
  step: 0.00001,
}
const PARTICLE_SIZE_MODE_OPTIONS = [
  { value: 'fixed', label: '固定尺寸' },
  { value: 'adaptive', label: '自适应（模型 / 观察距离）' },
] as const satisfies ReadonlyArray<{ value: GSplatParticleSizeMode; label: string }>
const PARTICLE_MOTION_OPTIONS = [
  { value: 'chaotic', label: '无序炸裂与聚合', detail: '随机三维方向散开并原路返回' },
  { value: 'layered-print', label: '逐层打印重组', detail: '按高度逐层扫描并逆向重组' },
  { value: 'fractal-orbit', label: '分形几何轨道重组', detail: '沿耦合几何轨道散开并返回' },
] as const satisfies ReadonlyArray<{
  value: GSplatParticleMotionType
  label: string
  detail: string
}>
const PARTICLE_CHAOTIC_MOTION_FIELDS: RevealNumberField[] = [
  {
    key: 'scatterScale',
    label: 'scatterScale（炸裂范围）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.scatterScale.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.scatterScale.max,
    step: 0.001,
  },
]
const PARTICLE_LAYERED_PRINT_MOTION_FIELDS: RevealNumberField[] = [
  {
    key: 'layerCount',
    label: 'layerCount（打印层数）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.layerCount.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.layerCount.max,
    step: 1,
  },
  {
    key: 'bandWidth',
    label: 'bandWidth（扫描带宽）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.printBandWidth.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.printBandWidth.max,
    step: 0.01,
  },
  {
    key: 'liftScale',
    label: 'liftScale（抬升距离）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.liftScale.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.liftScale.max,
    step: 0.005,
  },
]
const PARTICLE_FRACTAL_ORBIT_MOTION_FIELDS: RevealNumberField[] = [
  {
    key: 'orbitScale',
    label: 'orbitScale（轨道范围）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.orbitScale.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.orbitScale.max,
    step: 0.01,
  },
  {
    key: 'frequency',
    label: 'frequency（轨道频率）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.orbitFrequency.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.orbitFrequency.max,
    step: 0.05,
  },
  {
    key: 'sweepWidth',
    label: 'sweepWidth（轨道扫描带宽）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.orbitSweepWidth.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.orbitSweepWidth.max,
    step: 0.01,
  },
]
const PARTICLE_ADVANCED_NUMBER_FIELDS: RevealNumberField[] = [
  {
    key: 'seed',
    label: 'seed（随机种子）',
    min: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.seed.min,
    max: GS3D_EFFECT_ANIMATION_PRESET_NUMERIC_LIMITS.particleScatter.seed.max,
    step: 1,
  },
]
const PARTICLE_PHASE_WEIGHT_FIELDS: RevealNumberField[] = [
  { key: 'particleize', label: '粒子化阶段权重', min: 0, max: 10, step: 0.1 },
  { key: 'scatter', label: '炸裂阶段权重', min: 0, max: 10, step: 0.1 },
  { key: 'gather', label: '聚合阶段权重', min: 0, max: 10, step: 0.1 },
  { key: 'restore', label: '高斯恢复阶段权重', min: 0, max: 10, step: 0.1 },
]

function getAnimationService(): AnimationService | null {
  return engine.value?.context.getService('AnimationService') ?? null
}

function createKeyframe(
  source: Omit<CameraKeyframe, 'id' | 'roll'> & { roll?: number },
): CameraKeyframe {
  return {
    id: nextKeyframeId++,
    time: source.time,
    position: [...source.position],
    target: [...source.target],
    fov: source.fov,
    roll: source.roll ?? 0,
  }
}

function toFiniteNumber(value: unknown, fallback = 0): number {
  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}

function formatNumber(value: unknown, digits = 2): string {
  return Number(toFiniteNumber(value).toFixed(digits)).toString()
}

function formatCameraMotionSemantic(value: string): string {
  switch (value) {
    case 'dolly':
      return '拉近/拉远'
    case 'truck':
      return '左右平移'
    case 'orbit':
      return '环绕'
    case 'rise-overlook':
      return '上升俯看'
    case 'spiral':
      return '螺旋'
    case 'figure-eight':
      return '8 字环绕'
    case 'sine-wave':
      return '正弦高度起伏'
    case 'pan-zoom-scan':
      return '平移缩放扫描'
    case 'dolly-zoom':
      return '希区柯克变焦（滑动变焦）'
    case 'butterfly':
      return '球面蝴蝶'
    case 'turbulence':
      return '谐波流体飞行'
    case 'dolly-roll':
      return '推进滚转'
    case 'sequence':
      return '组合运镜'
    default:
      return value
  }
}

function formatCameraMotionParamLabel(value: string): string {
  const labels: Record<string, string> = {
    direction: '方向',
    distanceScale: '距离倍率',
    translationScale: '平移倍率',
    angle: '角度',
    keyframeCount: '关键帧数',
    heightScale: '高度倍率',
    verticalDirection: '垂直方向',
    rotationDirection: '旋转方向',
    radiusMode: '半径模式',
    radiusScale: '半径倍率',
    pitchAmplitude: '俯仰幅度',
    yawAmplitude: '偏航幅度',
    distanceAmplitude: '距离幅度',
    waveCount: '波次数',
    cycleCount: '循环数',
    pulseCount: '脉冲数',
    fovCompensation: 'FOV 补偿',
    wingCount: '翼对数',
    harmonicCount: '谐波数',
    rollDirection: '滚转方向',
    rollTurns: '滚转圈数',
  }

  return labels[value] ?? value
}

function createDefaultCameraMotionParams(template: SemanticCameraMotionTemplate): Record<string, string | number> {
  if (!isSemanticCameraMotionLeafTemplate(template)) {
    return {}
  }
  const params = { ...(template.params ?? {}) } as Record<string, string | number>

  switch (template.semantic) {
    case 'dolly':
      return {
        direction: params.direction ?? 'in',
        distanceScale: params.distanceScale ?? (params.direction === 'out' ? 1.5 : 0.35),
      }
    case 'truck':
      return {
        direction: params.direction ?? 'right',
        translationScale: params.translationScale ?? 0.5,
      }
    case 'orbit':
      return {
        direction: params.direction ?? 'right',
        angle: params.angle ?? 90,
        keyframeCount: params.keyframeCount ?? 3,
      }
    case 'rise-overlook':
      return {
        distanceScale: params.distanceScale ?? 1.15,
        heightScale: params.heightScale ?? 0.35,
        keyframeCount: params.keyframeCount ?? 3,
      }
    case 'spiral':
      return {
        verticalDirection: params.verticalDirection ?? 'up',
        rotationDirection: params.rotationDirection ?? 'clockwise',
        radiusMode: params.radiusMode ?? 'fixed',
        angle: params.angle ?? 360,
        radiusScale: params.radiusScale ?? 1,
        heightScale: params.heightScale ?? 0.5,
        keyframeCount: params.keyframeCount ?? 13,
      }
    case 'figure-eight':
      return {
        direction: params.direction ?? 'right',
        angle: params.angle ?? 360,
        pitchAmplitude: params.pitchAmplitude ?? 17.2,
        keyframeCount: params.keyframeCount ?? 49,
      }
    case 'sine-wave':
      return {
        direction: params.direction ?? 'right',
        angle: params.angle ?? 360,
        pitchAmplitude: params.pitchAmplitude ?? 5.7,
        waveCount: params.waveCount ?? 6,
        keyframeCount: params.keyframeCount ?? 73,
      }
    case 'pan-zoom-scan':
      return {
        direction: params.direction ?? 'right',
        distanceAmplitude: params.distanceAmplitude ?? 0.3,
        pitchAmplitude: params.pitchAmplitude ?? 8.6,
        yawAmplitude: params.yawAmplitude ?? 28.6,
        cycleCount: params.cycleCount ?? 1,
        keyframeCount: params.keyframeCount ?? 49,
      }
    case 'dolly-zoom':
      return {
        direction: params.direction ?? 'right',
        angle: params.angle ?? 360,
        distanceAmplitude: params.distanceAmplitude ?? 0.4,
        pulseCount: params.pulseCount ?? 1,
        fovCompensation: params.fovCompensation ?? 1,
        keyframeCount: params.keyframeCount ?? 49,
      }
    case 'butterfly':
      return {
        direction: params.direction ?? 'right',
        pitchAmplitude: params.pitchAmplitude ?? 11.5,
        yawAmplitude: params.yawAmplitude ?? 43,
        wingCount: params.wingCount ?? 1,
        keyframeCount: params.keyframeCount ?? 49,
      }
    case 'turbulence':
      return {
        direction: params.direction ?? 'right',
        angle: params.angle ?? 360,
        distanceAmplitude: params.distanceAmplitude ?? 0.15,
        pitchAmplitude: params.pitchAmplitude ?? 5.7,
        yawAmplitude: params.yawAmplitude ?? 8.6,
        harmonicCount: params.harmonicCount ?? 3,
        keyframeCount: params.keyframeCount ?? 73,
      }
    case 'dolly-roll':
      return {
        distanceScale: params.distanceScale ?? 0.5,
        rollDirection: params.rollDirection ?? 'clockwise',
        rollTurns: params.rollTurns ?? 1,
        keyframeCount: params.keyframeCount ?? 25,
      }
  }
}

function syncCameraMotionParamState(template: SemanticCameraMotionTemplate | null | undefined): void {
  cameraMotionTemplateParams.value = template ? createDefaultCameraMotionParams(template) : {}
  cameraMotionSequenceTemplate.value = template?.semantic === 'sequence' ?
    cloneCameraMotionTemplate(template) :
    null
}

function cloneCameraMotionTemplate<T extends SemanticCameraMotionTemplate>(template: T): T {
  return JSON.parse(JSON.stringify(template)) as T
}

function syncCameraMotionMetaState(template?: SemanticCameraMotionTemplate | null): void {
  cameraMotionTemplateName.value = template?.name?.trim() || selectedCameraMotionSemantic.value
  cameraMotionTemplateDescription.value = template?.description ?? ''
}

function syncCameraMotionTimingState(template?: SemanticCameraMotionTemplate | null): void {
  cameraMotionTemplateInterpolation.value = template?.interpolation ?? 'spline'
  cameraMotionTemplateEasing.value = template?.easing ?? 'linear'
  cameraMotionTemplateSegmentEasingFirst.value = template?.segmentEasing?.first ?? 'none'
  cameraMotionTemplateSegmentEasingLast.value = template?.segmentEasing?.last ?? 'none'
  cameraMotionTemplateHoldDuration.value =
    template?.holdDuration ?? (template?.semantic === 'sequence' ? 0 : 1)
  normalizeCameraMotionTimingState()
}

function createTimestampId(prefix: string): string {
  return `${prefix}-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`
}

function getCameraMotionParamRange(field: CameraMotionParamField): {
  min: number
  max: number
} {
  const direction = String(cameraMotionTemplateParams.value.direction ?? '')
  const directionalRange = field.rangeByDirection?.[direction]
  return {
    min: directionalRange?.min ?? field.min ?? Number.NEGATIVE_INFINITY,
    max: directionalRange?.max ?? field.max ?? Number.POSITIVE_INFINITY,
  }
}

function normalizeCameraMotionParamField(
  field: CameraMotionParamField,
  normalizeDependencies = true,
): void {
  const value = cameraMotionTemplateParams.value[field.key]
  if (field.type === 'select') {
    const allowed = field.options?.map((option) => option.value) ?? []
    if (!allowed.includes(String(value))) {
      cameraMotionTemplateParams.value[field.key] = allowed[0] ?? ''
    }
    if (normalizeDependencies) {
      normalizeCameraMotionSemanticParams(field.key)
    }
    return
  }

  const range = getCameraMotionParamRange(field)
  const numeric = clampNumber(value, range.min, range.max)
  const normalized = field.step === 1 ? Math.round(numeric) : numeric
  cameraMotionTemplateParams.value[field.key] = normalized
  if (normalizeDependencies) {
    normalizeCameraMotionSemanticParams(field.key)
  }
}

function normalizeCameraMotionParams(): void {
  for (const field of selectedCameraMotionParamFields.value) {
    normalizeCameraMotionParamField(field, false)
  }
  normalizeCameraMotionSemanticParams()
}

function normalizeCameraMotionSemanticParams(changedKey?: string): void {
  normalizeDollyDistanceParams(changedKey)
  normalizeSpiralRadiusParams(changedKey)
}

function normalizeDollyDistanceParams(changedKey?: string): void {
  if (selectedCameraMotionSemantic.value !== 'dolly') {
    return
  }

  const params = cameraMotionTemplateParams.value
  const direction = String(params.direction ?? 'in')
  const distanceScale = toFiniteNumber(params.distanceScale, direction === 'out' ? 1.5 : 0.35)

  if (direction === 'in') {
    params.distanceScale = changedKey === 'direction' ? 0.35 : clampNumber(distanceScale, 0, 0.99)
    return
  }

  if (direction === 'out') {
    params.distanceScale = changedKey === 'direction' ? 1.5 : clampNumber(distanceScale, 1.01, 10)
  }
}

function normalizeSpiralRadiusParams(changedKey?: string): void {
  if (selectedCameraMotionSemantic.value !== 'spiral') {
    return
  }

  const params = cameraMotionTemplateParams.value
  const radiusMode = String(params.radiusMode ?? 'fixed')
  const radiusScale = toFiniteNumber(params.radiusScale, 1)

  if (radiusMode === 'fixed') {
    params.radiusScale = 1
    return
  }

  if (radiusMode === 'in' && (changedKey === 'radiusMode' || radiusScale >= 1)) {
    params.radiusScale = 0.65
    return
  }

  if (radiusMode === 'out' && (changedKey === 'radiusMode' || radiusScale <= 1)) {
    params.radiusScale = 1.35
  }
}

function normalizeCameraMotionEasingValue(value: CameraEasingMode): CameraEasingMode {
  return CAMERA_MOTION_OPTIONAL_EASING_OPTIONS.some((option) => option.value === value) ? value : 'none'
}

function normalizeRequiredCameraMotionEasingValue(value: CameraEasingMode): AnimationEasing {
  const normalized = normalizeCameraMotionEasingValue(value)
  return normalized === 'none' ? 'linear' : normalized
}

function normalizeCameraMotionTimingState(): void {
  cameraMotionTemplateHoldDuration.value = clampNumber(cameraMotionTemplateHoldDuration.value, 0, 30)

  if (cameraMotionTemplateInterpolation.value === 'step') {
    cameraMotionTemplateEasing.value = 'none'
    cameraMotionTemplateSegmentEasingFirst.value = 'none'
    cameraMotionTemplateSegmentEasingLast.value = 'none'
    return
  }

  cameraMotionTemplateEasing.value = normalizeRequiredCameraMotionEasingValue(
    cameraMotionTemplateEasing.value
  )
  cameraMotionTemplateSegmentEasingFirst.value = normalizeCameraMotionEasingValue(
    cameraMotionTemplateSegmentEasingFirst.value
  )
  cameraMotionTemplateSegmentEasingLast.value = normalizeCameraMotionEasingValue(
    cameraMotionTemplateSegmentEasingLast.value
  )
}

function selectCameraMotionTemplateInterpolation(event: Event): void {
  cameraMotionTemplateInterpolation.value = (event.target as HTMLSelectElement).value as AnimationInterpolation
  normalizeCameraMotionTimingState()
}

function createCurrentCameraMotionTemplate(): SemanticCameraMotionTemplate {
  const template = selectedCameraMotionTemplate.value
  if (!template) {
    throw new Error('没有可用的运镜模板。')
  }

  if (template.semantic === 'sequence') {
    const sequence = cameraMotionSequenceTemplate.value ?? cloneCameraMotionTemplate(template)
    cameraMotionTemplateHoldDuration.value = clampNumber(cameraMotionTemplateHoldDuration.value, 0, 30)
    const description = cameraMotionTemplateDescription.value.trim()
    const { description: _sourceDescription, ...sourceSequence } = cloneCameraMotionTemplate(sequence)
    return parseSemanticCameraMotionTemplate({
      ...sourceSequence,
      id: createTimestampId('camera-sequence'),
      name: cameraMotionTemplateName.value.trim() || '组合运镜',
      ...(description ? { description } : {}),
      holdDuration: cameraMotionTemplateHoldDuration.value,
    })
  }

  normalizeCameraMotionParams()
  normalizeCameraMotionTimingState()

  const {
    description: _sourceDescription,
    easing: _sourceEasing,
    segmentEasing: _sourceSegmentEasing,
    ...sourceTemplate
  } = template
  const description = cameraMotionTemplateDescription.value.trim()
  const nextTemplate = {
    ...sourceTemplate,
    id: createTimestampId(`camera-${selectedCameraMotionSemantic.value}`),
    name: cameraMotionTemplateName.value.trim() || selectedCameraMotionSemantic.value,
    semantic: selectedCameraMotionSemantic.value,
    params: { ...cameraMotionTemplateParams.value },
    interpolation: cameraMotionTemplateInterpolation.value,
    holdDuration: cameraMotionTemplateHoldDuration.value,
  }

  const timingTemplate = cameraMotionTemplateInterpolation.value === 'step' ?
    nextTemplate :
    {
      ...nextTemplate,
      easing: normalizeRequiredCameraMotionEasingValue(cameraMotionTemplateEasing.value),
      ...(
        cameraMotionTemplateSegmentEasingFirst.value !== 'none' ||
        cameraMotionTemplateSegmentEasingLast.value !== 'none' ?
          {
            segmentEasing: {
              ...(cameraMotionTemplateSegmentEasingFirst.value !== 'none' ?
                { first: cameraMotionTemplateSegmentEasingFirst.value } :
                {}),
              ...(cameraMotionTemplateSegmentEasingLast.value !== 'none' ?
                { last: cameraMotionTemplateSegmentEasingLast.value } :
                {}),
            },
          } :
          {}
      ),
    }

  if (description) {
    return parseSemanticCameraMotionTemplate({
      ...timingTemplate,
      description,
    })
  }

  return parseSemanticCameraMotionTemplate(timingTemplate)
}

function roundNumber(value: unknown, digits = 3): number {
  return Number(toFiniteNumber(value).toFixed(digits))
}

function clampNumber(value: unknown, min: number, max: number): number {
  return Math.min(max, Math.max(min, toFiniteNumber(value, min)))
}

function isVisualShowcaseEffectKey(value: string): value is VisualShowcaseEffectKey {
  return VISUAL_SHOWCASE_EFFECT_KEYS.includes(value as VisualShowcaseEffectKey)
}

function isShowcaseRevealEffectKey(value: string): value is ShowcaseRevealEffectKey {
  return SHOWCASE_REVEAL_EFFECT_KEYS.includes(value as ShowcaseRevealEffectKey)
}

function isDissolveEffectKey(value: string): value is DissolveEffectKey {
  return DISSOLVE_EFFECT_KEYS.includes(value as DissolveEffectKey)
}

function isParticleEffectKey(value: string): value is ParticleEffectKey {
  return PARTICLE_EFFECT_KEYS.includes(value as ParticleEffectKey)
}

function getEffectTemplateGroupIndex(template: GSplatEffectTemplate | null): number {
  if (!template) {
    return 5
  }
  if (isShowcaseRevealEffectKey(template.effectKey)) {
    return 1
  }
  if (isVisualShowcaseEffectKey(template.effectKey)) {
    return 2
  }
  if (isDissolveEffectKey(template.effectKey)) {
    return 4
  }
  if (isParticleEffectKey(template.effectKey)) {
    return 3
  }
  if (template.effectKey.startsWith('reveal-') || template.effectKey === 'obb-double-wave') {
    return 0
  }
  return 5
}

function getCurrentCameraState() {
  return engine.value?.camera.getState() ?? null
}

function syncCurrentCameraFov(): void {
  const state = getCurrentCameraState()
  if (state) {
    currentCameraFov.value = roundNumber(state.fov, 1)
  }
}

function applyCameraPatch(patch: CameraStatePatch): void {
  engine.value?.camera.applyStatePatch(patch, {
    cancelTransition: true,
  })
}

function clearGeneratedCameraMotionPlan(): void {
  cameraMotionPlan.value = null
  selectedCameraMotionPlanSegmentIndex.value = 0
}

function setGeneratedCameraMotionPlan(plan: SemanticCameraMotionPlan): void {
  cameraMotionPlan.value = plan
  selectedCameraMotionPlanSegmentIndex.value = 0
}

function selectCameraMotionPlanSegment(index: number): void {
  const plan = cameraMotionPlan.value
  if (!plan || index < 0 || index >= plan.segments.length) {
    return
  }
  selectedCameraMotionPlanSegmentIndex.value = index
}

function formatCameraMotionPlanSegmentKind(
  segment: SemanticCameraMotionPlan['segments'][number],
): string {
  return segment.kind === 'hold' ? '定格片段' : '运动片段'
}

function formatCameraMotionPlanSegmentInterpolation(
  segment: SemanticCameraMotionPlan['segments'][number],
): string {
  return segment.clip.tracks[0]?.interpolation ?? 'linear'
}

function applyCameraMotionPlanInitialPose(plan: SemanticCameraMotionPlan): void {
  const camera = plan.initialCamera
  applyCameraPatch({
    position: new THREE.Vector3(...camera.position),
    target: new THREE.Vector3(...camera.target),
    up: new THREE.Vector3(...camera.up),
    fov: camera.fov,
    roll: camera.roll,
  })
  syncCurrentCameraFov()
}

function applyCameraMotionPlanKeyframe(
  frame: SemanticCameraMotionPlan['segments'][number]['keyframes'][number],
): void {
  applyCameraPose(frame)
}

function sortKeyframes(): void {
  cameraKeyframes.value.sort((a, b) => a.time - b.time)
}

function ensureSelectedKeyframe(): void {
  if (cameraKeyframes.value.length === 0) {
    selectedKeyframeId.value = null
    return
  }

  const hasSelected = cameraKeyframes.value.some((frame) => frame.id === selectedKeyframeId.value)
  if (!hasSelected) {
    selectedKeyframeId.value = cameraKeyframes.value[0].id
  }
}

function selectKeyframe(frame: CameraKeyframe): void {
  selectedKeyframeId.value = frame.id
}

function getFallbackKeyframeTime(index: number): number {
  if (index <= 0) {
    return 0
  }
  return cameraKeyframes.value[index - 1].time + MIN_KEYFRAME_TIME_GAP
}

function normalizeKeyframeTime(index: number): void {
  const frame = cameraKeyframes.value[index]
  if (!frame) {
    return
  }

  const previous = index > 0 ? cameraKeyframes.value[index - 1] : null
  const next = index < cameraKeyframes.value.length - 1 ?
    cameraKeyframes.value[index + 1] :
    null
  let time = Number(frame.time)

  if (!Number.isFinite(time)) {
    time = getFallbackKeyframeTime(index)
  }

  time = Math.max(0, time)
  if (previous && time <= previous.time) {
    time = previous.time + MIN_KEYFRAME_TIME_GAP
  }
  if (next && time >= next.time) {
    time = getFallbackKeyframeTime(index)
  }
  if (next && time >= next.time) {
    time = Math.max(0, next.time - MIN_KEYFRAME_TIME_GAP)
  }

  frame.time = roundNumber(time)
  handleKeyframeChanged()
}

function normalizeKeyframeVector(
  frame: CameraKeyframe,
  field: CameraVectorField,
  axis: CameraVectorAxis,
): void {
  const value = Number(frame[field][axis])
  frame[field][axis] = Number.isFinite(value) ? roundNumber(value) : 0
  handleKeyframeChanged()
}

function normalizeKeyframeFov(frame: CameraKeyframe): void {
  const value = Number(frame.fov)
  frame.fov = Number.isFinite(value) ?
    roundNumber(clampNumber(value, CAMERA_FOV_MIN, CAMERA_FOV_MAX), 1) :
    65
  handleKeyframeChanged()
}

function clearCameraMotionTemplateMessage(): void {
  cameraMotionTemplateMessage.value = ''
}

function clearEffectTemplateMessage(): void {
  effectTemplateMessage.value = ''
}

function handleKeyframeChanged(): void {
  clearCameraMotionTemplateMessage()
  clearGeneratedCameraMotionPlan()
  cameraSegmentEasing.value = null
  restartActivePlayer()
}

function resolveNewKeyframeTime(): number {
  if (cameraKeyframes.value.length === 0) {
    return 0
  }

  const last = cameraKeyframes.value[cameraKeyframes.value.length - 1]
  let time = player.value?.time ?? ((last?.time ?? 0) + KEYFRAME_TIME_STEP)

  if (!Number.isFinite(time) || time < 0) {
    time = (last?.time ?? 0) + KEYFRAME_TIME_STEP
  }

  const hasSameTime = cameraKeyframes.value.some((frame) => {
    return Math.abs(frame.time - time) < MIN_KEYFRAME_TIME_GAP
  })
  if (hasSameTime) {
    time = (last?.time ?? 0) + KEYFRAME_TIME_STEP
  }

  return roundNumber(time)
}

function addKeyframeFromCamera(): void {
  const state = getCurrentCameraState()
  if (!state) {
    return
  }

  const nextFrame = createKeyframe({
    time: resolveNewKeyframeTime(),
    position: [
      roundNumber(state.position.x),
      roundNumber(state.position.y),
      roundNumber(state.position.z),
    ],
    target: [
      roundNumber(state.target.x),
      roundNumber(state.target.y),
      roundNumber(state.target.z),
    ],
    fov: roundNumber(state.fov, 1),
  })
  cameraKeyframes.value.push(nextFrame)
  sortKeyframes()
  selectedKeyframeId.value = nextFrame.id
  syncCurrentCameraFov()
  handleKeyframeChanged()
}

function removeKeyframe(index: number): void {
  if (cameraKeyframes.value.length <= 1) {
    return
  }

  cameraKeyframes.value.splice(index, 1)
  ensureSelectedKeyframe()
  handleKeyframeChanged()
}

function clearKeyframes(): void {
  clearCameraMotionTemplateMessage()
  clearGeneratedCameraMotionPlan()
  cameraSegmentEasing.value = null
  cameraKeyframes.value = []
  selectedKeyframeId.value = null
  stopCameraAnimation()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readImportedCameraPathMatrix(value: unknown, path: string): number[][] {
  if (!Array.isArray(value) || value.length !== 4) {
    throw new Error(`${path} 必须是 4x4 transform_matrix`)
  }

  return value.map((row, rowIndex) => {
    if (!Array.isArray(row) || row.length !== 4) {
      throw new Error(`${path}[${rowIndex}] 必须包含 4 个数字`)
    }

    return row.map((item, columnIndex) => {
      const numberValue = typeof item === 'number' ? item : Number(item)
      if (!Number.isFinite(numberValue)) {
        throw new Error(`${path}[${rowIndex}][${columnIndex}] 必须是有限数字`)
      }
      return numberValue
    })
  })
}

function resolveImportedCameraPathSide(filePath: string): ImportedCameraPathSide | null {
  const normalized = filePath.toLowerCase()
  if (normalized.includes('left')) {
    return 'left'
  }
  if (normalized.includes('right')) {
    return 'right'
  }
  return null
}

function parseImportedCameraPathJson(source: string, fileName: string): ImportedCameraPathState {
  const parsed = JSON.parse(source) as unknown
  if (!isRecord(parsed) || !Array.isArray(parsed.frames)) {
    throw new Error('JSON 根对象必须包含 frames 数组')
  }

  const state: ImportedCameraPathState = {
    fileName,
    left: [],
    right: [],
  }

  parsed.frames.forEach((frame, index) => {
    if (!isRecord(frame)) {
      throw new Error(`frames[${index}] 必须是对象`)
    }

    const filePath = typeof frame.file_path === 'string' ? frame.file_path : ''
    const side = resolveImportedCameraPathSide(filePath)
    if (!side) {
      return
    }

    const timestampValue = frame.timestamp
    const timestamp = typeof timestampValue === 'number' && Number.isFinite(timestampValue) ?
      timestampValue :
      null

    state[side].push({
      filePath,
      timestamp,
      matrix: readImportedCameraPathMatrix(frame.transform_matrix, `frames[${index}].transform_matrix`),
    })
  })

  if (state.left.length === 0 && state.right.length === 0) {
    throw new Error('未找到 file_path 包含 left 或 right 的帧')
  }

  state.left.sort(compareImportedCameraPathFrames)
  state.right.sort(compareImportedCameraPathFrames)
  return state
}

function compareImportedCameraPathFrames(
  a: ImportedCameraPathFrame,
  b: ImportedCameraPathFrame,
): number {
  if (a.timestamp !== null && b.timestamp !== null) {
    return a.timestamp - b.timestamp
  }
  return a.filePath.localeCompare(b.filePath)
}

function importedTimestampDeltaToSeconds(delta: number): number {
  const absDelta = Math.abs(delta)
  if (absDelta >= 1_000_000) {
    return delta / 1_000_000_000
  }
  if (absDelta >= 1_000) {
    return delta / 1_000
  }
  return delta
}

function createImportedCameraPathKeyframes(frames: ImportedCameraPathFrame[]): CameraKeyframe[] {
  const firstTimestamp = frames.find((frame) => frame.timestamp !== null)?.timestamp ?? null

  return frames.map((frame, index) => {
    const matrix = frame.matrix
    const position = new THREE.Vector3(matrix[0][3], matrix[1][3], matrix[2][3])
    const forward = new THREE.Vector3(-matrix[0][2], -matrix[1][2], -matrix[2][2])

    if (forward.lengthSq() <= 0) {
      forward.set(0, 1, 0)
    } else {
      forward.normalize()
    }

    const target = position.clone().addScaledVector(forward, IMPORTED_CAMERA_TARGET_DISTANCE)
    const time = firstTimestamp !== null && frame.timestamp !== null ?
      importedTimestampDeltaToSeconds(frame.timestamp - firstTimestamp) :
      index * KEYFRAME_TIME_STEP

    return createKeyframe({
      time: roundNumber(Math.max(0, time), 3),
      position: [
        roundNumber(position.x),
        roundNumber(position.y),
        roundNumber(position.z),
      ],
      target: [
        roundNumber(target.x),
        roundNumber(target.y),
        roundNumber(target.z),
      ],
      fov: currentCameraFov.value,
    })
  })
}

function openCameraPathImportPicker(): void {
  const input = cameraPathInputRef.value
  if (!input) {
    return
  }

  input.value = ''
  input.click()
}

async function handleCameraPathFileSelected(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) {
    return
  }

  try {
    importedCameraPath.value = parseImportedCameraPathJson(await file.text(), file.name)
    cameraPathImportMessage.value =
      `已读取 ${file.name}：左 ${importedCameraPath.value.left.length} 帧，` +
      `右 ${importedCameraPath.value.right.length} 帧。`
  } catch (error) {
    importedCameraPath.value = null
    cameraPathImportMessage.value = `外部镜头路径解析失败：${getErrorMessage(error)}`
  } finally {
    input.value = ''
  }
}

function applyImportedCameraPath(side: ImportedCameraPathSide): void {
  const imported = importedCameraPath.value
  const frames = imported?.[side] ?? []
  if (frames.length === 0) {
    cameraPathImportMessage.value = side === 'left' ?
      '当前文件没有左镜头路径。' :
      '当前文件没有右镜头路径。'
    return
  }

  stopCameraAnimation()
  clearCameraMotionTemplateMessage()
  clearGeneratedCameraMotionPlan()
  cameraSegmentEasing.value = null
  cameraKeyframes.value = createImportedCameraPathKeyframes(frames)
  sortKeyframes()
  selectedKeyframeId.value = cameraKeyframes.value[0]?.id ?? null
  const firstFrame = cameraKeyframes.value[0]
  if (firstFrame) {
    applyCameraPose(firstFrame, false)
  }
  cameraPathImportMessage.value = side === 'left' ?
    `已生成左镜头关键帧：${frames.length} 帧。` :
    `已生成右镜头关键帧：${frames.length} 帧。`
}

function normalizeCameraMotionTemplateDuration(): void {
  const duration = Number(cameraMotionTemplateDuration.value)
  cameraMotionTemplateDuration.value = Number.isFinite(duration) ?
    Math.min(
      CAMERA_MOTION_TEMPLATE_DURATION_MAX,
      Math.max(CAMERA_MOTION_TEMPLATE_DURATION_MIN, duration),
    ) :
    DEFAULT_CAMERA_MOTION_TEMPLATE_DURATION
}

function selectCameraMotionSemantic(event: Event): void {
  selectedCameraMotionSemantic.value = (event.target as HTMLSelectElement).value as CameraMotionSemanticKey
  syncCameraMotionParamState(selectedCameraMotionTemplate.value)
  syncCameraMotionMetaState(selectedCameraMotionTemplate.value)
  syncCameraMotionTimingState(selectedCameraMotionTemplate.value)
  clearCameraMotionTemplateMessage()
}

function applyEffectTemplate(option: EffectTemplateOption): void {
  try {
    const template = option.template ?? parseGSplatEffectTemplate(option.source)

    stopCameraAnimation()
    clearShaderEffect(false)
    writeEffectTemplateState(template)
    loadError.value = ''
    effectTemplateMessage.value = `已套用「${template.name}」参数，点击播放后生效。`
  } catch (error) {
    effectTemplateMessage.value = `特效模板套用失败：${getErrorMessage(error)}`
  }
}

function applySelectedCameraMotionTemplate(playOnce = false): void {
  const template = selectedCameraMotionTemplate.value
  if (!template) {
    cameraMotionTemplateMessage.value = '没有可用的运镜模板。'
    return
  }
  applyCameraMotionTemplate({
    source: template,
    template,
    key: template.id,
    label: selectedCameraMotionSemanticOption.value.label,
    detail: selectedCameraMotionSemanticOption.value.detail,
    error: null,
  }, { playOnce })
}

function applyCameraMotionTemplate(
  option: CameraMotionTemplateOption,
  playbackOptions: { playOnce?: boolean } = {},
): void {
  try {
    normalizeCameraMotionTemplateDuration()
    const template = createCurrentCameraMotionTemplate()
    const isRestartingActivePlayback =
      playbackOptions.playOnce && (playerState.value === 'playing' || playerState.value === 'paused')
    const firstFrameAsStart =
      template.semantic !== 'sequence' && isRestartingActivePlayback ? cameraKeyframes.value[0] : null
    const state = firstFrameAsStart ? null : getCurrentCameraState()
    if (!firstFrameAsStart && !state) {
      cameraMotionTemplateMessage.value = '请先初始化引擎相机。'
      return
    }
    stopCameraAnimation()

    const startCamera = firstFrameAsStart ? {
      position: firstFrameAsStart.position,
      target: firstFrameAsStart.target,
      up: [0, 0, 1] as Vector3Tuple,
      fov: firstFrameAsStart.fov,
      roll: firstFrameAsStart.roll,
    } : {
      position: toVectorTuple(state!.position),
      target: toVectorTuple(state!.target),
      up: toVectorTuple(state!.up),
      fov: state!.fov,
      roll: state!.roll,
    }

    if (template.semantic === 'sequence') {
      const plan = createSemanticCameraMotionPlan(
        template,
        { camera: startCamera },
        { duration: normalizedCameraMotionTemplateDuration.value },
      )
      setGeneratedCameraMotionPlan(plan)
      playCameraMotionSequencePlan(plan)
      cameraMotionTemplateMessage.value =
        `已生成并播放「${template.name}」：${plan.diagnostics.leafCount} 个叶子运镜，` +
        `${plan.diagnostics.segmentCount} 个独立片段，总时长 ${formatNumber(plan.duration, 1)}s。`
      return
    }

    const result = createSemanticCameraMotion(
      template,
      {
        camera: startCamera,
      },
      { duration: normalizedCameraMotionTemplateDuration.value },
    )

    interpolationMode.value = template.interpolation
    easingMode.value = template.easing ?? 'none'
    loopMode.value = 'once'
    clearGeneratedCameraMotionPlan()
    cameraSegmentEasing.value = result.clip.tracks[0]?.segmentEasing ?
      Array.from(result.clip.tracks[0].segmentEasing) :
      null
    cameraKeyframes.value = result.keyframes.map((frame) =>
      createKeyframe({
        time: roundNumber(frame.time, 3),
        position: [
          roundNumber(frame.position[0]),
          roundNumber(frame.position[1]),
          roundNumber(frame.position[2]),
        ],
        target: [
          roundNumber(frame.target[0]),
          roundNumber(frame.target[1]),
          roundNumber(frame.target[2]),
        ],
        fov: roundNumber(clampNumber(frame.fov, CAMERA_FOV_MIN, CAMERA_FOV_MAX), 1),
        roll: roundNumber(frame.roll),
      })
    )
    sortKeyframes()
    selectedKeyframeId.value = cameraKeyframes.value[0]?.id ?? null
    const firstFrame = cameraKeyframes.value[0]
    if (firstFrame) {
      applyCameraPose(firstFrame, false)
    }
    currentCameraFov.value = firstFrame?.fov ?? currentCameraFov.value
    if (playbackOptions.playOnce) {
      playCameraAnimation(0, {
        autoplay: true,
        lockControls: true,
        resetToFirstFrameOnComplete: true,
      })
    }
    cameraMotionTemplateMessage.value =
      `已生成「${template.name}」关键帧：${result.keyframes.length} 帧，` +
      `时长 ${formatNumber(result.clip.duration ?? normalizedCameraMotionTemplateDuration.value, 1)}s。`
  } catch (error) {
    cameraMotionTemplateMessage.value = `运镜模板生成失败：${getErrorMessage(error)}`
  }
}

function playCameraMotionSequencePlan(
  plan: SemanticCameraMotionPlan,
  options: CameraMotionPlanPlaybackOptions = {},
): void {
  const service = getAnimationService()
  if (!service) {
    throw new Error('组合运镜播放前需要 AnimationService 可用。')
  }

  lockCameraControlsForPlayback()
  saveCameraPlaybackReturnPose()
  const planPlayer = playSemanticCameraMotionPlan(service, plan, {
    id: `effect-camera-sequence-${nextCameraPlayerId++}`,
    autoplay: false,
  })
  planPlayer.on('complete', () => {
    if (cameraMotionPlanPlayer.value !== planPlayer) {
      return
    }
    cameraMotionPlanPlayer.value = null
    playerState.value = 'completed'
    restoreCameraControlsAfterPlayback()
    resetCameraToPlaybackReturnPose()
    if (options.clearShaderEffectOnComplete) {
      clearShaderEffect()
    }
    cameraPlaybackReturnState.value = null
    syncCurrentCameraFov()
  })
  planPlayer.on('stop', () => {
    if (cameraMotionPlanPlayer.value === planPlayer) {
      cameraMotionPlanPlayer.value = null
    }
  })
  cameraMotionPlanPlayer.value = planPlayer
  playerState.value = 'playing'
  planPlayer.play()
}

function pauseCameraMotionSequence(): void {
  const planPlayer = cameraMotionPlanPlayer.value
  if (!planPlayer || planPlayer.state !== 'playing') {
    return
  }
  planPlayer.pause()
  playerState.value = 'paused'
}

function resumeCameraMotionSequence(): void {
  const planPlayer = cameraMotionPlanPlayer.value
  if (!planPlayer || planPlayer.state !== 'paused') {
    return
  }
  planPlayer.resume()
  playerState.value = 'playing'
}

function applyCameraPose(frame: CameraFramePose, stopAnimation = true): void {
  const state = getCurrentCameraState()
  if (!state) {
    return
  }

  if (stopAnimation) {
    stopCameraAnimation()
  }
  state.position.set(frame.position[0], frame.position[1], frame.position[2])
  state.target.set(frame.target[0], frame.target[1], frame.target[2])
  applyCameraPatch({
    position: state.position,
    target: state.target,
    fov: frame.fov,
    roll: frame.roll ?? 0,
  })
  syncCurrentCameraFov()
}

function applyKeyframeToCamera(frame: CameraKeyframe): void {
  applyCameraPose(frame)
}

function syncFirstKeyframeFromCamera(): void {
  const state = getCurrentCameraState()
  const firstFrame = cameraKeyframes.value[0]
  if (!state || !firstFrame) {
    return
  }

  firstFrame.position = [
    roundNumber(state.position.x),
    roundNumber(state.position.y),
    roundNumber(state.position.z),
  ]
  firstFrame.target = [
    roundNumber(state.target.x),
    roundNumber(state.target.y),
    roundNumber(state.target.z),
  ]
  firstFrame.fov = roundNumber(state.fov, 1)
  firstFrame.roll = roundNumber(state.roll)
  selectedKeyframeId.value = firstFrame.id
  syncCurrentCameraFov()
}

function prepareCameraAnimationView(): void {
  useEarthControl()
  syncFirstKeyframeFromCamera()
  selectedCameraMotionSemantic.value = 'dolly'
  syncCameraMotionParamState(selectedCameraMotionTemplate.value)
  syncCameraMotionMetaState(selectedCameraMotionTemplate.value)
  syncCameraMotionTimingState(selectedCameraMotionTemplate.value)
  applySelectedCameraMotionTemplate()
}

function updateCurrentCameraFov(): void {
  const fov = roundNumber(
    clampNumber(currentCameraFov.value, CAMERA_FOV_MIN, CAMERA_FOV_MAX),
    1,
  )
  currentCameraFov.value = fov
  stopCameraAnimation()
  applyCameraPatch({ fov })
}

function getTrackTiming(): Partial<
  Pick<AnimationTrackData, 'interpolation' | 'easing' | 'segmentEasing' | 'smoothness'>
> {
  const timing: Partial<
    Pick<AnimationTrackData, 'interpolation' | 'easing' | 'segmentEasing' | 'smoothness'>
  > = {
    interpolation: interpolationMode.value,
  }

  if (interpolationMode.value === 'spline') {
    timing.smoothness = 1
  }

  if (effectiveEasingMode.value !== 'none') {
    timing.easing = effectiveEasingMode.value
  }

  const segmentEasing = getCameraSegmentEasing()
  if (segmentEasing) {
    timing.segmentEasing = segmentEasing
  }

  return timing
}

function getCameraSegmentEasing(): AnimationEasing[] | null {
  if (interpolationMode.value === 'step') {
    return null
  }

  const segmentEasing = cameraSegmentEasing.value
  const expectedLength = Math.max(0, cameraKeyframes.value.length - 1)
  return segmentEasing && segmentEasing.length === expectedLength ?
    Array.from(segmentEasing) :
    null
}

function createCameraClip(): AnimationClipData {
  const target = { kind: 'camera' as const, active: 'bind-on-play' as const }
  const frames = cameraKeyframes.value
  const times = frames.map((frame) => frame.time)
  const trackTiming = getTrackTiming()

  return {
    name: '3DGS effect camera timeline',
    duration: cameraClipDuration.value,
    loopMode: loopMode.value,
    metadata: {
      interpolationMode: interpolationMode.value,
      easingMode: effectiveEasingMode.value,
      loopMode: loopMode.value,
    },
    tracks: [
      {
        target,
        propertyPath: 'position',
        valueType: 'vec3',
        ...trackTiming,
        times,
        values: frames.flatMap((frame) => frame.position),
      },
      {
        target,
        propertyPath: 'target',
        valueType: 'vec3',
        ...trackTiming,
        times,
        values: frames.flatMap((frame) => frame.target),
      },
      {
        target,
        propertyPath: 'fov',
        valueType: 'number',
        ...trackTiming,
        times,
        values: frames.map((frame) => frame.fov),
      },
      {
        target,
        propertyPath: 'roll',
        valueType: 'number',
        ...trackTiming,
        times,
        values: frames.map((frame) => frame.roll),
      },
    ],
  }
}

function cleanupPlayer(): void {
  clearCameraPlaybackFallbackTimer()
  const currentPlayer = player.value
  if (currentPlayer) {
    const stoppedByService = getAnimationService()?.stop(currentPlayer.id) ?? false
    if (!stoppedByService) {
      currentPlayer.stop()
    }
  }
  player.value = null
}

function clearCameraPlaybackFallbackTimer(): void {
  if (cameraPlaybackFallbackTimer.value !== null) {
    clearTimeout(cameraPlaybackFallbackTimer.value)
    cameraPlaybackFallbackTimer.value = null
  }
}

function lockCameraControlsForPlayback(): void {
  const controls = engine.value?.controls
  if (!controls || cameraControlsInputLocked.value) {
    return
  }

  setCameraControlsInputActive(false)
  cameraControlsInputLocked.value = true
}

function restoreCameraControlsAfterPlayback(): void {
  if (!cameraControlsInputLocked.value) {
    return
  }

  setCameraControlsInputActive(true)
  cameraControlsInputLocked.value = false
}

function setCameraControlsInputActive(active: boolean): void {
  const controls = engine.value?.controls
  if (!controls) {
    return
  }

  controls.setInputActive(active)
  const controller = controls.activeController
  if (controller && 'enabled' in controller) {
    controller.enabled = active
  }
}

function cloneCameraState(state: CameraState): CameraState {
  return {
    ...state,
    position: state.position.clone(),
    target: state.target.clone(),
    up: state.up.clone(),
  }
}

function saveCameraPlaybackReturnPose(): void {
  const state = getCurrentCameraState()
  cameraPlaybackReturnState.value = state ? cloneCameraState(state) : null
}

function resetCameraToPlaybackReturnPose(): void {
  const state = cameraPlaybackReturnState.value
  if (state) {
    engine.value?.camera.setState(cloneCameraState(state))
    syncCurrentCameraFov()
  }
}

function finishCameraPlayback(
  expectedPlayer: AnimationPlayerHandle,
  options: Pick<CameraPlaybackOptions, 'resetToFirstFrameOnComplete' | 'clearShaderEffectOnComplete'>,
  stopPlayer: boolean,
  token: number,
): void {
  if (token !== cameraPlaybackToken) {
    return
  }

  if (stopPlayer) {
    if (player.value === expectedPlayer) {
      cleanupPlayer()
    } else {
      clearCameraPlaybackFallbackTimer()
    }
  } else {
    clearCameraPlaybackFallbackTimer()
    if (player.value === expectedPlayer) {
      player.value = null
    }
  }
  playerState.value = 'completed'
  restoreCameraControlsAfterPlayback()
  if (options.resetToFirstFrameOnComplete) {
    resetCameraToPlaybackReturnPose()
  }
  if (options.clearShaderEffectOnComplete) {
    clearShaderEffect()
  }
  cameraPlaybackReturnState.value = null
  syncCurrentCameraFov()
}

function scheduleCameraPlaybackFallback(
  expectedPlayer: AnimationPlayerHandle,
  options: Pick<CameraPlaybackOptions, 'resetToFirstFrameOnComplete' | 'clearShaderEffectOnComplete'>,
  startTime: number,
  token: number,
): void {
  clearCameraPlaybackFallbackTimer()
  const remainingSeconds = Math.max(0, cameraClipDuration.value - startTime)
  cameraPlaybackFallbackTimer.value = setTimeout(() => {
    finishCameraPlayback(expectedPlayer, options, true, token)
  }, remainingSeconds * 1000 + 500)
}

function playCameraAnimation(
  startTime = 0,
  options: boolean | CameraPlaybackOptions = true,
): void {
  const service = getAnimationService()
  if (!service || cameraKeyframes.value.length === 0) {
    return
  }

  const playbackOptions = typeof options === 'boolean' ? { autoplay: options } : options
  const autoplay = playbackOptions.autoplay ?? true
  const token = ++cameraPlaybackToken

  cleanupPlayer()
  if (playbackOptions.lockControls) {
    lockCameraControlsForPlayback()
  } else {
    restoreCameraControlsAfterPlayback()
  }
  if (playbackOptions.resetToFirstFrameOnComplete) {
    saveCameraPlaybackReturnPose()
  } else {
    cameraPlaybackReturnState.value = null
  }
  const nextPlayer = service.play(createCameraClip(), {
    id: `effect-camera-demo-${nextCameraPlayerId++}`,
    startTime: clampNumber(startTime, 0, Math.max(cameraClipDuration.value, 0)),
    autoplay: false,
  })
  nextPlayer.on('complete', () => {
    finishCameraPlayback(nextPlayer, playbackOptions, false, token)
  })
  player.value = nextPlayer
  playerState.value = autoplay ? 'playing' : 'paused'
  if (autoplay && (playbackOptions.lockControls || playbackOptions.resetToFirstFrameOnComplete)) {
    scheduleCameraPlaybackFallback(nextPlayer, playbackOptions, startTime, token)
  }
  if (autoplay) {
    nextPlayer.play()
  }
}

function stopCameraAnimation(): void {
  cameraPlaybackToken += 1
  const planPlayer = cameraMotionPlanPlayer.value
  cameraMotionPlanPlayer.value = null
  planPlayer?.stop()
  cleanupPlayer()
  restoreCameraControlsAfterPlayback()
  cameraPlaybackReturnState.value = null
  playerState.value = 'idle'
}

function restartActivePlayer(): void {
  const currentPlayer = player.value
  if (!currentPlayer || cameraKeyframes.value.length === 0) {
    return
  }

  const shouldAutoplay = playerState.value === 'playing'
  if (!shouldAutoplay && playerState.value !== 'paused') {
    return
  }

  playCameraAnimation(currentPlayer.time, shouldAutoplay)
}

function selectInterpolationMode(mode: AnimationInterpolation): void {
  cameraSegmentEasing.value = null
  interpolationMode.value = mode
  restartActivePlayer()
}

function selectEasingMode(mode: CameraEasingMode): void {
  if (interpolationMode.value === 'step' && mode !== 'none') {
    return
  }

  easingMode.value = mode
  cameraSegmentEasing.value = null
  restartActivePlayer()
}

function selectLoopMode(mode: AnimationLoopMode): void {
  loopMode.value = mode
  restartActivePlayer()
}

function useEarthControl(): void {
  engine.value?.controls.setMode('earth')
}

function refreshFirstGsplatObject(): void {
  firstGsplatObject.value = getFirstGSplatHandle()?.object3D ?? null
}

function handleModelTransformChanged(object: THREE.Object3D): void {
  object.updateMatrixWorld(true)
  engine.value?.context.renderInvalidation.invalidate('example.gs3d.modelTransform.changed')
}

function ensureWorldAxesContributor(): THREE.AxesHelper | null {
  const currentEngine = engine.value
  if (!currentEngine) {
    return null
  }

  if (!worldAxesScene) {
    worldAxesScene = new THREE.Scene()
  }

  if (!worldAxesHelper) {
    worldAxesHelper = new THREE.AxesHelper(5)
    worldAxesHelper.name = 'WorldOriginAxes'
    worldAxesHelper.visible = worldAxesVisible.value
    worldAxesScene.add(worldAxesHelper)
  }

  if (!worldAxesContributor) {
    worldAxesContributor = {
      kind: 'gs3d-effect-animation-world-axes',
      phases: [RenderPhase.Overlay3D],
      // 世界坐标轴走统一图形后端提交；WebGL2 仍用 legacy renderer，WebGPU 复用 renderScene。
      graphicsRequirements: { backends: ['webgl2', 'webgpu'] },
      render(frame, phase, context) {
        if (phase !== RenderPhase.Overlay3D || !worldAxesScene || !worldAxesHelper?.visible) {
          return
        }

        if (context.graphics.kind === 'webgpu') {
          context.graphics.renderScene?.(worldAxesScene, frame.camera)
          return
        }

        context.renderer.render(worldAxesScene, frame.camera)
      },
    }
    currentEngine.context.getService('RenderPipeline').addRenderContributor(worldAxesContributor)
  }

  return worldAxesHelper
}

function syncWorldAxesVisibility(): void {
  const helper = ensureWorldAxesContributor()
  if (!helper) {
    return
  }

  helper.visible = worldAxesVisible.value
  engine.value?.context.renderInvalidation.invalidate('example.gs3d.worldAxes.changed')
}

function disposeWorldAxesHelper(): void {
  if (worldAxesContributor) {
    engine.value?.context.getService('RenderPipeline').removeRenderContributor(worldAxesContributor)
    worldAxesContributor = null
  }

  worldAxesHelper?.geometry.dispose()
  if (Array.isArray(worldAxesHelper?.material)) {
    for (const material of worldAxesHelper.material) {
      material.dispose()
    }
  } else {
    worldAxesHelper?.material.dispose()
  }
  worldAxesHelper?.removeFromParent()
  worldAxesHelper = null
  worldAxesScene?.clear()
  worldAxesScene = null
}

function getFirstGSplatHandle() {
  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')
  return plugin?.getAll()[0] ?? null
}

function isSinglePlyModelInput(path: string): boolean {
  const cleanPath = path.split(/[?#]/, 1)[0]?.toLowerCase() ?? ''
  return cleanPath.endsWith('.ply')
}

function createBoundingBoxPivot(object: THREE.Object3D): THREE.Vector3 | null {
  object.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(object)
  if (box.isEmpty()) {
    return null
  }

  const center = box.getCenter(new THREE.Vector3())
  return object.worldToLocal(center.clone())
}

function centerObjectPivotAtOrigin(object: THREE.Object3D, pivot: THREE.Vector3): void {
  object.updateMatrixWorld(true)
  const worldPivot = object.localToWorld(pivot.clone())
  const parentPivot = object.parent ? object.parent.worldToLocal(worldPivot) : worldPivot
  object.position.sub(parentPivot)
  object.updateMatrixWorld(true)
}

function setActiveShaderEffect(effect: GSplatShaderEffectLike | null, option: ShaderEffectOption | null): void {
  clearParticleEffectCompletionTimer()
  activeShaderEffect = effect
  activeEffectName.value = option?.label ?? '未启用'
  activeEffectKey.value = option?.key ?? null
  if (effect && option && isParticleEffectKey(option.key)) {
    const completionTime = (
      effect as GSplatShaderEffectLike & { getCompletionTime?: () => number }
    ).getCompletionTime?.()
    if (typeof completionTime === 'number' && Number.isFinite(completionTime)) {
      particleEffectCompletionTimer = setTimeout(() => {
        if (activeShaderEffect === effect) {
          clearShaderEffect(false)
        }
      }, completionTime * 1000 + 100)
    }
  }
}

function clearParticleEffectCompletionTimer(): void {
  if (particleEffectCompletionTimer !== null) {
    clearTimeout(particleEffectCompletionTimer)
    particleEffectCompletionTimer = null
  }
}

function createColorControl(color: ColorTuple): RevealColorControl {
  return {
    hex: colorTupleToHex(color),
    intensity: getColorIntensity(color) || DEFAULT_COLOR_INTENSITY,
  }
}

function getColorIntensity(color: ColorTuple): number {
  return Math.min(COLOR_INTENSITY_MAX, Math.max(0, color[0], color[1], color[2]))
}

function getColorBase(color: ColorTuple): ColorTuple {
  const intensity = getColorIntensity(color)
  if (intensity <= 0.00001) {
    return [1, 1, 1]
  }

  return [
    Math.min(1, Math.max(0, color[0] / intensity)),
    Math.min(1, Math.max(0, color[1] / intensity)),
    Math.min(1, Math.max(0, color[2] / intensity)),
  ]
}

function colorTupleToHex(color: ColorTuple): string {
  const base = getColorBase(color)
  return `#${base.map((value) => Math.round(value * 255).toString(16).padStart(2, '0')).join('')}`
}

function parseColorHex(hex: string): ColorTuple | null {
  const value = /^#?([0-9a-f]{6})$/i.exec(hex.trim())?.[1]
  if (!value) {
    return null
  }

  return [
    Number.parseInt(value.slice(0, 2), 16) / 255,
    Number.parseInt(value.slice(2, 4), 16) / 255,
    Number.parseInt(value.slice(4, 6), 16) / 255,
  ]
}

function applyColorIntensity(base: ColorTuple, intensity: number): ColorTuple {
  const value = Math.min(COLOR_INTENSITY_MAX, Math.max(0, intensity))
  return [base[0] * value, base[1] * value, base[2] * value]
}

function getRevealParams(panelKey: RevealPanelKey): Record<string, unknown> {
  if (!selectedRevealAdaptive.value) {
    if (panelKey === 'radial') {
      return radialRevealManualParams.value
    }
    if (panelKey === 'rain') {
      return rainRevealManualParams.value
    }
    return gridRevealManualParams.value
  }
  if (panelKey === 'radial') {
    return radialRevealParams.value
  }
  if (panelKey === 'rain') {
    return rainRevealParams.value
  }
  return gridRevealParams.value
}

function getRevealNumberFields(panelKey: RevealPanelKey): RevealNumberField[] {
  if (selectedRevealAdaptive.value) {
    return REVEAL_PARAM_PANELS.find((panel) => panel.key === panelKey)?.numberFields ?? []
  }
  if (panelKey === 'radial') {
    return RADIAL_REVEAL_MANUAL_NUMBER_FIELDS
  }
  if (panelKey === 'rain') {
    return RAIN_REVEAL_MANUAL_NUMBER_FIELDS
  }
  return GRID_REVEAL_MANUAL_NUMBER_FIELDS
}

function getRevealColorFields(panelKey: RevealPanelKey): RevealColorField[] {
  return REVEAL_PARAM_PANELS.find((panel) => panel.key === panelKey)?.colorFields ?? []
}

function getRevealNumberParam(panelKey: RevealPanelKey, paramKey: string): number {
  const value = getRevealParams(panelKey)[paramKey]
  return typeof value === 'number' ? value : 0
}

function setRevealNumberParam(panelKey: RevealPanelKey, field: RevealNumberField, event: Event, effectKey: string): void {
  const input = event.target as HTMLInputElement
  const rawValue = Number(input.value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  getRevealParams(panelKey)[field.key] = Math.min(field.max, Math.max(field.min, rawValue))
  refreshActiveRevealEffect(effectKey)
}

function getRevealColorParam(panelKey: RevealPanelKey, paramKey: string): ColorTuple {
  const value = getRevealParams(panelKey)[paramKey]
  return Array.isArray(value) && value.length === 3 ? value as ColorTuple : [0, 0, 0]
}

function getRevealColorControl(panelKey: RevealPanelKey, paramKey: string): RevealColorControl {
  const controls = selectedRevealAdaptive.value ?
    revealColorControls.value[panelKey] :
    revealManualColorControls.value[panelKey]
  if (!controls[paramKey]) {
    controls[paramKey] = createColorControl(getRevealColorParam(panelKey, paramKey))
  }
  return controls[paramKey]
}

function getRevealColorHex(panelKey: RevealPanelKey, paramKey: string): string {
  return getRevealColorControl(panelKey, paramKey).hex
}

function getRevealColorIntensity(panelKey: RevealPanelKey, paramKey: string): number {
  return getRevealColorControl(panelKey, paramKey).intensity
}

function syncRevealColorParam(panelKey: RevealPanelKey, paramKey: string): void {
  const control = getRevealColorControl(panelKey, paramKey)
  const base = parseColorHex(control.hex)
  if (!base) {
    return
  }
  getRevealParams(panelKey)[paramKey] = applyColorIntensity(base, control.intensity)
}

function setRevealColorHex(panelKey: RevealPanelKey, paramKey: string, event: Event, effectKey: string): void {
  const input = event.target as HTMLInputElement
  if (!parseColorHex(input.value)) {
    return
  }

  getRevealColorControl(panelKey, paramKey).hex = input.value
  syncRevealColorParam(panelKey, paramKey)
  refreshActiveRevealEffect(effectKey)
}

function setRevealColorIntensity(panelKey: RevealPanelKey, paramKey: string, event: Event, effectKey: string): void {
  const input = event.target as HTMLInputElement
  const rawValue = Number(input.value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  getRevealColorControl(panelKey, paramKey).intensity = Math.min(COLOR_INTENSITY_MAX, Math.max(0, rawValue))
  syncRevealColorParam(panelKey, paramKey)
  refreshActiveRevealEffect(effectKey)
}

function getVisualShowcaseNumberParam(effectKey: VisualShowcaseEffectKey, paramKey: string): number {
  const value = (visualShowcaseParams.value[effectKey] as Record<string, unknown>)[paramKey]
  return typeof value === 'number' ? value : 0
}

function setVisualShowcaseNumberParam(
  effectKey: VisualShowcaseEffectKey,
  field: RevealNumberField,
  event: Event,
): void {
  const input = event.target as HTMLInputElement
  const rawValue = Number(input.value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  const params = visualShowcaseParams.value[effectKey] as Record<string, unknown>
  params[field.key] = Math.min(
    field.max,
    Math.max(field.min, rawValue),
  )
  refreshActiveShowcaseEffect(effectKey)
}

function getShowcaseRevealNumberParam(effectKey: ShowcaseRevealEffectKey, paramKey: string): number {
  const value = (showcaseRevealParams.value[effectKey] as Record<string, number>)[paramKey]
  return typeof value === 'number' ? value : 0
}

function setShowcaseRevealNumberParam(
  effectKey: ShowcaseRevealEffectKey,
  field: RevealNumberField,
  event: Event,
): void {
  const input = event.target as HTMLInputElement
  const rawValue = Number(input.value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  const params = showcaseRevealParams.value[effectKey] as Record<string, number>
  params[field.key] = Math.min(
    field.max,
    Math.max(field.min, rawValue),
  )
  refreshActiveShowcaseEffect(effectKey)
}

function getShowcaseRevealOptionNumberParam(effectKey: string, paramKey: string): number {
  return isShowcaseRevealEffectKey(effectKey) ?
    getShowcaseRevealNumberParam(effectKey, paramKey) :
    0
}

function setShowcaseRevealOptionNumberParam(
  effectKey: string,
  field: RevealNumberField,
  event: Event,
): void {
  if (isShowcaseRevealEffectKey(effectKey)) {
    setShowcaseRevealNumberParam(effectKey, field, event)
  }
}

function getDissolveNumberParam(effectKey: DissolveEffectKey, paramKey: string): number {
  const value = (dissolveParams.value[effectKey] as Record<string, number>)[paramKey]
  return typeof value === 'number' ? value : 0
}

function setDissolveNumberParam(
  effectKey: DissolveEffectKey,
  field: RevealNumberField,
  event: Event,
): void {
  const input = event.target as HTMLInputElement
  const rawValue = Number(input.value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  const params = dissolveParams.value[effectKey] as Record<string, number>
  params[field.key] = Math.min(
    field.max,
    Math.max(field.min, rawValue),
  )
  refreshActiveShowcaseEffect(effectKey)
}

type ParticleNumericGroup = 'style' | 'jitter' | 'timing' | 'random'

function getParticleNumberParam(
  effectKey: ParticleEffectKey,
  group: ParticleNumericGroup,
  paramKey: string,
): number {
  const value = (particleParams.value[effectKey][group] as unknown as Record<string, unknown>)[
    paramKey
  ]
  return typeof value === 'number' ? value : 0
}

function getParticleStyleNumberFields(effectKey: ParticleEffectKey): RevealNumberField[] {
  const sizeField =
    particleParams.value[effectKey].style.sizeMode === 'adaptive'
      ? PARTICLE_ADAPTIVE_SIZE_FIELD
      : PARTICLE_FIXED_SIZE_FIELD
  return [sizeField, ...PARTICLE_STYLE_NUMBER_FIELDS]
}

function getParticleMotionNumberFields(effectKey: ParticleEffectKey): RevealNumberField[] {
  switch (particleParams.value[effectKey].motion.type) {
    case 'chaotic':
      return PARTICLE_CHAOTIC_MOTION_FIELDS
    case 'layered-print':
      return PARTICLE_LAYERED_PRINT_MOTION_FIELDS
    case 'fractal-orbit':
      return PARTICLE_FRACTAL_ORBIT_MOTION_FIELDS
  }
}

function getParticleMotionNumberParam(effectKey: ParticleEffectKey, paramKey: string): number {
  const params = particleParams.value[effectKey].motion.params as unknown as Record<
    string,
    unknown
  >
  const value = params[paramKey]
  return typeof value === 'number' ? value : 0
}

function getParticleMotionDetail(effectKey: ParticleEffectKey): string {
  const type = particleParams.value[effectKey].motion.type
  return PARTICLE_MOTION_OPTIONS.find((option) => option.value === type)?.detail ?? ''
}

function setParticleMotionType(effectKey: ParticleEffectKey, event: Event): void {
  const value = (event.target as HTMLSelectElement).value as GSplatParticleMotionType
  switch (value) {
    case 'chaotic':
      particleParams.value[effectKey].motion = {
        type: value,
        params: { ...GSPLAT_PARTICLE_MOTION_DEFAULTS.chaotic },
      }
      break
    case 'layered-print':
      particleParams.value[effectKey].motion = {
        type: value,
        params: { ...GSPLAT_PARTICLE_MOTION_DEFAULTS['layered-print'] },
      }
      break
    case 'fractal-orbit':
      particleParams.value[effectKey].motion = {
        type: value,
        params: { ...GSPLAT_PARTICLE_MOTION_DEFAULTS['fractal-orbit'] },
      }
      break
    default:
      return
  }
  refreshActiveShowcaseEffect(effectKey)
}

function setParticleMotionNumberParam(
  effectKey: ParticleEffectKey,
  field: RevealNumberField,
  event: Event,
): void {
  const rawValue = Number((event.target as HTMLInputElement).value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  let value = Math.min(field.max, Math.max(field.min, rawValue))
  if (field.key === 'layerCount') {
    value = Math.round(value)
  }
  const params = particleParams.value[effectKey].motion.params as unknown as Record<string, number>
  params[field.key] = value
  refreshActiveShowcaseEffect(effectKey)
}

function setParticlePrintDirection(effectKey: ParticleEffectKey, event: Event): void {
  const motion = particleParams.value[effectKey].motion
  const value = (event.target as HTMLSelectElement).value
  if (motion.type !== 'layered-print' || (value !== 'bottom-up' && value !== 'top-down')) {
    return
  }
  motion.params.direction = value
  refreshActiveShowcaseEffect(effectKey)
}

function getParticleNumberFieldMax(
  effectKey: ParticleEffectKey,
  field: RevealNumberField,
): number {
  const params = particleParams.value[effectKey].timing
  if (field.key === 'preScatterHoldDuration') {
    return Math.max(0, params.duration - params.peakHoldDuration - 0.1)
  }
  if (field.key === 'peakHoldDuration') {
    return Math.max(0, params.duration - params.preScatterHoldDuration - 0.1)
  }
  return field.max
}

function setParticleSizeMode(effectKey: ParticleEffectKey, event: Event): void {
  const value = (event.target as HTMLSelectElement).value
  if (value !== 'fixed' && value !== 'adaptive') {
    return
  }
  particleParams.value[effectKey].style.sizeMode = value
  refreshActiveShowcaseEffect(effectKey)
}

function setParticleNumberParam(
  effectKey: ParticleEffectKey,
  group: ParticleNumericGroup,
  field: RevealNumberField,
  event: Event,
): void {
  const rawValue = Number((event.target as HTMLInputElement).value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  const params = particleParams.value[effectKey]
  let value = Math.min(
    getParticleNumberFieldMax(effectKey, field),
    Math.max(field.min, rawValue),
  )
  if (field.key === 'seed') {
    value = Math.round(value)
  }
  ;(params[group] as unknown as Record<string, number>)[field.key] = value
  if (
    group === 'timing' &&
    field.key === 'duration' &&
    params.timing.preScatterHoldDuration + params.timing.peakHoldDuration >= value
  ) {
    const holdTotal = params.timing.preScatterHoldDuration + params.timing.peakHoldDuration
    const availableHoldDuration = Math.max(0, value - 0.1)
    if (holdTotal > 0) {
      params.timing.preScatterHoldDuration =
        availableHoldDuration * (params.timing.preScatterHoldDuration / holdTotal)
      params.timing.peakHoldDuration =
        availableHoldDuration - params.timing.preScatterHoldDuration
    }
  }
  refreshActiveShowcaseEffect(effectKey)
}

function getParticlePhaseWeight(effectKey: ParticleEffectKey, phase: string): number {
  const value = (particleParams.value[effectKey].timing.phaseWeights as Record<string, number>)[
    phase
  ]
  return typeof value === 'number' ? value : 1
}

function setParticlePhaseWeight(
  effectKey: ParticleEffectKey,
  field: RevealNumberField,
  event: Event,
): void {
  const rawValue = Number((event.target as HTMLInputElement).value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  const input = event.target as HTMLInputElement
  const phaseWeights = particleParams.value[effectKey].timing.phaseWeights as Record<
    string,
    number
  >
  const currentValue = phaseWeights[field.key]
  const value = Math.min(field.max, Math.max(field.min, rawValue))
  const nextTotal = Object.entries(phaseWeights).reduce(
    (total, [key, weight]) => total + (key === field.key ? value : weight),
    0,
  )
  if (nextTotal <= 0) {
    input.value = String(currentValue)
    return
  }

  phaseWeights[field.key] = value
  refreshActiveShowcaseEffect(effectKey)
}

function getVisualDissolveNumberFields(effectKey: string): RevealNumberField[] {
  if (effectKey === 'visual-electronic') {
    return ELECTRONIC_NUMBER_FIELDS
  }
  return isVisualShowcaseEffectKey(effectKey) ? VISUAL_SHOWCASE_NUMBER_FIELDS : DISSOLVE_NUMBER_FIELDS
}

function getVisualDissolveNumberParam(effectKey: string, paramKey: string): number {
  if (isVisualShowcaseEffectKey(effectKey)) {
    return getVisualShowcaseNumberParam(effectKey, paramKey)
  }
  if (isDissolveEffectKey(effectKey)) {
    return getDissolveNumberParam(effectKey, paramKey)
  }
  return 0
}

function setVisualDissolveNumberParam(effectKey: string, field: RevealNumberField, event: Event): void {
  if (isVisualShowcaseEffectKey(effectKey)) {
    setVisualShowcaseNumberParam(effectKey, field, event)
    return
  }
  if (isDissolveEffectKey(effectKey)) {
    setDissolveNumberParam(effectKey, field, event)
  }
}

function getObbRevealNumberParam(paramKey: string): number {
  const params = selectedRevealAdaptive.value ? obbRevealParams.value : obbRevealManualParams.value
  const value = params[paramKey as keyof typeof params]
  return typeof value === 'number' ? value : 0
}

function setObbRevealNumberParam(field: RevealNumberField, event: Event): void {
  const input = event.target as HTMLInputElement
  const rawValue = Number(input.value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  const params = selectedRevealAdaptive.value ? obbRevealParams.value : obbRevealManualParams.value
  params[field.key as keyof typeof params] = Math.min(field.max, Math.max(field.min, rawValue)) as never
  refreshActiveObbEffect()
}

function getObbRevealColorParam(paramKey: string): ColorTuple {
  const params = selectedRevealAdaptive.value ? obbRevealParams.value : obbRevealManualParams.value
  const value = params[paramKey as keyof typeof params]
  return Array.isArray(value) && value.length === 3 ? value as ColorTuple : [0, 0, 0]
}

function getObbRevealColorControl(paramKey: string): RevealColorControl {
  const controls = selectedRevealAdaptive.value ?
    obbRevealColorControls.value :
    obbRevealManualColorControls.value
  if (!controls[paramKey]) {
    controls[paramKey] = createColorControl(getObbRevealColorParam(paramKey))
  }
  return controls[paramKey]
}

function getObbRevealColorHex(paramKey: string): string {
  return getObbRevealColorControl(paramKey).hex
}

function getObbRevealColorIntensity(paramKey: string): number {
  return getObbRevealColorControl(paramKey).intensity
}

function syncObbRevealColorParam(paramKey: string): void {
  const control = getObbRevealColorControl(paramKey)
  const base = parseColorHex(control.hex)
  if (!base) {
    return
  }
  const params = selectedRevealAdaptive.value ? obbRevealParams.value : obbRevealManualParams.value
  params[paramKey as keyof typeof params] = applyColorIntensity(base, control.intensity) as never
}

function getObbRevealNumberFields(): RevealNumberField[] {
  return selectedRevealAdaptive.value ? OBB_REVEAL_NUMBER_FIELDS : OBB_REVEAL_MANUAL_NUMBER_FIELDS
}

function getObbRevealColorFields(): RevealColorField[] {
  return OBB_REVEAL_COLOR_FIELDS
}

function setRevealAdaptiveMode(adaptive: boolean): void {
  selectedRevealAdaptive.value = adaptive
  if (isSelectedObbEffect.value) {
    refreshActiveObbEffect()
    return
  }
  const panel = selectedRevealPanel.value
  if (panel) {
    refreshActiveRevealEffect(panel.effect.key)
  }
}

function setObbRevealColorHex(paramKey: string, event: Event): void {
  const input = event.target as HTMLInputElement
  if (!parseColorHex(input.value)) {
    return
  }

  getObbRevealColorControl(paramKey).hex = input.value
  syncObbRevealColorParam(paramKey)
  refreshActiveObbEffect()
}

function setObbRevealColorIntensity(paramKey: string, event: Event): void {
  const input = event.target as HTMLInputElement
  const rawValue = Number(input.value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  getObbRevealColorControl(paramKey).intensity = Math.min(COLOR_INTENSITY_MAX, Math.max(0, rawValue))
  syncObbRevealColorParam(paramKey)
  refreshActiveObbEffect()
}

function selectEffectOption(option: ShaderEffectOption): void {
  clearEffectTemplateMessage()
  selectedEffectKey.value = option.key
  previewShaderEffectOption(option)
}

function selectEffectByKey(event: Event): void {
  const key = (event.target as HTMLSelectElement).value
  const option = findEffectOption(key)
  if (option) {
    selectEffectOption(option)
  }
}

function setBoxDirection(key: BoxSweepDirectionKey): void {
  boxDirectionKey.value = key
  refreshActiveBoxEffect()
}

function setBoxTintColor(color: string): void {
  boxTintColor.value = color
  refreshActiveBoxEffect()
}

function setBoxEdgeColor(color: string): void {
  boxEdgeColor.value = color
  refreshActiveBoxEffect()
}

function normalizeBoxSize(): void {
  const size = Number(boxSize.value)
  boxSize.value = Number.isFinite(size) ? Math.min(1000, Math.max(0.5, size)) : DEFAULT_BOX_SIZE
  refreshActiveBoxEffect()
}

function normalizeBoxDuration(): void {
  const duration = Number(boxDurationSeconds.value)
  boxDurationSeconds.value = Number.isFinite(duration) ? Math.min(12, Math.max(0.5, duration)) : DEFAULT_BOX_DURATION
  refreshActiveBoxEffect()
}

function normalizeBoxCropEdgeScaleFactor(): void {
  const value = Number(boxCropEdgeScaleFactor.value)
  boxCropEdgeScaleFactor.value = Number.isFinite(value) ?
    Math.min(BOX_CROP_EDGE_SCALE_FACTOR_MAX, Math.max(BOX_CROP_EDGE_SCALE_FACTOR_MIN, value)) :
    DEFAULT_BOX_CROP_EDGE_SCALE_FACTOR
  if (activeEffectKey.value === 'box-crop') {
    refreshActiveBoxEffect()
  }
}

function refreshActiveBoxEffect(): void {
  if (!selectedEffectKey.value.startsWith('box-')) {
    return
  }

  previewSelectedShaderEffect()
}

function refreshActiveRevealEffect(effectKey: string): void {
  if (selectedEffectKey.value !== effectKey) {
    return
  }

  previewSelectedShaderEffect()
}

function refreshActiveShowcaseEffect(effectKey: string): void {
  if (selectedEffectKey.value !== effectKey) {
    return
  }

  previewSelectedShaderEffect()
}

function refreshActiveObbEffect(): void {
  if (selectedEffectKey.value === ADAPTIVE_OBB_EFFECT.key) {
    previewShaderEffectOption(ADAPTIVE_OBB_EFFECT)
  }
}

function previewSelectedShaderEffect(): void {
  previewShaderEffectOption(selectedEffectOption.value)
}

function previewShaderEffectOption(option: ShaderEffectOption): void {
  if (stats.value.totalModels <= 0) {
    loadError.value = '请先加载 3DGS 模型'
    return
  }
  applyShaderEffectOption(option)
}

function findEffectOption(key: string): ShaderEffectOption | null {
  if (key === ADAPTIVE_OBB_EFFECT.key) {
    return ADAPTIVE_OBB_EFFECT
  }

  const showcaseRevealOption = showcaseRevealEffects.find((effect) => effect.key === key)
  if (showcaseRevealOption) {
    return showcaseRevealOption
  }

  const particleOption = particleEffectGroup.effects.find((effect) => effect.key === key)
  if (particleOption) {
    return particleOption
  }

  for (const group of visualDissolveGroups) {
    const option = group.effects.find((effect) => effect.key === key)
    if (option) {
      return option
    }
  }

  for (const group of EFFECT_GROUPS) {
    const option = group.effects.find((effect) => effect.key === key)
    if (option) {
      return option
    }
  }

  return null
}

function createCurrentEffectTemplate(option: ShaderEffectOption): GSplatEffectTemplate {
  const base = {
    kind: 'share3d.gsplat-effect-template' as const,
    version: 1 as const,
    id: `runtime-${option.key}`,
    name: option.label,
    description: option.detail,
  }

  switch (option.key) {
    case 'box-reveal':
    case 'box-hide':
    case 'box-tint':
    case 'box-untint':
      return {
        ...base,
        effectKey: option.key,
        params: {
          size: normalizedBoxSize.value,
          direction: boxDirectionKey.value,
          duration: normalizedBoxDuration.value,
          tintColor: boxTintColor.value,
          edgeColor: boxEdgeColor.value,
        },
      }
    case 'box-crop':
      return {
        ...base,
        effectKey: 'box-crop',
        params: {
          size: normalizedBoxSize.value,
          edgeScaleFactor: boxCropEdgeScaleFactor.value,
        },
      }
    case 'reveal-radial':
      if (!selectedRevealAdaptive.value) {
        return {
          ...base,
          effectKey: 'reveal-radial',
          adaptive: false,
          params: {
            speed: radialRevealManualParams.value.speed,
            acceleration: radialRevealManualParams.value.acceleration,
            delay: radialRevealManualParams.value.delay,
            dotTint: toEffectTemplateColorControl(getRevealColorControl('radial', 'dotTint')),
            waveTint: toEffectTemplateColorControl(getRevealColorControl('radial', 'waveTint')),
            oscillationIntensity: radialRevealManualParams.value.oscillationIntensity,
            bandWidth: radialRevealManualParams.value.bandWidth,
          },
        }
      }
      return {
        ...base,
        effectKey: 'reveal-radial',
        adaptive: true,
        params: {
          duration: radialRevealParams.value.duration,
          dotTint: toEffectTemplateColorControl(getRevealColorControl('radial', 'dotTint')),
          waveTint: toEffectTemplateColorControl(getRevealColorControl('radial', 'waveTint')),
          intensity: radialRevealParams.value.intensity,
        },
      }
    case 'reveal-rain':
      if (!selectedRevealAdaptive.value) {
        return {
          ...base,
          effectKey: 'reveal-rain',
          adaptive: false,
          params: {
            distance: rainRevealManualParams.value.distance,
            speed: rainRevealManualParams.value.speed,
            acceleration: rainRevealManualParams.value.acceleration,
            flightTime: rainRevealManualParams.value.flightTime,
            rainSize: rainRevealManualParams.value.rainSize,
            rotation: rainRevealManualParams.value.rotation,
            fallTint: toEffectTemplateColorControl(getRevealColorControl('rain', 'fallTint')),
            fallTintIntensity: rainRevealManualParams.value.fallTintIntensity,
            hitTint: toEffectTemplateColorControl(getRevealColorControl('rain', 'hitTint')),
            hitDuration: rainRevealManualParams.value.hitDuration,
          },
        }
      }
      return {
        ...base,
        effectKey: 'reveal-rain',
        adaptive: true,
        params: {
          duration: rainRevealParams.value.duration,
          fallTint: toEffectTemplateColorControl(getRevealColorControl('rain', 'fallTint')),
          fallTintIntensity: rainRevealParams.value.fallTintIntensity,
          hitTint: toEffectTemplateColorControl(getRevealColorControl('rain', 'hitTint')),
          intensity: rainRevealParams.value.intensity,
        },
      }
    case 'reveal-grid':
      if (!selectedRevealAdaptive.value) {
        return {
          ...base,
          effectKey: 'reveal-grid',
          adaptive: false,
          params: {
            blockSize: gridRevealManualParams.value.blockSize,
            delay: gridRevealManualParams.value.delay,
            duration: gridRevealManualParams.value.duration,
            dotSize: gridRevealManualParams.value.dotSize,
            moveTint: toEffectTemplateColorControl(getRevealColorControl('grid', 'moveTint')),
            moveTintIntensity: gridRevealManualParams.value.moveTintIntensity,
            landTint: toEffectTemplateColorControl(getRevealColorControl('grid', 'landTint')),
            landDuration: gridRevealManualParams.value.landDuration,
          },
        }
      }
      return {
        ...base,
        effectKey: 'reveal-grid',
        adaptive: true,
        params: {
          duration: gridRevealParams.value.duration,
          moveTint: toEffectTemplateColorControl(getRevealColorControl('grid', 'moveTint')),
          moveTintIntensity: gridRevealParams.value.moveTintIntensity,
          landTint: toEffectTemplateColorControl(getRevealColorControl('grid', 'landTint')),
          intensity: gridRevealParams.value.intensity,
        },
      }
    case 'obb-double-wave':
      if (!selectedRevealAdaptive.value) {
        return {
          ...base,
          effectKey: 'obb-double-wave',
          adaptive: false,
          params: {
            speed: obbRevealManualParams.value.speed,
            acceleration: obbRevealManualParams.value.acceleration,
            delay: obbRevealManualParams.value.delay,
            dotTint: toEffectTemplateColorControl(getObbRevealColorControl('dotTint')),
            waveTint: toEffectTemplateColorControl(getObbRevealColorControl('waveTint')),
            oscillationIntensity: obbRevealManualParams.value.oscillationIntensity,
            bandWidth: obbRevealManualParams.value.bandWidth,
          },
        }
      }
      return {
        ...base,
        effectKey: 'obb-double-wave',
        adaptive: true,
        params: {
          duration: obbRevealParams.value.duration,
          dotTint: toEffectTemplateColorControl(getObbRevealColorControl('dotTint')),
          waveTint: toEffectTemplateColorControl(getObbRevealColorControl('waveTint')),
          intensity: obbRevealParams.value.intensity,
        },
      }
    default:
      if (isVisualShowcaseEffectKey(option.key)) {
        const params = visualShowcaseParams.value[option.key]
        return {
          ...base,
          effectKey: option.key,
          params: {
            intensity: params.intensity,
            timeScale: params.timeScale,
            ...(option.key === 'visual-electronic' ? {
              swayAmplitude: params.swayAmplitude ?? 0.2,
              swaySpeed: params.swaySpeed ?? 2,
              flickerColor: params.flickerColor === undefined ? { color: '#0080b3', intensity: 1 } : {
                color: params.flickerColor.hex,
                intensity: params.flickerColor.intensity,
              },
            } : {}),
          },
        }
      }
      if (isShowcaseRevealEffectKey(option.key)) {
        const params = showcaseRevealParams.value[option.key]
        return {
          ...base,
          effectKey: option.key,
          params: {
            timeScale: params.timeScale,
              motionScale: params.motionScale,
              duration: params.duration,
          },
        }
      }
      if (isParticleEffectKey(option.key)) {
        const params = particleParams.value[option.key]
        return {
          ...base,
          effectKey: option.key,
          params: cloneParticleScatterParams(params),
        }
      }
      if (isDissolveEffectKey(option.key)) {
        const params = dissolveParams.value[option.key]
        return {
          ...base,
          effectKey: option.key,
          params: {
            speed: params.speed,
            motionScale: params.motionScale,
          },
        }
      }
      throw new Error(`未知特效：${option.key}`)
  }
}

function createEffectTemplateRuntimeContext(): GSplatEffectTemplateRuntimeContext {
  const cameraState = engine.value?.camera.getState()
  const handle = getFirstGSplatHandle()
  const localBounds = getFirstGSplatLocalBounds()
  const worldBounds = getFirstGSplatWorldBounds()
  const sceneBox = getSceneBounds()
  const worldCenter = cameraState?.target.clone() ??
    (worldBounds ?? sceneBox)?.getCenter(new THREE.Vector3()) ??
    new THREE.Vector3()
  const cameraPosition = cameraState?.position.clone() ?? new THREE.Vector3(0, 0, 0)
  const cameraTarget = cameraState?.target.clone() ?? worldCenter.clone()
  const cameraUp = cameraState?.up.clone() ?? new THREE.Vector3(0, 0, 1)

  if (handle?.object3D && localBounds) {
    handle.object3D.updateWorldMatrix(true, false)
    const radiusBounds = worldBounds ?? sceneBox ?? localBounds

    return {
      effectSpace: 'effect-local',
      center: toVectorTuple(worldCenter),
      effectBounds: toEffectTemplateBoxLike(worldBounds),
      sceneBounds: toEffectTemplateBoxLike(radiusBounds),
      sceneRadius: getSceneRadius(worldCenter, radiusBounds),
      defaultSceneRadius: DEFAULT_SCENE_RADIUS,
      minRevealRadius: MIN_REVEAL_RADIUS,
      camera: {
        position: toVectorTuple(cameraPosition),
        target: toVectorTuple(cameraTarget),
        up: toVectorTuple(cameraUp),
      },
    }
  }

  const radiusBounds = worldBounds ?? sceneBox

  return {
    effectSpace: 'effect-local',
    center: toVectorTuple(worldCenter),
    effectBounds: toEffectTemplateBoxLike(worldBounds),
    sceneBounds: toEffectTemplateBoxLike(sceneBox),
    sceneRadius: getSceneRadius(worldCenter, radiusBounds),
    defaultSceneRadius: DEFAULT_SCENE_RADIUS,
    minRevealRadius: MIN_REVEAL_RADIUS,
    camera: {
      position: toVectorTuple(cameraPosition),
      target: toVectorTuple(cameraTarget),
      up: toVectorTuple(cameraUp),
    },
  }
}

function getSceneBounds(): THREE.Box3 | null {
  const box = engine.value?.context.sceneBounds.getWorldBoundingBox({
    purpose: 'fit',
    includeInvisible: true,
  })

  if (!box || box.isEmpty()) {
    return null
  }

  return box.clone()
}

function getFirstGSplatWorldBounds(): THREE.Box3 | null {
  const handle = getFirstGSplatHandle()
  if (!handle?.boundingBox) {
    return null
  }

  const box = handle.boundingBox.clone()
  if (box.isEmpty()) {
    return null
  }

  const object = handle.object3D
  if (object) {
    object.updateWorldMatrix(true, false)
    box.applyMatrix4(object.matrixWorld)
  }

  return box.isEmpty() ? null : box
}

function getFirstGSplatLocalBounds(): THREE.Box3 | null {
  const handle = getFirstGSplatHandle()
  if (!handle?.boundingBox) {
    return null
  }

  const box = handle.boundingBox.clone()
  return box.isEmpty() ? null : box
}

function toEffectTemplateBoxLike(box: THREE.Box3 | null): GSplatEffectTemplateBox3Like | null {
  if (!box || box.isEmpty()) {
    return null
  }

  return {
    min: toVectorTuple(box.min),
    max: toVectorTuple(box.max),
  }
}

function getSceneRadius(center: THREE.Vector3, box: THREE.Box3 | null): number {
  if (!box) {
    return DEFAULT_SCENE_RADIUS
  }

  const sphere = box.getBoundingSphere(new THREE.Sphere())
  const radius = center.distanceTo(sphere.center) + sphere.radius
  return Number.isFinite(radius) && radius > 0 ? radius : DEFAULT_SCENE_RADIUS
}

function toVectorTuple(vector: THREE.Vector3): Vector3Tuple {
  return [vector.x, vector.y, vector.z]
}

function applyShaderEffectOption(option: ShaderEffectOption): boolean {
  const handle = getFirstGSplatHandle()
  if (!handle) {
    loadError.value = '请先加载 3DGS 模型'
    return false
  }

  let effect: GSplatShaderEffectLike
  try {
    effect = createGSplatEffectFromTemplate(
      createCurrentEffectTemplate(option),
      createEffectTemplateRuntimeContext(),
    ).effect
  } catch (error) {
    loadError.value = `特效创建失败：${getErrorMessage(error)}`
    return false
  }

  clearShaderEffect(false)
  if (!handle.setShaderEffect(effect)) {
    effect.destroy()
    loadError.value = '当前模型不支持着色器特效'
    return false
  }

  loadError.value = ''
  setActiveShaderEffect(effect, option)
  return true
}

function clearShaderEffect(syncState = true): void {
  getFirstGSplatHandle()?.clearShaderEffect()
  activeShaderEffect?.destroy()
  setActiveShaderEffect(null, null)
  if (syncState) {
    loadError.value = ''
  }
}

function cloneVector3Tuple(value: readonly [number, number, number]): Vector3Tuple {
  return [value[0], value[1], value[2]]
}

function toPresetColorControlInput(control: RevealColorControl): {
  hex: string
  intensity: number
} {
  return {
    hex: control.hex,
    intensity: control.intensity,
  }
}

function toRevealColorControl(control: GS3DEffectAnimationPresetColorControl): RevealColorControl {
  return {
    hex: control.color,
    intensity: control.intensity,
  }
}

function toRuntimeColorTuple(control: GS3DEffectAnimationPresetColorControl): ColorTuple {
  return presetColorControlToColorTuple(control) as ColorTuple
}

function toRevealColorControlInput(
  control: GS3DEffectAnimationPresetColorControlInput,
): RevealColorControl {
  return {
    hex: control.hex,
    intensity: control.intensity,
  }
}

function toRuntimeColorTupleInput(control: GS3DEffectAnimationPresetColorControlInput): ColorTuple {
  const base = parseColorHex(control.hex)
  return base ? applyColorIntensity(base, control.intensity) : [1, 1, 1]
}

function toEffectTemplateColorControl(
  control: RevealColorControl,
): GSplatEffectTemplateColorControl {
  return {
    color: control.hex,
    intensity: control.intensity,
  }
}

function createCurrentPresetState(): GS3DEffectAnimationPresetState {
  return {
    camera: {
      interpolation: interpolationMode.value,
      easing: effectiveEasingMode.value,
      loopMode: loopMode.value,
      currentFov: currentCameraFov.value,
      keyframes: cameraKeyframes.value.map((frame) => ({
        time: frame.time,
        position: cloneVector3Tuple(frame.position),
        target: cloneVector3Tuple(frame.target),
        fov: frame.fov,
      })),
    },
    effect: {
      selectedEffectKey: selectedEffectKey.value,
      adaptive: selectedRevealAdaptive.value,
      box: {
        size: boxSize.value,
        direction: boxDirectionKey.value,
        duration: boxDurationSeconds.value,
        tintColor: boxTintColor.value,
        edgeColor: boxEdgeColor.value,
      },
      reveal: {
        radial: {
          duration: radialRevealParams.value.duration,
          dotTint: toPresetColorControlInput(getRevealColorControl('radial', 'dotTint')),
          waveTint: toPresetColorControlInput(getRevealColorControl('radial', 'waveTint')),
          intensity: radialRevealParams.value.intensity,
        },
        rain: {
          duration: rainRevealParams.value.duration,
          fallTint: toPresetColorControlInput(getRevealColorControl('rain', 'fallTint')),
          fallTintIntensity: rainRevealParams.value.fallTintIntensity,
          hitTint: toPresetColorControlInput(getRevealColorControl('rain', 'hitTint')),
          intensity: rainRevealParams.value.intensity,
        },
        grid: {
          duration: gridRevealParams.value.duration,
          moveTint: toPresetColorControlInput(getRevealColorControl('grid', 'moveTint')),
          moveTintIntensity: gridRevealParams.value.moveTintIntensity,
          landTint: toPresetColorControlInput(getRevealColorControl('grid', 'landTint')),
          intensity: gridRevealParams.value.intensity,
        },
        obb: {
          duration: obbRevealParams.value.duration,
          dotTint: toPresetColorControlInput(getObbRevealColorControl('dotTint')),
          waveTint: toPresetColorControlInput(getObbRevealColorControl('waveTint')),
          intensity: obbRevealParams.value.intensity,
        },
      },
      revealManual: {
        radial: {
          speed: radialRevealManualParams.value.speed,
          acceleration: radialRevealManualParams.value.acceleration,
          delay: radialRevealManualParams.value.delay,
          dotTint: toPresetColorControlInput(revealManualColorControls.value.radial.dotTint),
          waveTint: toPresetColorControlInput(revealManualColorControls.value.radial.waveTint),
          oscillationIntensity: radialRevealManualParams.value.oscillationIntensity,
          bandWidth: radialRevealManualParams.value.bandWidth,
        },
        rain: {
          distance: rainRevealManualParams.value.distance,
          speed: rainRevealManualParams.value.speed,
          acceleration: rainRevealManualParams.value.acceleration,
          flightTime: rainRevealManualParams.value.flightTime,
          rainSize: rainRevealManualParams.value.rainSize,
          rotation: rainRevealManualParams.value.rotation,
          fallTint: toPresetColorControlInput(revealManualColorControls.value.rain.fallTint),
          fallTintIntensity: rainRevealManualParams.value.fallTintIntensity,
          hitTint: toPresetColorControlInput(revealManualColorControls.value.rain.hitTint),
          hitDuration: rainRevealManualParams.value.hitDuration,
        },
        grid: {
          blockSize: gridRevealManualParams.value.blockSize,
          delay: gridRevealManualParams.value.delay,
          duration: gridRevealManualParams.value.duration,
          dotSize: gridRevealManualParams.value.dotSize,
          moveTint: toPresetColorControlInput(revealManualColorControls.value.grid.moveTint),
          moveTintIntensity: gridRevealManualParams.value.moveTintIntensity,
          landTint: toPresetColorControlInput(revealManualColorControls.value.grid.landTint),
          landDuration: gridRevealManualParams.value.landDuration,
        },
        obb: {
          speed: obbRevealManualParams.value.speed,
          acceleration: obbRevealManualParams.value.acceleration,
          delay: obbRevealManualParams.value.delay,
          dotTint: toPresetColorControlInput(obbRevealManualColorControls.value.dotTint),
          waveTint: toPresetColorControlInput(obbRevealManualColorControls.value.waveTint),
          oscillationIntensity: obbRevealManualParams.value.oscillationIntensity,
          bandWidth: obbRevealManualParams.value.bandWidth,
        },
      },
      visual: {
        'visual-electronic': { ...visualShowcaseParams.value['visual-electronic'] },
        'visual-deep-meditation': { ...visualShowcaseParams.value['visual-deep-meditation'] },
        'visual-waves': { ...visualShowcaseParams.value['visual-waves'] },
        'visual-flare': { ...visualShowcaseParams.value['visual-flare'] },
        'visual-disintegrate': { ...visualShowcaseParams.value['visual-disintegrate'] },
      },
      showcaseReveal: {
        'reveal-magic': { ...showcaseRevealParams.value['reveal-magic'] },
        'reveal-spread': { ...showcaseRevealParams.value['reveal-spread'] },
        'reveal-unroll': { ...showcaseRevealParams.value['reveal-unroll'] },
        'reveal-twister': { ...showcaseRevealParams.value['reveal-twister'] },
      },
      particle: {
        'particle-scatter-reassemble': cloneParticleScatterParams(
          particleParams.value['particle-scatter-reassemble'],
        ),
      },
      dissolve: {
        'dissolve-drift': { ...dissolveParams.value['dissolve-drift'] },
      },
    },
  }
}

function createVideoExportDownloadName(): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `gs3d-effect-animation-${stamp}.mp4`
}

function normalizeVideoExportSettings(): void {
  videoExportSettings.value = normalizeGs3dVideoExportSettings(videoExportSettings.value)
}

function updateVideoExportDurationFromTimeline(): void {
  videoExportSettings.value = normalizeGs3dVideoExportSettings({
    ...videoExportSettings.value,
    durationSeconds: cameraExportTimelineDuration.value || DEFAULT_VIDEO_EXPORT_SETTINGS.durationSeconds,
  })
}

function createVideoExportInitialCamera() {
  const plan = cameraMotionPlan.value
  if (plan) {
    return {
      position: cloneVector3Tuple(plan.initialCamera.position),
      target: cloneVector3Tuple(plan.initialCamera.target),
      up: cloneVector3Tuple(plan.initialCamera.up),
      fov: plan.initialCamera.fov,
      roll: plan.initialCamera.roll,
    }
  }

  const firstFrame = cameraKeyframes.value[0]
  const state = getCurrentCameraState()
  if (firstFrame) {
    return {
      position: cloneVector3Tuple(firstFrame.position),
      target: cloneVector3Tuple(firstFrame.target),
      up: state?.up?.toArray?.() ?? [0, 0, 1],
      fov: firstFrame.fov,
      roll: state?.roll ?? 0,
    }
  }

  if (!state) {
    throw new Error('导出前需要可用相机状态。')
  }

  return {
    position: toVectorTuple(state.position),
    target: toVectorTuple(state.target),
    up: toVectorTuple(state.up),
    fov: state.fov,
    roll: state.roll ?? 0,
  }
}

function createCurrentVideoExportCameraMotionTemplate(durationSeconds: number): SemanticCameraMotionTemplate {
  const easing = effectiveEasingMode.value === 'none' ? undefined : effectiveEasingMode.value
  return {
    kind: 'share3d.camera-motion-semantic',
    version: 1,
    id: 'gs3d-effect-animation-video-export-camera',
    name: 'GS3D effect animation export camera',
    description: 'Current example camera motion normalized for browser video export.',
    semantic: 'orbit',
    interpolation: interpolationMode.value,
    ...(easing ? { easing } : {}),
    params: {
      direction: 'right',
      angle: 180,
      keyframeCount: Math.max(2, Math.min(24, cameraKeyframes.value.length || 3)),
    },
  }
}

function createCurrentVideoExportClipTemplate(): Gs3dClipTemplateDetails {
  const plan = cameraMotionPlan.value
  return createGs3dEffectAnimationClipTemplateDetails({
    cameraMotionTemplate: createCurrentVideoExportCameraMotionTemplate(
      videoExportSettings.value.durationSeconds,
    ),
    ...(plan ? { cameraMotionPlan: plan } : { cameraClip: createCameraClip() }),
    effectTemplate: createCurrentEffectTemplate(selectedEffectOption.value),
    durationSeconds: videoExportSettings.value.durationSeconds,
    initialCamera: createVideoExportInitialCamera(),
    effectContext: createEffectTemplateRuntimeContext(),
  })
}

function createVideoExportEngineAdapter(): VideoRenderEngineLike {
  const currentEngine = engine.value
  if (!currentEngine) {
    throw new Error('导出前需要先初始化引擎。')
  }

  return {
    stop: () => currentEngine.loop.stop(),
    renderManualFrame: (request: ManualFrameRequest) => {
      prepareSceneBackgroundTexture(request.width, request.height)
      currentEngine.loop.renderManualFrame(request)
    },
    renderer:
      (currentEngine.context.getRendererIfAvailable() as unknown as VideoRenderEngineLike['renderer']) ??
      undefined,
    preserveDrawingBuffer: true,
    getGraphicsInfo: () => currentEngine.getGraphicsInfo(),
    createRegionCaptureSession: () => currentEngine.createRegionCaptureSession(),
    getGsplatRenderableDiagnostics: () => createVideoExportGsplatDiagnostics('pending'),
    waitForAssets: () => waitForVideoExportStableFrame(),
    flushGsplatOrderUpload: () => undefined,
    dispose: () => {
      currentEngine.loop.syncViewport()
      prepareSceneBackgroundTexture()
    },
  }
}

function createVideoExportGsplatDiagnostics(
  stage: GsplatRenderableDiagnostics['stage'],
): GsplatRenderableDiagnostics {
  return {
    assetLoaded: stats.value.totalModels > 0,
    gpuPrepared: stats.value.totalModels > 0,
    sortGeneration: undefined,
    pendingOrderUpload: false,
    orderUploadFlushed: true,
    visibleCount: stats.value.totalModels,
    consecutiveNonEmptyFrames: stats.value.totalModels > 0 ? 2 : 0,
    stage,
  }
}

async function waitForVideoExportStableFrame(): Promise<void> {
  await new Promise<void>((resolve) => window.setTimeout(resolve, VIDEO_EXPORT_MIN_STABLE_DELAY_MS))
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
}

function createVideoExportClipTemplateApplier() {
  const service = getAnimationService()
  if (!service) {
    throw new Error('导出前需要 AnimationService 可用。')
  }

  return createGs3dClipTemplateApplier({
    animationService: service,
    cameraPlayerId: VIDEO_EXPORT_CAMERA_PLAYER_ID,
    gsplat: {
      setShaderEffect(effect) {
        const handle = getFirstGSplatHandle()
        return effect === null ? (handle?.clearShaderEffect(), true) : (handle?.setShaderEffect(effect) ?? false)
      },
      clearShaderEffect() {
        getFirstGSplatHandle()?.clearShaderEffect()
        return true
      },
    },
    restoreInitialCamera: (camera) => {
      applyCameraPatch({
        position: tupleLikeToVector3(camera.position),
        target: tupleLikeToVector3(camera.target),
        up: tupleLikeToVector3(camera.up),
        fov: camera.fov,
        roll: camera.roll ?? 0,
      })
    },
  })
}

function tupleLikeToVector3(value: readonly [number, number, number] | { x: number; y: number; z: number }): THREE.Vector3 {
  if (Array.isArray(value)) {
    return new THREE.Vector3(value[0], value[1], value[2])
  }
  const vector = value as { x: number; y: number; z: number }
  return new THREE.Vector3(vector.x, vector.y, vector.z)
}

function captureVideoExportEditorState() {
  const cameraState = getCurrentCameraState()
  return {
    cameraState: cameraState ? {
      position: cameraState.position.clone(),
      target: cameraState.target.clone(),
      up: cameraState.up.clone(),
      fov: cameraState.fov,
      zoom: cameraState.zoom,
      mode: cameraState.mode,
      roll: cameraState.roll,
    } : null,
    playerState: playerState.value,
    playerTime: player.value?.time ?? 0,
    activeEffectKey: activeEffectKey.value,
  }
}

async function captureVideoExportViewportSnapshot(canvas: HTMLCanvasElement): Promise<void> {
  releaseVideoExportViewportSnapshot()
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/png')
  })
  if (blob) {
    videoExportViewportSnapshotUrl.value = URL.createObjectURL(blob)
    return
  }
  videoExportViewportSnapshotUrl.value = canvas.toDataURL('image/png')
}

function releaseVideoExportViewportSnapshot(): void {
  if (!videoExportViewportSnapshotUrl.value) {
    return
  }
  if (videoExportViewportSnapshotUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(videoExportViewportSnapshotUrl.value)
  }
  videoExportViewportSnapshotUrl.value = ''
}

function restoreVideoExportViewport(): void {
  const currentEngine = engine.value
  if (!currentEngine) {
    return
  }
  currentEngine.loop.syncViewport()
  currentEngine.loop.renderOnce()
  currentEngine.loop.start()
}

function restoreVideoExportEditorState(snapshot: ReturnType<typeof captureVideoExportEditorState>): void {
  getAnimationService()?.stop(VIDEO_EXPORT_CAMERA_PLAYER_ID)
  clearShaderEffect(false)

  if (snapshot.cameraState) {
    applyCameraPatch(snapshot.cameraState)
  }
  if (snapshot.activeEffectKey) {
    const option = findEffectOption(snapshot.activeEffectKey)
    if (option) {
      applyShaderEffectOption(option)
    }
  }
  if (snapshot.playerState === 'playing' || snapshot.playerState === 'paused') {
    playCameraAnimation(snapshot.playerTime, snapshot.playerState === 'playing')
  } else {
    playerState.value = snapshot.playerState
  }
}

function handleVideoExportProgress(progress: Gs3dVideoExportProgress): void {
  videoExportStage.value = progress.stage
  videoExportCurrentFrame.value = progress.frameNumber
  videoExportTotalFrames.value = progress.totalFrames
  videoExportProgress.value = progress.progress
  videoExportMessage.value = progress.message
}

async function startVideoExport(): Promise<void> {
  if (isVideoExporting.value) {
    return
  }
  if (!engine.value || stats.value.totalModels === 0) {
    videoExportMessage.value = '请先加载一个 3DGS 模型。'
    return
  }

  normalizeVideoExportSettings()
  const exportFileName = createVideoExportDownloadName()
  let exportFileHandle: FileSystemFileHandle | undefined
  if (videoExportSettings.value.durationSeconds > GS3D_VIDEO_STREAMING_THRESHOLD_SECONDS) {
    try {
      exportFileHandle = await pickGs3dVideoExportFileHandle(exportFileName)
    } catch (error) {
      videoExportMessage.value = `未选择保存位置：${getErrorMessage(error)}`
      return
    }
    if (!exportFileHandle) {
      videoExportMessage.value =
        '超长视频流式导出需要 File System Access API 或 OPFS；请使用 Chrome/Edge，并通过 localhost 或 HTTPS 打开示例。'
      return
    }
  }
  revokeGs3dVideoExportResult(videoExportResult.value)
  videoExportResult.value = null
  const abortController = new AbortController()
  const snapshot = captureVideoExportEditorState()
  videoExportAbortController.value = abortController
  videoExportStage.value = 'runtime'
  videoExportProgress.value = 0
  videoExportCurrentFrame.value = 0
  videoExportTotalFrames.value = Math.max(
    1,
    Math.ceil(videoExportSettings.value.durationSeconds * videoExportSettings.value.fps),
  )

  try {
    stopCameraAnimation()
    const canvas = engine.value.context.graphics.canvas
    await captureVideoExportViewportSnapshot(canvas)
    const result = await exportGs3dEffectAnimationVideo({
      container: containerRef.value ?? canvas.parentElement ?? document.body,
      settings: videoExportSettings.value,
      clipTemplate: createCurrentVideoExportClipTemplate(),
      runtimeConfig: {
        graphics: { backend: graphicsCard.requestedBackend.value },
        engineFactory: () => createVideoExportEngineAdapter(),
        timelineAdapter: createVideoExportClipTemplateApplier(),
        stabilityGuard: {
          waitForGsplatRenderable: async () => {
            await waitForVideoExportStableFrame()
            return createVideoExportGsplatDiagnostics('ready')
          },
        },
      },
      fileName: exportFileName,
      ...(exportFileHandle ? { fileHandle: exportFileHandle } : {}),
      signal: abortController.signal,
      onProgress: handleVideoExportProgress,
    })
    videoExportResult.value = result
    if (result.exportMode === 'stream') {
      videoExportMessage.value = result.url
        ? `流式导出完成：${result.fileName}。文件为 fragmented MP4，可下载后 remux 为标准 MP4。`
        : `流式导出完成：${result.fileName}。文件为 fragmented MP4，可用 ffmpeg remux 为标准 MP4。`
    } else {
      triggerVideoExportDownload(result)
    }
  } catch (error) {
    if (abortController.signal.aborted) {
      videoExportStage.value = 'cancelled'
      videoExportMessage.value = '导出已取消。'
    } else {
      videoExportStage.value = 'error'
      videoExportMessage.value = `导出失败：${getErrorMessage(error)}`
    }
  } finally {
    restoreVideoExportEditorState(snapshot)
    restoreVideoExportViewport()
    videoExportAbortController.value = null
    window.setTimeout(() => {
      releaseVideoExportViewportSnapshot()
    }, 160)
  }
}

function cancelVideoExport(): void {
  videoExportAbortController.value?.abort()
}

function triggerVideoExportDownload(result: Gs3dMemoryVideoExportResult): void {
  // object URL 由 gs3dVideoExport 管理，这里只负责触发下载。
  downloadUrl(result.url, result.fileName)
}

function createPresetDownloadName(): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `gs3d-effect-animation-${stamp}.json`
}

function createEffectTemplateDownloadName(option: ShaderEffectOption): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `gs3d-effect-template-${option.key}-${stamp}.json`
}

function createCameraMotionTemplateDownloadName(semantic: CameraMotionSemanticKey): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `gs3d-camera-motion-template-${semantic}-${stamp}.json`
}

function exportPresetJson(): void {
  try {
    const preset = createGS3DEffectAnimationPreset(createCurrentPresetState())
    const content = stringifyGS3DEffectAnimationPreset(preset)
    downloadBlob(
      new Blob([content], { type: 'application/json;charset=utf-8' }),
      createPresetDownloadName()
    )

    presetIoMessage.value = '已导出当前镜头动画与特效参数。'
  } catch (error) {
    presetIoMessage.value = `导出失败：${getErrorMessage(error)}`
  }
}

function exportCurrentEffectTemplateJson(): void {
  try {
    const option = activeOrSelectedEffectOption.value
    const template = createCurrentEffectTemplate(option)
    const content = `${JSON.stringify(template, null, 2)}\n`
    downloadBlob(
      new Blob([content], { type: 'application/json;charset=utf-8' }),
      createEffectTemplateDownloadName(option)
    )

    effectTemplateMessage.value = activeEffectKey.value ?
      `已导出当前运行特效「${option.label}」模板。` :
      `已导出当前选中特效「${option.label}」模板。`
  } catch (error) {
    effectTemplateMessage.value = `特效模板导出失败：${getErrorMessage(error)}`
  }
}

function exportCurrentCameraMotionTemplateJson(): void {
  try {
    const template = createCurrentCameraMotionTemplate()
    const content = `${JSON.stringify(template, null, 2)}\n`
    downloadBlob(
      new Blob([content], { type: 'application/json;charset=utf-8' }),
      createCameraMotionTemplateDownloadName(selectedCameraMotionSemantic.value)
    )

    cameraMotionTemplateMessage.value = `已导出「${template.name}」运镜模板。`
  } catch (error) {
    cameraMotionTemplateMessage.value = `运镜模板导出失败：${getErrorMessage(error)}`
  }
}

function openEffectTemplateImportPicker(): void {
  const input = effectTemplateInputRef.value
  if (!input) {
    return
  }

  input.value = ''
  input.click()
}

function openCameraMotionTemplateImportPicker(): void {
  const input = cameraMotionTemplateInputRef.value
  if (!input) {
    return
  }

  input.value = ''
  input.click()
}

async function handleCameraMotionTemplateFileSelected(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) {
    return
  }

  try {
    const template = parseSemanticCameraMotionTemplate(JSON.parse(await file.text()))
    importedCameraMotionTemplate.value = template
    selectedCameraMotionSemantic.value = template.semantic
    syncCameraMotionParamState(template)
    syncCameraMotionMetaState(template)
    syncCameraMotionTimingState(template)
    clearCameraMotionTemplateMessage()
    applyCameraMotionTemplate({
      source: template,
      template,
      key: template.id,
      label: template.name,
      detail: template.description ?? template.semantic,
      error: null,
    })
    cameraMotionTemplateMessage.value = `已导入并生成「${template.name}」运镜模板。`
  } catch (error) {
    cameraMotionTemplateMessage.value = `运镜模板导入失败：${getErrorMessage(error)}`
  } finally {
    input.value = ''
  }
}

async function handleEffectTemplateFileSelected(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) {
    return
  }

  try {
    const template = parseGSplatEffectTemplate(parseGSplatEffectTemplateImportJson(await file.text()))
    stopCameraAnimation()
    clearShaderEffect(false)
    writeEffectTemplateState(template)
    loadError.value = ''
    effectTemplateMessage.value = `已导入「${template.name}」特效模板，播放前参数已恢复。`
  } catch (error) {
    effectTemplateMessage.value = `特效模板导入失败：${getErrorMessage(error)}`
  } finally {
    input.value = ''
  }
}

function openPresetImportPicker(): void {
  const input = presetInputRef.value
  if (!input) {
    return
  }

  input.value = ''
  input.click()
}

async function handlePresetFileSelected(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) {
    return
  }

  try {
    const source = await file.text()
    parseAndApplyGS3DEffectAnimationPresetImport(source, {
      stopPlayback: stopCameraAnimation,
      clearActiveEffect: () => clearShaderEffect(false),
      writeState: writeImportedPresetState,
      applyFirstKeyframe: (frame) => applyCameraPose(frame, false),
      applyCurrentFov: applyImportedCurrentFov,
    })
    presetIoMessage.value = `已导入 ${file.name}，播放前参数已恢复。`
  } catch (error) {
    presetIoMessage.value = `导入失败：${getErrorMessage(error)}。请确认选择的是本示例导出的 JSON。`
  } finally {
    input.value = ''
  }
}

function writeImportedPresetState(preset: GS3DEffectAnimationPresetV1): void {
  const { camera, effect } = preset

  clearCameraMotionTemplateMessage()
  clearEffectTemplateMessage()
  clearGeneratedCameraMotionPlan()
  cameraSegmentEasing.value = null
  interpolationMode.value = camera.interpolation
  easingMode.value = camera.easing
  loopMode.value = camera.loopMode
  currentCameraFov.value = camera.currentFov
  cameraKeyframes.value = camera.keyframes.map((frame) =>
    createKeyframe({
      time: frame.time,
      position: cloneVector3Tuple(frame.position),
      target: cloneVector3Tuple(frame.target),
      fov: frame.fov,
    }),
  )
  selectedKeyframeId.value = cameraKeyframes.value[0]?.id ?? null

  selectedEffectKey.value = effect.selectedEffectKey
  selectedRevealAdaptive.value = true

  switch (effect.selectedEffectKey) {
    case 'box-reveal':
    case 'box-hide':
    case 'box-tint':
    case 'box-untint':
    case 'box-crop':
      boxSize.value = effect.params.size
      boxDirectionKey.value = effect.params.direction
      boxDurationSeconds.value = effect.params.duration
      boxTintColor.value = effect.params.tintColor
      boxEdgeColor.value = effect.params.edgeColor
      boxCropEdgeScaleFactor.value = DEFAULT_BOX_CROP_EDGE_SCALE_FACTOR
      break
    case 'reveal-radial':
      selectedRevealAdaptive.value = effect.adaptive ?? true
      if (effect.adaptive === false) {
        radialRevealManualParams.value = {
          speed: effect.params.speed,
          acceleration: effect.params.acceleration,
          delay: effect.params.delay,
          dotTint: toRuntimeColorTuple(effect.params.dotTint),
          waveTint: toRuntimeColorTuple(effect.params.waveTint),
          oscillationIntensity: effect.params.oscillationIntensity,
          bandWidth: effect.params.bandWidth,
        }
        revealManualColorControls.value.radial = {
          dotTint: toRevealColorControl(effect.params.dotTint),
          waveTint: toRevealColorControl(effect.params.waveTint),
        }
      } else {
        radialRevealParams.value = {
          duration: effect.params.duration,
          dotTint: toRuntimeColorTuple(effect.params.dotTint),
          waveTint: toRuntimeColorTuple(effect.params.waveTint),
          intensity: effect.params.intensity,
        }
        revealColorControls.value.radial = {
          dotTint: toRevealColorControl(effect.params.dotTint),
          waveTint: toRevealColorControl(effect.params.waveTint),
        }
      }
      break
    case 'reveal-rain':
      selectedRevealAdaptive.value = effect.adaptive ?? true
      if (effect.adaptive === false) {
        rainRevealManualParams.value = {
          distance: effect.params.distance,
          speed: effect.params.speed,
          acceleration: effect.params.acceleration,
          flightTime: effect.params.flightTime,
          rainSize: effect.params.rainSize,
          rotation: effect.params.rotation,
          fallTint: toRuntimeColorTuple(effect.params.fallTint),
          fallTintIntensity: effect.params.fallTintIntensity,
          hitTint: toRuntimeColorTuple(effect.params.hitTint),
          hitDuration: effect.params.hitDuration,
        }
        revealManualColorControls.value.rain = {
          fallTint: toRevealColorControl(effect.params.fallTint),
          hitTint: toRevealColorControl(effect.params.hitTint),
        }
      } else {
        rainRevealParams.value = {
          duration: effect.params.duration,
          fallTint: toRuntimeColorTuple(effect.params.fallTint),
          fallTintIntensity: effect.params.fallTintIntensity,
          hitTint: toRuntimeColorTuple(effect.params.hitTint),
          intensity: effect.params.intensity,
        }
        revealColorControls.value.rain = {
          fallTint: toRevealColorControl(effect.params.fallTint),
          hitTint: toRevealColorControl(effect.params.hitTint),
        }
      }
      break
    case 'reveal-grid':
      selectedRevealAdaptive.value = effect.adaptive ?? true
      if (effect.adaptive === false) {
        gridRevealManualParams.value = {
          blockSize: effect.params.blockSize,
          delay: effect.params.delay,
          duration: effect.params.duration,
          dotSize: effect.params.dotSize,
          moveTint: toRuntimeColorTuple(effect.params.moveTint),
          moveTintIntensity: effect.params.moveTintIntensity,
          landTint: toRuntimeColorTuple(effect.params.landTint),
          landDuration: effect.params.landDuration,
        }
        revealManualColorControls.value.grid = {
          moveTint: toRevealColorControl(effect.params.moveTint),
          landTint: toRevealColorControl(effect.params.landTint),
        }
      } else {
        gridRevealParams.value = {
          duration: effect.params.duration,
          moveTint: toRuntimeColorTuple(effect.params.moveTint),
          moveTintIntensity: effect.params.moveTintIntensity,
          landTint: toRuntimeColorTuple(effect.params.landTint),
          intensity: effect.params.intensity,
        }
        revealColorControls.value.grid = {
          moveTint: toRevealColorControl(effect.params.moveTint),
          landTint: toRevealColorControl(effect.params.landTint),
        }
      }
      break
    case 'obb-double-wave':
      selectedRevealAdaptive.value = effect.adaptive ?? true
      if (effect.adaptive === false) {
        obbRevealManualParams.value = {
          speed: effect.params.speed,
          acceleration: effect.params.acceleration,
          delay: effect.params.delay,
          dotTint: toRuntimeColorTuple(effect.params.dotTint),
          waveTint: toRuntimeColorTuple(effect.params.waveTint),
          oscillationIntensity: effect.params.oscillationIntensity,
          bandWidth: effect.params.bandWidth,
        }
        obbRevealManualColorControls.value = {
          dotTint: toRevealColorControl(effect.params.dotTint),
          waveTint: toRevealColorControl(effect.params.waveTint),
        }
      } else {
        obbRevealParams.value = {
          duration: effect.params.duration,
          dotTint: toRuntimeColorTuple(effect.params.dotTint),
          waveTint: toRuntimeColorTuple(effect.params.waveTint),
          intensity: effect.params.intensity,
        }
        obbRevealColorControls.value = {
          dotTint: toRevealColorControl(effect.params.dotTint),
          waveTint: toRevealColorControl(effect.params.waveTint),
        }
      }
      break
    case 'visual-electronic':
      visualShowcaseParams.value['visual-electronic'] = {
        intensity: effect.params.intensity,
        timeScale: effect.params.timeScale,
        swayAmplitude: effect.params.swayAmplitude,
        swaySpeed: effect.params.swaySpeed,
        flickerColor: toRevealColorControl(
          effect.params.flickerColor ?? { color: '#0080b3', intensity: 1 },
        ),
      }
      break
    case 'visual-deep-meditation':
    case 'visual-waves':
    case 'visual-flare':
    case 'visual-disintegrate':
      visualShowcaseParams.value[effect.selectedEffectKey] = {
        intensity: effect.params.intensity,
        timeScale: effect.params.timeScale,
      }
      break
    case 'reveal-magic':
    case 'reveal-spread':
    case 'reveal-unroll':
    case 'reveal-twister':
      showcaseRevealParams.value[effect.selectedEffectKey] = {
        timeScale: effect.params.timeScale,
          motionScale: effect.params.motionScale,
          duration: effect.params.duration,
      }
      break
    case 'particle-scatter-reassemble':
      particleParams.value[effect.selectedEffectKey] = cloneParticleScatterParams(effect.params)
      break
    case 'dissolve-drift':
      dissolveParams.value[effect.selectedEffectKey] = {
        speed: effect.params.speed,
        motionScale: effect.params.motionScale,
      }
      break
  }
}

function writeEffectTemplateState(template: GSplatEffectTemplate): void {
  const effectState = applyGSplatEffectTemplateToPresetEffectState(
    createCurrentPresetState().effect,
    template,
  )
  writeEffectPanelState(effectState)
  const cropEdgeScaleFactor = getGSplatEffectTemplateBoxCropEdgeScaleFactor(template)
  if (cropEdgeScaleFactor !== null) {
    boxCropEdgeScaleFactor.value = cropEdgeScaleFactor
  }
}

function writeEffectPanelState(effect: GSplatEffectTemplatePresetEffectState): void {
  selectedEffectKey.value = effect.selectedEffectKey
  selectedRevealAdaptive.value = effect.adaptive ?? true
  boxSize.value = effect.box.size
  boxDirectionKey.value = effect.box.direction as BoxSweepDirectionKey
  boxDurationSeconds.value = effect.box.duration
  boxTintColor.value = effect.box.tintColor
  boxEdgeColor.value = effect.box.edgeColor

  radialRevealParams.value = {
    duration: effect.reveal.radial.duration,
    dotTint: toRuntimeColorTupleInput(effect.reveal.radial.dotTint),
    waveTint: toRuntimeColorTupleInput(effect.reveal.radial.waveTint),
    intensity: effect.reveal.radial.intensity,
  }
  revealColorControls.value.radial = {
    dotTint: toRevealColorControlInput(effect.reveal.radial.dotTint),
    waveTint: toRevealColorControlInput(effect.reveal.radial.waveTint),
  }

  rainRevealParams.value = {
    duration: effect.reveal.rain.duration,
    fallTint: toRuntimeColorTupleInput(effect.reveal.rain.fallTint),
    fallTintIntensity: effect.reveal.rain.fallTintIntensity,
    hitTint: toRuntimeColorTupleInput(effect.reveal.rain.hitTint),
    intensity: effect.reveal.rain.intensity,
  }
  revealColorControls.value.rain = {
    fallTint: toRevealColorControlInput(effect.reveal.rain.fallTint),
    hitTint: toRevealColorControlInput(effect.reveal.rain.hitTint),
  }

  gridRevealParams.value = {
    duration: effect.reveal.grid.duration,
    moveTint: toRuntimeColorTupleInput(effect.reveal.grid.moveTint),
    moveTintIntensity: effect.reveal.grid.moveTintIntensity,
    landTint: toRuntimeColorTupleInput(effect.reveal.grid.landTint),
    intensity: effect.reveal.grid.intensity,
  }
  revealColorControls.value.grid = {
    moveTint: toRevealColorControlInput(effect.reveal.grid.moveTint),
    landTint: toRevealColorControlInput(effect.reveal.grid.landTint),
  }

  obbRevealParams.value = {
    duration: effect.reveal.obb.duration,
    dotTint: toRuntimeColorTupleInput(effect.reveal.obb.dotTint),
    waveTint: toRuntimeColorTupleInput(effect.reveal.obb.waveTint),
    intensity: effect.reveal.obb.intensity,
  }
  obbRevealColorControls.value = {
    dotTint: toRevealColorControlInput(effect.reveal.obb.dotTint),
    waveTint: toRevealColorControlInput(effect.reveal.obb.waveTint),
  }

  radialRevealManualParams.value = {
    speed: effect.revealManual.radial.speed,
    acceleration: effect.revealManual.radial.acceleration,
    delay: effect.revealManual.radial.delay,
    dotTint: toRuntimeColorTupleInput(effect.revealManual.radial.dotTint),
    waveTint: toRuntimeColorTupleInput(effect.revealManual.radial.waveTint),
    oscillationIntensity: effect.revealManual.radial.oscillationIntensity,
    bandWidth: effect.revealManual.radial.bandWidth,
  }
  revealManualColorControls.value.radial = {
    dotTint: toRevealColorControlInput(effect.revealManual.radial.dotTint),
    waveTint: toRevealColorControlInput(effect.revealManual.radial.waveTint),
  }

  rainRevealManualParams.value = {
    distance: effect.revealManual.rain.distance,
    speed: effect.revealManual.rain.speed,
    acceleration: effect.revealManual.rain.acceleration,
    flightTime: effect.revealManual.rain.flightTime,
    rainSize: effect.revealManual.rain.rainSize,
    rotation: effect.revealManual.rain.rotation,
    fallTint: toRuntimeColorTupleInput(effect.revealManual.rain.fallTint),
    fallTintIntensity: effect.revealManual.rain.fallTintIntensity,
    hitTint: toRuntimeColorTupleInput(effect.revealManual.rain.hitTint),
    hitDuration: effect.revealManual.rain.hitDuration,
  }
  revealManualColorControls.value.rain = {
    fallTint: toRevealColorControlInput(effect.revealManual.rain.fallTint),
    hitTint: toRevealColorControlInput(effect.revealManual.rain.hitTint),
  }

  gridRevealManualParams.value = {
    blockSize: effect.revealManual.grid.blockSize,
    delay: effect.revealManual.grid.delay,
    duration: effect.revealManual.grid.duration,
    dotSize: effect.revealManual.grid.dotSize,
    moveTint: toRuntimeColorTupleInput(effect.revealManual.grid.moveTint),
    moveTintIntensity: effect.revealManual.grid.moveTintIntensity,
    landTint: toRuntimeColorTupleInput(effect.revealManual.grid.landTint),
    landDuration: effect.revealManual.grid.landDuration,
  }
  revealManualColorControls.value.grid = {
    moveTint: toRevealColorControlInput(effect.revealManual.grid.moveTint),
    landTint: toRevealColorControlInput(effect.revealManual.grid.landTint),
  }

  obbRevealManualParams.value = {
    speed: effect.revealManual.obb.speed,
    acceleration: effect.revealManual.obb.acceleration,
    delay: effect.revealManual.obb.delay,
    dotTint: toRuntimeColorTupleInput(effect.revealManual.obb.dotTint),
    waveTint: toRuntimeColorTupleInput(effect.revealManual.obb.waveTint),
    oscillationIntensity: effect.revealManual.obb.oscillationIntensity,
    bandWidth: effect.revealManual.obb.bandWidth,
  }
  obbRevealManualColorControls.value = {
    dotTint: toRevealColorControlInput(effect.revealManual.obb.dotTint),
    waveTint: toRevealColorControlInput(effect.revealManual.obb.waveTint),
  }

  visualShowcaseParams.value = {
    'visual-electronic': { ...effect.visual['visual-electronic'] },
    'visual-deep-meditation': { ...effect.visual['visual-deep-meditation'] },
    'visual-waves': { ...effect.visual['visual-waves'] },
    'visual-flare': { ...effect.visual['visual-flare'] },
    'visual-disintegrate': { ...effect.visual['visual-disintegrate'] },
  }
  showcaseRevealParams.value = {
    'reveal-magic': { ...effect.showcaseReveal['reveal-magic'] },
    'reveal-spread': { ...effect.showcaseReveal['reveal-spread'] },
    'reveal-unroll': { ...effect.showcaseReveal['reveal-unroll'] },
    'reveal-twister': { ...effect.showcaseReveal['reveal-twister'] },
  }
  particleParams.value = {
    'particle-scatter-reassemble': cloneParticleScatterParams(
      effect.particle['particle-scatter-reassemble'],
    ),
  }
  dissolveParams.value = {
    'dissolve-drift': { ...effect.dissolve['dissolve-drift'] },
  }
}

function applyImportedCurrentFov(fov: number): void {
  currentCameraFov.value = fov
  applyCameraPatch({ fov })
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function playCombinedDemo(): void {
  if (!hasCameraPlaybackSource.value) {
    loadError.value = '请先应用一个运镜或至少保留 1 个镜头关键帧'
    return
  }

  stopCameraAnimation()
  clearShaderEffect(false)
  const plan = cameraMotionPlan.value
  if (plan) {
    applyCameraMotionPlanInitialPose(plan)
  } else {
    const firstFrame = cameraKeyframes.value[0]
    if (firstFrame) {
      applyCameraPose(firstFrame, false)
    }
  }
  if (!applyShaderEffectOption(selectedEffectOption.value)) {
    return
  }
  const clearEffectWithCamera = !isParticleEffectKey(selectedEffectKey.value)
  if (plan) {
    playCameraMotionSequencePlan(plan, {
      clearShaderEffectOnComplete: clearEffectWithCamera,
    })
    return
  }
  playCameraAnimation(0, {
    autoplay: true,
    lockControls: true,
    resetToFirstFrameOnComplete: true,
    clearShaderEffectOnComplete: clearEffectWithCamera,
  })
}

function stopCombinedDemo(): void {
  stopCameraAnimation()
  clearShaderEffect()
}

async function handleLoadModel(): Promise<void> {
  loadError.value = ''
  stopCombinedDemo()
  const shouldUseBoundingBoxPivot = isSinglePlyModelInput(modelInput.value)
  firstGsplatPivot.value = null

  try {
    if (stats.value.totalModels > 0) {
      clearSceneModels()
    }
    const handle = await loadModel()
    if (shouldUseBoundingBoxPivot && handle?.object3D) {
      const pivot = createBoundingBoxPivot(handle.object3D)
      if (pivot) {
        centerObjectPivotAtOrigin(handle.object3D, pivot)
        firstGsplatPivot.value = pivot
      }
    }
    refreshFirstGsplatObject()
    prepareCameraAnimationView()
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : '未知错误'
  }
}

function clearSceneModels(): void {
  stopCombinedDemo()
  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')
  firstGsplatPivot.value = null

  for (const handle of plugin?.getAll() ?? []) {
    plugin?.unmount(handle.id)
    plugin?.dispose(handle.id)
  }

  removeAllModels(false)
  refreshFirstGsplatObject()
  loadError.value = ''
}

function applySceneBackground(): void {
  const currentEngine = engine.value
  if (!currentEngine) {
    return
  }

  if (sceneBackgroundMode.value === 'image' && sceneBackgroundSourceTexture) {
    prepareSceneBackgroundTexture()
    return
  }

  currentEngine.setBackground(sceneBackgroundColor.value)
}

function getSceneBackgroundViewportSize(): { width: number; height: number } {
  const renderer = engine.value?.context.renderer
  if (renderer) {
    const size = renderer.getSize(new THREE.Vector2())
    if (size.x > 0 && size.y > 0) {
      return { width: size.x, height: size.y }
    }
  }

  return {
    width: Math.max(1, containerRef.value?.clientWidth ?? 1),
    height: Math.max(1, containerRef.value?.clientHeight ?? 1),
  }
}

function getSceneBackgroundTextureImageSize(texture: THREE.Texture): {
  width: number
  height: number
} {
  const image = texture.image as
    | {
        naturalWidth?: number
        naturalHeight?: number
        videoWidth?: number
        videoHeight?: number
        width?: number
        height?: number
      }
    | undefined
  return {
    width: Math.max(1, image?.naturalWidth ?? image?.videoWidth ?? image?.width ?? 1),
    height: Math.max(1, image?.naturalHeight ?? image?.videoHeight ?? image?.height ?? 1),
  }
}

function createContainedSceneBackgroundTexture(
  sourceTexture: THREE.Texture,
  viewportWidth: number,
  viewportHeight: number,
): THREE.CanvasTexture {
  const sourceSize = getSceneBackgroundTextureImageSize(sourceTexture)
  const maxTextureSize = engine.value?.context.renderer.capabilities.maxTextureSize ?? 4096
  const resolutionScale = Math.min(
    1,
    maxTextureSize / Math.max(1, viewportWidth),
    maxTextureSize / Math.max(1, viewportHeight),
  )
  const width = Math.max(1, Math.round(viewportWidth * resolutionScale))
  const height = Math.max(1, Math.round(viewportHeight * resolutionScale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('浏览器无法创建背景图片画布。')
  }

  context.fillStyle = sceneBackgroundImageFillColor.value
  context.fillRect(0, 0, width, height)
  const scale = Math.min(width / sourceSize.width, height / sourceSize.height)
  const drawWidth = sourceSize.width * scale
  const drawHeight = sourceSize.height * scale
  context.drawImage(
    sourceTexture.image as CanvasImageSource,
    (width - drawWidth) / 2,
    (height - drawHeight) / 2,
    drawWidth,
    drawHeight,
  )

  const texture = new THREE.CanvasTexture(canvas)
  texture.name = 'gs3d-effect-animation-contained-background'
  texture.colorSpace = THREE.SRGBColorSpace
  texture.wrapS = THREE.ClampToEdgeWrapping
  texture.wrapT = THREE.ClampToEdgeWrapping
  return texture
}

function prepareSceneBackgroundTexture(
  requestedWidth?: number,
  requestedHeight?: number,
): void {
  const currentEngine = engine.value
  const sourceTexture = sceneBackgroundSourceTexture
  if (!currentEngine || !sourceTexture || sceneBackgroundMode.value !== 'image') {
    return
  }

  const currentViewport = getSceneBackgroundViewportSize()
  const viewportWidth = Math.max(1, requestedWidth ?? currentViewport.width)
  const viewportHeight = Math.max(1, requestedHeight ?? currentViewport.height)
  let textureToDispose: THREE.CanvasTexture | null = null

  if (sceneBackgroundImageFit.value === 'contain') {
    const nextDerivedKey = [
      sourceTexture.uuid,
      Math.round(viewportWidth),
      Math.round(viewportHeight),
      sceneBackgroundImageFillColor.value,
    ].join(':')
    if (!sceneBackgroundDerivedTexture || nextDerivedKey !== sceneBackgroundDerivedKey) {
      const nextTexture = createContainedSceneBackgroundTexture(
        sourceTexture,
        viewportWidth,
        viewportHeight,
      )
      textureToDispose = sceneBackgroundDerivedTexture
      sceneBackgroundDerivedTexture = nextTexture
      sceneBackgroundDerivedKey = nextDerivedKey
    }
    sceneBackgroundTexture = sceneBackgroundDerivedTexture
  } else {
    textureToDispose = sceneBackgroundDerivedTexture
    sceneBackgroundDerivedTexture = null
    sceneBackgroundDerivedKey = ''
    sceneBackgroundTexture = sourceTexture
  }

  const activeTexture = sceneBackgroundTexture
  if (!activeTexture) {
    textureToDispose?.dispose()
    return
  }

  const imageSize = getSceneBackgroundTextureImageSize(activeTexture)
  const transform = computeSceneBackgroundUvTransform({
    fit: sceneBackgroundImageFit.value,
    imageWidth: imageSize.width,
    imageHeight: imageSize.height,
    viewportWidth,
    viewportHeight,
    zoom: sceneBackgroundImageZoom.value,
    offsetX: sceneBackgroundImageOffsetX.value,
    offsetY: sceneBackgroundImageOffsetY.value,
    rotationDegrees: sceneBackgroundImageRotation.value,
  })
  const nextTransformKey = [
    activeTexture.uuid,
    viewportWidth,
    viewportHeight,
    sceneBackgroundImageFit.value,
    sceneBackgroundImageZoom.value,
    sceneBackgroundImageOffsetX.value,
    sceneBackgroundImageOffsetY.value,
    sceneBackgroundImageRotation.value,
  ].join(':')
  const isAlreadyApplied =
    nextTransformKey === sceneBackgroundTransformKey &&
    currentEngine.context.sceneGraph.mainScene.background === activeTexture

  if (!isAlreadyApplied) {
    activeTexture.center.set(0.5, 0.5)
    activeTexture.repeat.set(transform.repeatX, transform.repeatY)
    activeTexture.offset.set(transform.offsetX, transform.offsetY)
    activeTexture.rotation = transform.rotationRadians
    activeTexture.matrixAutoUpdate = true
    activeTexture.updateMatrix()
    sceneBackgroundTransformKey = nextTransformKey
    currentEngine.setBackground(activeTexture)
  }
  textureToDispose?.dispose()
}

function setSceneBackgroundMode(mode: SceneBackgroundMode): void {
  sceneBackgroundMode.value = mode
  sceneBackgroundMessage.value = ''
  if (mode === 'image' && !sceneBackgroundSourceTexture) {
    sceneBackgroundMessage.value = '请选择一张图片作为场景背景。'
    return
  }
  applySceneBackground()
}

function setSceneBackgroundColor(color: string): void {
  sceneBackgroundColor.value = color
  sceneBackgroundMessage.value = ''
  if (sceneBackgroundMode.value === 'color') {
    applySceneBackground()
  }
}

function setSceneBackgroundImageFit(fit: SceneBackgroundImageFit): void {
  sceneBackgroundImageFit.value = fit
  sceneBackgroundTransformKey = ''
  prepareSceneBackgroundTexture()
}

function setSceneBackgroundImageZoom(zoom: number): void {
  sceneBackgroundImageZoom.value = clampNumber(zoom, 1, 3)
  sceneBackgroundTransformKey = ''
  prepareSceneBackgroundTexture()
}

function setSceneBackgroundImageOffsetX(offset: number): void {
  sceneBackgroundImageOffsetX.value = clampNumber(offset, -1, 1)
  sceneBackgroundTransformKey = ''
  prepareSceneBackgroundTexture()
}

function setSceneBackgroundImageOffsetY(offset: number): void {
  sceneBackgroundImageOffsetY.value = clampNumber(offset, -1, 1)
  sceneBackgroundTransformKey = ''
  prepareSceneBackgroundTexture()
}

function setSceneBackgroundImageRotation(rotation: number): void {
  sceneBackgroundImageRotation.value = clampNumber(rotation, -180, 180)
  sceneBackgroundTransformKey = ''
  prepareSceneBackgroundTexture()
}

function setSceneBackgroundImageFillColor(color: string): void {
  sceneBackgroundImageFillColor.value = color
  sceneBackgroundDerivedKey = ''
  sceneBackgroundTransformKey = ''
  prepareSceneBackgroundTexture()
}

function resetSceneBackgroundImageTransform(shouldRefresh = true): void {
  sceneBackgroundImageFit.value = 'cover'
  sceneBackgroundImageZoom.value = 1
  sceneBackgroundImageOffsetX.value = 0
  sceneBackgroundImageOffsetY.value = 0
  sceneBackgroundImageRotation.value = 0
  sceneBackgroundImageFillColor.value = '#000000'
  sceneBackgroundDerivedKey = ''
  sceneBackgroundTransformKey = ''
  if (shouldRefresh) {
    prepareSceneBackgroundTexture()
  }
}

async function loadSceneBackgroundImage(file: File): Promise<void> {
  if (file.type && !file.type.startsWith('image/')) {
    sceneBackgroundMessage.value = '请选择浏览器支持的图片文件。'
    return
  }

  const loadToken = ++sceneBackgroundLoadToken
  const objectUrl = URL.createObjectURL(file)
  sceneBackgroundImageLoading.value = true
  sceneBackgroundMessage.value = '正在加载背景图片…'

  try {
    const texture = await new THREE.TextureLoader().loadAsync(objectUrl)
    if (loadToken !== sceneBackgroundLoadToken) {
      texture.dispose()
      return
    }

    texture.colorSpace = THREE.SRGBColorSpace
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.needsUpdate = true
    const previousSourceTexture = sceneBackgroundSourceTexture
    const previousDerivedTexture = sceneBackgroundDerivedTexture
    sceneBackgroundSourceTexture = texture
    sceneBackgroundTexture = texture
    sceneBackgroundDerivedTexture = null
    sceneBackgroundImageName.value = file.name
    sceneBackgroundMode.value = 'image'
    resetSceneBackgroundImageTransform(false)
    prepareSceneBackgroundTexture()
    previousDerivedTexture?.dispose()
    previousSourceTexture?.dispose()
    sceneBackgroundMessage.value = '背景图片已应用到 Three.js 场景。'
  } catch {
    if (loadToken === sceneBackgroundLoadToken) {
      sceneBackgroundMessage.value = '背景图片加载失败，请更换图片后重试。'
    }
  } finally {
    URL.revokeObjectURL(objectUrl)
    if (loadToken === sceneBackgroundLoadToken) {
      sceneBackgroundImageLoading.value = false
    }
  }
}

function removeSceneBackgroundImage(): void {
  sceneBackgroundLoadToken += 1
  sceneBackgroundImageLoading.value = false
  const previousSourceTexture = sceneBackgroundSourceTexture
  const previousDerivedTexture = sceneBackgroundDerivedTexture
  sceneBackgroundSourceTexture = null
  sceneBackgroundTexture = null
  sceneBackgroundDerivedTexture = null
  sceneBackgroundDerivedKey = ''
  sceneBackgroundTransformKey = ''
  sceneBackgroundImageName.value = ''
  sceneBackgroundMode.value = 'color'
  sceneBackgroundMessage.value = '背景图片已移除。'
  applySceneBackground()
  previousDerivedTexture?.dispose()
  previousSourceTexture?.dispose()
}

function resetSceneBackground(): void {
  sceneBackgroundLoadToken += 1
  sceneBackgroundImageLoading.value = false
  const previousSourceTexture = sceneBackgroundSourceTexture
  const previousDerivedTexture = sceneBackgroundDerivedTexture
  sceneBackgroundSourceTexture = null
  sceneBackgroundTexture = null
  sceneBackgroundDerivedTexture = null
  sceneBackgroundDerivedKey = ''
  sceneBackgroundTransformKey = ''
  sceneBackgroundImageName.value = ''
  sceneBackgroundMode.value = 'color'
  sceneBackgroundColor.value = '#000000'
  sceneBackgroundMessage.value = ''
  resetSceneBackgroundImageTransform(false)
  applySceneBackground()
  previousDerivedTexture?.dispose()
  previousSourceTexture?.dispose()
}

onMounted(async () => {
  graphicsCard.markCreating()
  try {
    await init()
  } catch (error) {
    graphicsCard.markError(toGraphicsRuntimeError(error))
    return
  }
  graphicsCard.markReady(engine.value ? engine.value.getGraphicsInfo().kind : null)
  if (containerRef.value) {
    sceneBackgroundResizeObserver = new ResizeObserver(() => {
      prepareSceneBackgroundTexture()
    })
    sceneBackgroundResizeObserver.observe(containerRef.value)
  }
  if (!modelInput.value) {
    modelInput.value = DEFAULT_GS3D_MODEL
  }
  await autoLoad()
  refreshFirstGsplatObject()
  if (stats.value.totalModels === 0) {
    await handleLoadModel()
  }
  refreshFirstGsplatObject()
  prepareCameraAnimationView()
  syncWorldAxesVisibility()
})

watch(worldAxesVisible, () => {
  syncWorldAxesVisibility()
})

watch(engine, () => {
  applySceneBackground()
})

onBeforeUnmount(() => {
  sceneBackgroundLoadToken += 1
  sceneBackgroundResizeObserver?.disconnect()
  sceneBackgroundResizeObserver = null
  sceneBackgroundDerivedTexture?.dispose()
  sceneBackgroundSourceTexture?.dispose()
  sceneBackgroundSourceTexture = null
  sceneBackgroundTexture = null
  sceneBackgroundDerivedTexture = null
  cancelVideoExport()
  revokeGs3dVideoExportResult(videoExportResult.value)
  releaseVideoExportViewportSnapshot()
  stopCameraAnimation()
  clearShaderEffect(false)
  firstGsplatPivot.value = null
  disposeWorldAxesHelper()
})
</script>

<template>
  <div class="effect-animation-page">
    <div
      ref="containerRef"
      class="render-container"
      :class="{ 'render-container-exporting': isVideoExporting }"
    ></div>

    <SceneControlPanel
      layout="sidebar"
      kicker="示例面板"
      title="特效镜头编排"
      :meta="panelMeta"
    >
      <SceneAssetLoaderCard
        v-model="modelInput"
        badge="3DGS"
        :hint="modelLoaderHint"
        :loading="isLoading"
        :can-clear="stats.totalModels > 0"
        :allow-file="true"
        :directory-entry-names="['lod-meta.json', 'meta.json']"
        :file-entry-extensions="['ply']"
        file-accept=".ply"
        file-pick-label="选择 PLY 文件"
        :preset-models="PRESET_MODELS"
        @submit-load="handleLoadModel"
        @clear-scene="clearSceneModels"
      />

      <SceneBackgroundCard
        :mode="sceneBackgroundMode"
        :color="sceneBackgroundColor"
        :image-name="sceneBackgroundImageName"
        :image-loading="sceneBackgroundImageLoading"
        :message="sceneBackgroundMessage"
        :image-fit="sceneBackgroundImageFit"
        :image-zoom="sceneBackgroundImageZoom"
        :image-offset-x="sceneBackgroundImageOffsetX"
        :image-offset-y="sceneBackgroundImageOffsetY"
        :image-rotation="sceneBackgroundImageRotation"
        :image-fill-color="sceneBackgroundImageFillColor"
        @update:mode="setSceneBackgroundMode"
        @update:color="setSceneBackgroundColor"
        @update:image-fit="setSceneBackgroundImageFit"
        @update:image-zoom="setSceneBackgroundImageZoom"
        @update:image-offset-x="setSceneBackgroundImageOffsetX"
        @update:image-offset-y="setSceneBackgroundImageOffsetY"
        @update:image-rotation="setSceneBackgroundImageRotation"
        @update:image-fill-color="setSceneBackgroundImageFillColor"
        @select-image="loadSceneBackgroundImage"
        @remove-image="removeSceneBackgroundImage"
        @reset-image-transform="resetSceneBackgroundImageTransform"
        @reset="resetSceneBackground"
      />
      <GraphicsBackendControlCard :card="graphicsCard">
        <template #extensions>
          <GSplatBackendControlCard :state="backendControlState" />
        </template>
      </GraphicsBackendControlCard>

      <SceneObjectTransformCard
        :object="firstGsplatObject"
        :pivot="firstGsplatPivot"
        v-model:world-axes-visible="worldAxesVisible"
        @changed="handleModelTransformChanged"
      />

      <SceneGroupCard
        title="联合播放"
        subtitle="从当前镜头编排起点，同时重启选中特效和相机轨道"
        :badge="animationStatus"
      >
        <div class="status-grid">
          <div v-for="item in combinedStatusItems" :key="item.label" class="status-item">
            <span class="status-label">{{ item.label }}</span>
            <strong class="status-value">{{ item.value }}</strong>
          </div>
        </div>

        <div class="button-grid button-grid-two">
          <button
            class="panel-button"
            type="button"
            :disabled="stats.totalModels === 0 || !hasCameraPlaybackSource"
            @click="playCombinedDemo"
          >
            <span class="panel-button-label">播放</span>
            <span class="panel-button-meta">{{ animationModeMeta }}</span>
          </button>
          <button
            class="panel-button panel-button-subtle"
            type="button"
            :disabled="playerState === 'idle' && activeEffectKey === null"
            @click="stopCombinedDemo"
          >
            <span class="panel-button-label">停止</span>
            <span class="panel-button-meta">停止镜头并清除特效</span>
          </button>
        </div>
      </SceneGroupCard>

      <SceneGroupCard
        title="视频导出"
        subtitle="浏览器内逐帧渲染并编码 MP4"
        :badge="videoExportStatus"
      >
        <div class="video-export-grid">
          <label class="box-field">
            <span class="box-label">宽度</span>
            <input
              v-model.number="videoExportSettings.width"
              class="duration-input"
              type="number"
              min="64"
              max="3840"
              step="16"
              :disabled="isVideoExporting"
              @change="normalizeVideoExportSettings"
            />
          </label>
          <label class="box-field">
            <span class="box-label">高度</span>
            <input
              v-model.number="videoExportSettings.height"
              class="duration-input"
              type="number"
              min="64"
              max="2160"
              step="16"
              :disabled="isVideoExporting"
              @change="normalizeVideoExportSettings"
            />
          </label>
          <label class="box-field">
            <span class="box-label">FPS</span>
            <input
              v-model.number="videoExportSettings.fps"
              class="duration-input"
              type="number"
              min="1"
              max="60"
              step="1"
              :disabled="isVideoExporting"
              @change="normalizeVideoExportSettings"
            />
          </label>
          <label class="box-field">
            <span class="box-label">时长</span>
            <input
              v-model.number="videoExportSettings.durationSeconds"
              class="duration-input"
              type="number"
              min="0.1"
              max="3600"
              step="0.1"
              :disabled="isVideoExporting"
              @change="normalizeVideoExportSettings"
            />
          </label>
        </div>

        <label class="box-field">
          <span class="box-label">质量 {{ Math.round(videoExportSettings.quality * 100) }}%</span>
          <input
            v-model.number="videoExportSettings.quality"
            class="panel-range"
            type="range"
            min="0.1"
            max="1"
            step="0.01"
            :disabled="isVideoExporting"
            @change="normalizeVideoExportSettings"
          />
        </label>

        <div class="video-export-progress">
          <div class="video-export-progress-bar">
            <span :style="{ width: `${videoExportProgressPercent}%` }"></span>
          </div>
          <span class="panel-note">{{ videoExportMessage || 'MP4 / WebCodecs' }}</span>
        </div>

        <div class="button-grid button-grid-two">
          <button
            class="panel-button"
            type="button"
            :disabled="isVideoExporting || stats.totalModels === 0"
            @click="startVideoExport"
          >
            <span class="panel-button-label">导出视频</span>
            <span class="panel-button-meta">{{ videoExportSettings.width }}x{{ videoExportSettings.height }} / {{ videoExportSettings.fps }}fps</span>
          </button>
          <button
            class="panel-button panel-button-subtle"
            type="button"
            :disabled="!isVideoExporting"
            @click="cancelVideoExport"
          >
            <span class="panel-button-label">取消</span>
            <span class="panel-button-meta">{{ videoExportProgressPercent }}%</span>
          </button>
        </div>

        <div class="button-grid button-grid-two">
          <button
            class="panel-button panel-button-subtle"
            type="button"
            :disabled="isVideoExporting"
            @click="updateVideoExportDurationFromTimeline"
          >
            <span class="panel-button-label">匹配运镜时长</span>
            <span class="panel-button-meta">{{ formatNumber(cameraExportTimelineDuration, 2) }}s</span>
          </button>
          <a
            v-if="videoExportResult?.url"
            class="panel-button video-export-download"
            :href="videoExportResult.url"
            :download="videoExportResult.fileName"
          >
            <span class="panel-button-label">下载 MP4</span>
            <span class="panel-button-meta">{{ videoExportResult.framesEncoded }} 帧</span>
          </a>
        </div>
      </SceneGroupCard>

      <SceneGroupCard
        title="运镜选择"
        subtitle="按运镜语义生成单 clip 关键帧或多 clip 组合编排"
        :badge="cameraMotionTemplateSummary"
      >
        <label class="box-field">
          <span class="box-label">当前运镜</span>
          <select
            class="effect-select"
            :value="selectedCameraMotionSemantic"
            @change="selectCameraMotionSemantic"
          >
            <option
              v-for="option in CAMERA_MOTION_SEMANTIC_OPTIONS"
              :key="option.key"
              :value="option.key"
            >
              {{ option.label }} - {{ option.detail }}
            </option>
          </select>
        </label>
        <label class="box-field">
          <span class="box-label">运镜时长</span>
          <div class="duration-row">
            <input
              v-model.number="cameraMotionTemplateDuration"
              type="range"
              min="0.5"
              max="3600"
              step="0.25"
              class="duration-slider"
              @change="normalizeCameraMotionTemplateDuration"
            />
            <input
              v-model.number="cameraMotionTemplateDuration"
              type="number"
              min="0.5"
              max="3600"
              step="0.25"
              class="duration-input"
              @change="normalizeCameraMotionTemplateDuration"
            />
          </div>
        </label>
        <div class="selected-effect-summary">
          <span>{{ selectedCameraMotionSemanticOption.detail }}</span>
          <strong v-if="isSelectedCameraMotionSequence">递归组合 / 多 clip 硬切</strong>
          <strong v-else>关键帧：{{ cameraKeyframes.length }} / {{ formatNumber(cameraClipDuration, 2) }}s</strong>
        </div>
        <div class="motion-meta-grid">
          <label class="box-field">
            <span class="box-label">模板名称</span>
            <input
              v-model.trim="cameraMotionTemplateName"
              class="duration-input"
              type="text"
              maxlength="64"
            />
          </label>
          <label class="box-field">
            <span class="box-label">模板描述</span>
            <input
              v-model.trim="cameraMotionTemplateDescription"
              class="duration-input"
              type="text"
              maxlength="160"
              placeholder="可选"
            />
          </label>
        </div>
        <div class="motion-param-grid">
          <div
            v-for="item in selectedCameraMotionTemplateMeta"
            :key="item.label"
            class="status-item"
          >
            <span class="status-label">{{ item.label }}</span>
            <strong class="status-value">{{ item.value }}</strong>
          </div>
        </div>
        <div class="motion-param-grid">
          <label v-if="!isSelectedCameraMotionSequence" class="box-field">
            <span class="box-label">插值方式</span>
            <select
              class="effect-select"
              :value="cameraMotionTemplateInterpolation"
              @change="selectCameraMotionTemplateInterpolation"
            >
              <option
                v-for="option in CAMERA_INTERPOLATION_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }} - {{ option.detail }}
              </option>
            </select>
          </label>
          <label v-if="!isSelectedCameraMotionSequence" class="box-field">
            <span class="box-label">整体缓动</span>
            <select
              v-model="cameraMotionTemplateEasing"
              class="effect-select"
              :disabled="cameraMotionTemplateInterpolation === 'step'"
              @change="normalizeCameraMotionTimingState"
            >
              <option
                v-for="option in CAMERA_MOTION_EASING_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }} - {{ option.detail }}
              </option>
            </select>
          </label>
          <label v-if="!isSelectedCameraMotionSequence" class="box-field">
            <span class="box-label">首段缓动</span>
            <select
              v-model="cameraMotionTemplateSegmentEasingFirst"
              class="effect-select"
              :disabled="cameraMotionTemplateInterpolation === 'step'"
              @change="normalizeCameraMotionTimingState"
            >
              <option
                v-for="option in CAMERA_MOTION_OPTIONAL_EASING_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }} - {{ option.detail }}
              </option>
            </select>
          </label>
          <label v-if="!isSelectedCameraMotionSequence" class="box-field">
            <span class="box-label">尾段缓动</span>
            <select
              v-model="cameraMotionTemplateSegmentEasingLast"
              class="effect-select"
              :disabled="cameraMotionTemplateInterpolation === 'step'"
              @change="normalizeCameraMotionTimingState"
            >
              <option
                v-for="option in CAMERA_MOTION_OPTIONAL_EASING_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }} - {{ option.detail }}
              </option>
            </select>
          </label>
          <label class="box-field">
            <span class="box-label">尾帧定格时长</span>
            <input
              v-model.number="cameraMotionTemplateHoldDuration"
              class="duration-input"
              type="number"
              min="0"
              max="30"
              step="0.1"
              @change="normalizeCameraMotionTimingState"
            />
          </label>
        </div>
        <div class="motion-param-grid">
          <label
            v-for="field in selectedCameraMotionParamFields"
            :key="field.key"
            class="box-field"
          >
            <span class="box-label">{{ field.label }}</span>
            <select
              v-if="field.type === 'select'"
              v-model="cameraMotionTemplateParams[field.key]"
              class="effect-select"
              @change="normalizeCameraMotionParamField(field)"
            >
              <option
                v-for="option in field.options"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <input
              v-else
              v-model.number="cameraMotionTemplateParams[field.key]"
              class="duration-input"
              type="number"
              :min="getCameraMotionParamRange(field).min"
              :max="getCameraMotionParamRange(field).max"
              :step="field.step"
              @change="normalizeCameraMotionParamField(field)"
            />
          </label>
        </div>
        <CameraMotionTemplateNodeEditor
          v-if="isSelectedCameraMotionSequence && cameraMotionSequenceTemplate"
          v-model="cameraMotionSequenceTemplate"
          root
          :leaf-templates="cameraMotionLeafTemplates"
          :semantic-options="CAMERA_MOTION_SEMANTIC_OPTIONS"
          :param-fields="CAMERA_MOTION_PARAM_FIELDS"
        />
        <div v-if="cameraMotionPlanPlayer" class="button-grid button-grid-two">
          <button
            class="panel-button panel-button-subtle"
            type="button"
            :disabled="playerState !== 'playing'"
            @click="pauseCameraMotionSequence"
          >
            <span class="panel-button-label">暂停组合运镜</span>
            <span class="panel-button-meta">保留当前片段进度</span>
          </button>
          <button
            class="panel-button panel-button-subtle"
            type="button"
            :disabled="playerState !== 'paused'"
            @click="resumeCameraMotionSequence"
          >
            <span class="panel-button-label">继续组合运镜</span>
            <span class="panel-button-meta">继续当前 clip</span>
          </button>
        </div>
        <div class="button-grid button-grid-three">
          <button
            class="panel-button"
            type="button"
            :disabled="!engine || !selectedCameraMotionTemplate"
            @click="applySelectedCameraMotionTemplate(true)"
          >
            <span class="panel-button-label">应用运镜</span>
            <span class="panel-button-meta">{{ selectedCameraMotionSemanticOption.label }}</span>
          </button>
          <button class="panel-button panel-button-subtle" type="button" @click="exportCurrentCameraMotionTemplateJson">
            <span class="panel-button-label">导出运镜模板</span>
            <span class="panel-button-meta">{{ selectedCameraMotionSemanticOption.label }}</span>
          </button>
          <button class="panel-button panel-button-subtle" type="button" @click="openCameraMotionTemplateImportPicker">
            <span class="panel-button-label">导入运镜模板</span>
            <span class="panel-button-meta">生成镜头编排</span>
          </button>
        </div>
        <input
          ref="cameraMotionTemplateInputRef"
          class="preset-file-input"
          type="file"
          accept="application/json,.json"
          @change="handleCameraMotionTemplateFileSelected"
        />
        <p v-if="cameraMotionTemplateMessage" class="panel-note preset-io-message">
          {{ cameraMotionTemplateMessage }}
        </p>
      </SceneGroupCard>

      <SceneGroupCard
        title="特效选择"
        subtitle="选择一个特效后只显示它的参数"
        :badge="selectedEffectOption.label"
      >
        <label class="box-field">
          <span class="box-label">当前特效</span>
          <select class="effect-select" :value="selectedEffectKey" @change="selectEffectByKey">
            <optgroup
              v-for="group in effectSelectGroups"
              :key="group.title"
              :label="group.title"
            >
              <option v-for="effect in group.effects" :key="effect.key" :value="effect.key">
                {{ effect.label }} - {{ effect.detail }}
              </option>
            </optgroup>
          </select>
        </label>
        <div class="selected-effect-summary">
          <span>{{ selectedEffectOption.detail }}</span>
          <strong>运行中：{{ activeEffectName }}</strong>
        </div>
        <button class="panel-button effect-select-export-button" type="button" @click="exportCurrentEffectTemplateJson">
          <span class="panel-button-label">导出特效模板</span>
          <span class="panel-button-meta">{{ activeOrSelectedEffectOption.label }}</span>
        </button>
        <button class="panel-button panel-button-subtle effect-select-export-button" type="button" @click="openEffectTemplateImportPicker">
          <span class="panel-button-label">导入特效模板</span>
          <span class="panel-button-meta">恢复特效参数</span>
        </button>
        <input
          ref="effectTemplateInputRef"
          class="preset-file-input"
          type="file"
          accept="application/json,.json"
          @change="handleEffectTemplateFileSelected"
        />
        <p v-if="effectTemplateMessage" class="panel-note preset-io-message">
          {{ effectTemplateMessage }}
        </p>
        <div v-if="selectedBoxEffect" class="effect-groups">
          <section class="box-controls">
            <div class="effect-group-header">
              <span class="effect-category">盒体参数</span>
              <span class="effect-mode">{{ boxSettingsSummary }}</span>
            </div>

            <label class="box-field">
              <span class="box-label">目标盒边长</span>
              <div class="duration-row">
                <input
                  v-model.number="boxSize"
                  type="range"
                  min="0.5"
                  max="1000"
                  step="0.5"
                  class="duration-slider"
                  @change="normalizeBoxSize"
                />
                <input
                  v-model.number="boxSize"
                  type="number"
                  min="0.5"
                  max="1000"
                  step="0.5"
                  class="duration-input"
                  @change="normalizeBoxSize"
                />
              </div>
            </label>

            <div class="box-field">
              <span class="box-label">方向</span>
              <div class="direction-actions">
                <button
                  v-for="direction in BOX_SWEEP_DIRECTIONS"
                  :key="direction.key"
                  type="button"
                  class="direction-button"
                  :class="{ active: boxDirectionKey === direction.key }"
                  @click="setBoxDirection(direction.key)"
                >
                  {{ direction.label }}
                </button>
              </div>
            </div>

            <label class="box-field">
              <span class="box-label">时间</span>
              <div class="duration-row">
                <input
                  v-model.number="boxDurationSeconds"
                  type="range"
                  min="0.5"
                  max="12"
                  step="0.25"
                  class="duration-slider"
                  @change="normalizeBoxDuration"
                />
                <input
                  v-model.number="boxDurationSeconds"
                  type="number"
                  min="0.5"
                  max="12"
                  step="0.25"
                  class="duration-input"
                  @change="normalizeBoxDuration"
                />
              </div>
            </label>

            <label class="box-field">
              <span class="box-label">裁切边缘</span>
              <div class="duration-row">
                <input
                  v-model.number="boxCropEdgeScaleFactor"
                  type="range"
                  min="0"
                  max="2"
                  step="0.01"
                  class="duration-slider"
                  @change="normalizeBoxCropEdgeScaleFactor"
                />
                <input
                  v-model.number="boxCropEdgeScaleFactor"
                  type="number"
                  min="0"
                  max="2"
                  step="0.01"
                  class="duration-input"
                  @change="normalizeBoxCropEdgeScaleFactor"
                />
              </div>
            </label>

            <div class="box-color-grid">
              <div class="box-field">
                <span class="box-label">染色</span>
                <div class="color-row">
                  <input v-model="boxTintColor" type="color" class="color-input" @change="refreshActiveBoxEffect" />
                  <button
                    v-for="preset in BOX_COLOR_PRESETS"
                    :key="`tint-${preset.value}`"
                    type="button"
                    class="color-swatch"
                    :class="{ active: boxTintColor === preset.value }"
                    :style="{ backgroundColor: preset.value }"
                    :title="preset.label"
                    @click="setBoxTintColor(preset.value)"
                  ></button>
                </div>
              </div>

              <div class="box-field">
                <span class="box-label">边缘</span>
                <div class="color-row">
                  <input v-model="boxEdgeColor" type="color" class="color-input" @change="refreshActiveBoxEffect" />
                  <button
                    v-for="preset in BOX_COLOR_PRESETS"
                    :key="`edge-${preset.value}`"
                    type="button"
                    class="color-swatch"
                    :class="{ active: boxEdgeColor === preset.value }"
                    :style="{ backgroundColor: preset.value }"
                    :title="preset.label"
                    @click="setBoxEdgeColor(preset.value)"
                  ></button>
                </div>
              </div>
            </div>
          </section>

          <section class="effect-group">
            <div class="effect-group-header">
              <span class="effect-category">{{ boxEffectGroup.title }}</span>
              <span class="effect-mode">点击选择，播放时生效</span>
            </div>

            <div class="effect-actions">
              <button
                v-for="effect in selectedBoxEffect ? [selectedBoxEffect] : []"
                :key="effect.key"
                type="button"
                class="effect-button"
                :class="{ active: selectedEffectKey === effect.key }"
                @click="selectEffectOption(effect)"
              >
                <span class="effect-name">{{ effect.label }}</span>
                <span class="effect-detail">{{ effect.detail }}</span>
              </button>
            </div>
          </section>
        </div>

        <div v-if="selectedRevealPanel || selectedShowcaseRevealEffect" class="effect-groups">
          <div v-if="selectedRevealPanel" class="choice-section-title">PlayCanvas 显现</div>
          <section
            v-for="panel in selectedRevealPanel ? [selectedRevealPanel] : []"
            :key="panel.key"
            class="reveal-param-panel"
          >
            <div class="reveal-panel-header">
              <div class="reveal-panel-title">
                <span class="effect-category">{{ panel.effect.label }}</span>
                <span class="effect-mode">{{ panel.effect.detail }}</span>
              </div>

              <button
                type="button"
                class="effect-button reveal-apply-button"
                :class="{ active: selectedEffectKey === panel.effect.key }"
                @click="selectEffectOption(panel.effect)"
              >
                选择
              </button>
            </div>

            <div class="effect-mode-toggle" role="group" aria-label="显现参数模式">
              <button
                type="button"
                class="direction-button"
                :class="{ active: selectedRevealAdaptive }"
                @click="setRevealAdaptiveMode(true)"
              >
                自适应
              </button>
              <button
                type="button"
                class="direction-button"
                :class="{ active: !selectedRevealAdaptive }"
                @click="setRevealAdaptiveMode(false)"
              >
                手动
              </button>
            </div>

            <div class="reveal-param-grid">
              <label v-for="field in getRevealNumberFields(panel.key)" :key="field.key" class="box-field">
                <span class="box-label">{{ field.label }}</span>
                <div class="duration-row">
                  <input
                    :value="getRevealNumberParam(panel.key, field.key)"
                    type="range"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-slider"
                    @input="setRevealNumberParam(panel.key, field, $event, panel.effect.key)"
                  />
                  <input
                    :value="getRevealNumberParam(panel.key, field.key)"
                    type="number"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-input"
                    @change="setRevealNumberParam(panel.key, field, $event, panel.effect.key)"
                  />
                </div>
              </label>
            </div>

            <div class="reveal-color-grid">
              <div v-for="field in getRevealColorFields(panel.key)" :key="field.key" class="box-field">
                <span class="box-label">{{ field.label }}</span>
                <div class="color-control-row">
                  <input
                    :value="getRevealColorHex(panel.key, field.key)"
                    type="color"
                    class="color-input"
                    @input="setRevealColorHex(panel.key, field.key, $event, panel.effect.key)"
                  />
                  <label class="color-intensity-row">
                    <span class="color-intensity-label">强度</span>
                    <input
                      :value="getRevealColorIntensity(panel.key, field.key)"
                      type="range"
                      min="0"
                      :max="COLOR_INTENSITY_MAX"
                      :step="COLOR_INTENSITY_STEP"
                      class="duration-slider"
                      @input="setRevealColorIntensity(panel.key, field.key, $event, panel.effect.key)"
                    />
                    <input
                      :value="getRevealColorIntensity(panel.key, field.key)"
                      type="number"
                      min="0"
                      :max="COLOR_INTENSITY_MAX"
                      :step="COLOR_INTENSITY_STEP"
                      class="duration-input"
                      @change="setRevealColorIntensity(panel.key, field.key, $event, panel.effect.key)"
                    />
                  </label>
                </div>
              </div>
            </div>
          </section>

          <div v-if="selectedShowcaseRevealEffect" class="choice-section-title">展示显现</div>
          <section
            v-for="effect in selectedShowcaseRevealEffect ? [selectedShowcaseRevealEffect] : []"
            :key="effect.key"
            class="reveal-param-panel"
          >
            <div class="reveal-panel-header">
              <div class="reveal-panel-title">
                <span class="effect-category">{{ effect.label }}</span>
                <span class="effect-mode">{{ effect.detail }}</span>
              </div>

              <button
                type="button"
                class="effect-button reveal-apply-button"
                :class="{ active: selectedEffectKey === effect.key }"
                @click="selectEffectOption(effect)"
              >
                选择
              </button>
            </div>

            <div class="reveal-param-grid">
                <label v-for="field in SHOWCASE_REVEAL_NUMBER_FIELDS" :key="field.key" class="box-field">
                <span class="box-label">{{ field.label }}</span>
                <div class="duration-row">
                  <input
                    :value="getShowcaseRevealOptionNumberParam(effect.key, field.key)"
                    type="range"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-slider"
                    @input="setShowcaseRevealOptionNumberParam(effect.key, field, $event)"
                  />
                  <input
                    :value="getShowcaseRevealOptionNumberParam(effect.key, field.key)"
                    type="number"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-input"
                    @change="setShowcaseRevealOptionNumberParam(effect.key, field, $event)"
                  />
                </div>
              </label>
            </div>
          </section>
        </div>

        <div v-if="selectedParticleEffect" class="effect-groups">
          <section class="reveal-param-panel">
            <div class="reveal-panel-header">
              <div class="reveal-panel-title">
                <span class="effect-category">{{ selectedParticleEffect.label }}</span>
                <span class="effect-mode">{{ selectedParticleEffect.detail }}</span>
              </div>
              <button
                type="button"
                class="effect-button reveal-apply-button"
                :class="{ active: selectedEffectKey === selectedParticleEffect.key }"
                @click="selectEffectOption(selectedParticleEffect)"
              >
                重新播放
              </button>
            </div>

            <p class="panel-note">
              原地缩成持续微扰的晶莹发光粒子，按所选成对方式炸裂与聚合，最后恢复原始高斯形态。
            </p>
            <details class="particle-advanced-controls" open>
              <summary>炸裂与聚合</summary>
            <div class="reveal-param-grid">
              <label class="box-field">
                <span class="box-label">motion.type（成对运动方式）</span>
                <select
                  :value="particleParams['particle-scatter-reassemble'].motion.type"
                  class="effect-select"
                  @change="setParticleMotionType('particle-scatter-reassemble', $event)"
                >
                  <option
                    v-for="option in PARTICLE_MOTION_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <span class="effect-mode">
                  {{ getParticleMotionDetail('particle-scatter-reassemble') }}
                </span>
              </label>
              <label
                v-if="particleParams['particle-scatter-reassemble'].motion.type === 'layered-print'"
                class="box-field"
              >
                <span class="box-label">direction（打印方向）</span>
                <select
                  :value="particleParams['particle-scatter-reassemble'].motion.params.direction"
                  class="effect-select"
                  @change="setParticlePrintDirection('particle-scatter-reassemble', $event)"
                >
                  <option value="bottom-up">由下至上</option>
                  <option value="top-down">由上至下</option>
                </select>
              </label>
              <label
                v-for="field in getParticleMotionNumberFields('particle-scatter-reassemble')"
                :key="`motion-${field.key}`"
                class="box-field"
              >
                <span class="box-label">{{ field.label }}</span>
                <div class="duration-row">
                  <input
                    :value="getParticleMotionNumberParam('particle-scatter-reassemble', field.key)"
                    type="range"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-slider"
                    @input="setParticleMotionNumberParam('particle-scatter-reassemble', field, $event)"
                  />
                  <input
                    :value="getParticleMotionNumberParam('particle-scatter-reassemble', field.key)"
                    type="number"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-input"
                    @change="setParticleMotionNumberParam('particle-scatter-reassemble', field, $event)"
                  />
                </div>
              </label>
            </div>
            </details>

            <details class="particle-advanced-controls">
              <summary>粒子样式</summary>
              <div class="reveal-param-grid">
              <label class="box-field">
                <span class="box-label">sizeMode（粒子尺寸模式）</span>
                <select
                  :value="particleParams['particle-scatter-reassemble'].style.sizeMode"
                  class="effect-select"
                  @change="setParticleSizeMode('particle-scatter-reassemble', $event)"
                >
                  <option
                    v-for="option in PARTICLE_SIZE_MODE_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
              <label
                v-for="field in getParticleStyleNumberFields('particle-scatter-reassemble')"
                :key="field.key"
                class="box-field"
              >
                <span class="box-label">{{ field.label }}</span>
                <div class="duration-row">
                  <input
                    :value="getParticleNumberParam('particle-scatter-reassemble', 'style', field.key)"
                    type="range"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-slider"
                    @input="setParticleNumberParam('particle-scatter-reassemble', 'style', field, $event)"
                  />
                  <input
                    :value="getParticleNumberParam('particle-scatter-reassemble', 'style', field.key)"
                    type="number"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-input"
                    @change="setParticleNumberParam('particle-scatter-reassemble', 'style', field, $event)"
                  />
                </div>
              </label>
            </div>
            </details>

            <details class="particle-advanced-controls">
              <summary>粒子扰动</summary>
              <div class="reveal-param-grid">
                <label
                  v-for="field in PARTICLE_JITTER_NUMBER_FIELDS"
                  :key="field.key"
                  class="box-field"
                >
                  <span class="box-label">{{ field.label }}</span>
                  <div class="duration-row">
                    <input
                      :value="getParticleNumberParam('particle-scatter-reassemble', 'jitter', field.key)"
                      type="range"
                      :min="field.min"
                      :max="field.max"
                      :step="field.step"
                      class="duration-slider"
                      @input="setParticleNumberParam('particle-scatter-reassemble', 'jitter', field, $event)"
                    />
                    <input
                      :value="getParticleNumberParam('particle-scatter-reassemble', 'jitter', field.key)"
                      type="number"
                      :min="field.min"
                      :max="field.max"
                      :step="field.step"
                      class="duration-input"
                      @change="setParticleNumberParam('particle-scatter-reassemble', 'jitter', field, $event)"
                    />
                  </div>
                </label>
              </div>
            </details>

            <details class="particle-advanced-controls">
              <summary>生命周期</summary>
              <div class="reveal-param-grid">
                <label
                  v-for="field in PARTICLE_TIMING_NUMBER_FIELDS"
                  :key="field.key"
                  class="box-field"
                >
                  <span class="box-label">{{ field.label }}</span>
                  <div class="duration-row">
                    <input
                      :value="getParticleNumberParam('particle-scatter-reassemble', 'timing', field.key)"
                      type="range"
                      :min="field.min"
                      :max="getParticleNumberFieldMax('particle-scatter-reassemble', field)"
                      :step="field.step"
                      class="duration-slider"
                      @input="setParticleNumberParam('particle-scatter-reassemble', 'timing', field, $event)"
                    />
                    <input
                      :value="getParticleNumberParam('particle-scatter-reassemble', 'timing', field.key)"
                      type="number"
                      :min="field.min"
                      :max="getParticleNumberFieldMax('particle-scatter-reassemble', field)"
                      :step="field.step"
                      class="duration-input"
                      @change="setParticleNumberParam('particle-scatter-reassemble', 'timing', field, $event)"
                    />
                  </div>
                </label>
              </div>
            </details>

            <details class="particle-advanced-controls">
              <summary>随机与阶段权重</summary>
              <p class="particle-phase-weight-hint">
                阶段权重为 0 时会瞬时完成该阶段；四项权重至少保留一项大于 0。
              </p>
              <div class="reveal-param-grid">
                <label
                  v-for="field in PARTICLE_ADVANCED_NUMBER_FIELDS"
                  :key="field.key"
                  class="box-field"
                >
                  <span class="box-label">{{ field.label }}</span>
                  <input
                    :value="getParticleNumberParam('particle-scatter-reassemble', 'random', field.key)"
                    type="number"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-input"
                    @change="setParticleNumberParam('particle-scatter-reassemble', 'random', field, $event)"
                  />
                </label>
                <label
                  v-for="field in PARTICLE_PHASE_WEIGHT_FIELDS"
                  :key="field.key"
                  class="box-field"
                >
                  <span class="box-label">{{ field.label }}</span>
                  <div class="duration-row">
                    <input
                      :value="getParticlePhaseWeight('particle-scatter-reassemble', field.key)"
                      type="range"
                      :min="field.min"
                      :max="field.max"
                      :step="field.step"
                      class="duration-slider"
                      @input="setParticlePhaseWeight('particle-scatter-reassemble', field, $event)"
                    />
                    <input
                      :value="getParticlePhaseWeight('particle-scatter-reassemble', field.key)"
                      type="number"
                      :min="field.min"
                      :max="field.max"
                      :step="field.step"
                      class="duration-input"
                      @change="setParticlePhaseWeight('particle-scatter-reassemble', field, $event)"
                    />
                  </div>
                </label>
              </div>
            </details>
          </section>
        </div>

        <div v-if="selectedVisualDissolveEffect" class="effect-groups">
          <section
            v-for="group in visualDissolveGroups"
            v-show="group.effects.some((effect) => effect.key === selectedVisualDissolveEffect?.key)"
            :key="group.title"
            class="effect-group"
          >
            <div class="effect-group-header">
              <span class="effect-category">{{ group.title }}</span>
              <span class="effect-mode">{{ group.mode }}</span>
            </div>

            <section
              v-for="effect in group.effects"
              v-show="effect.key === selectedVisualDissolveEffect.key"
              :key="effect.key"
              class="reveal-param-panel"
            >
              <div class="reveal-panel-header">
                <div class="reveal-panel-title">
                  <span class="effect-category">{{ effect.label }}</span>
                  <span class="effect-mode">{{ effect.detail }}</span>
                </div>

                <button
                  type="button"
                  class="effect-button reveal-apply-button"
                  :class="{ active: selectedEffectKey === effect.key }"
                  @click="selectEffectOption(effect)"
                >
                  选择
                </button>
              </div>

              <div class="reveal-param-grid">
                <label
                  v-for="field in getVisualDissolveNumberFields(effect.key)"
                  :key="field.key"
                  class="box-field"
                >
                  <span class="box-label">{{ field.label }}</span>
                  <div class="duration-row">
                    <input
                      :value="getVisualDissolveNumberParam(effect.key, field.key)"
                      type="range"
                      :min="field.min"
                      :max="field.max"
                      :step="field.step"
                      class="duration-slider"
                      @input="setVisualDissolveNumberParam(effect.key, field, $event)"
                    />
                    <input
                      :value="getVisualDissolveNumberParam(effect.key, field.key)"
                      type="number"
                      :min="field.min"
                      :max="field.max"
                      :step="field.step"
                      class="duration-input"
                      @change="setVisualDissolveNumberParam(effect.key, field, $event)"
                    />
                  </div>
                </label>
                <label v-if="effect.key === 'visual-electronic'" class="box-field">
                  <span class="box-label">flickerColor（闪烁颜色）</span>
                  <input
                    v-model="visualShowcaseParams['visual-electronic'].flickerColor!.hex"
                    type="color"
                    class="color-input"
                    @input="refreshActiveShowcaseEffect('visual-electronic')"
                  />
                </label>
              </div>
            </section>
          </section>
        </div>

        <div v-if="isSelectedObbEffect" class="effect-groups">
          <section class="reveal-param-panel">
            <div class="reveal-panel-header">
              <div class="reveal-panel-title">
                <span class="effect-category">{{ ADAPTIVE_OBB_EFFECT.label }}</span>
                <span class="effect-mode">{{ ADAPTIVE_OBB_EFFECT.detail }}</span>
              </div>

              <button
                type="button"
                class="effect-button reveal-apply-button"
                :class="{ active: selectedEffectKey === ADAPTIVE_OBB_EFFECT.key }"
                @click="selectEffectOption(ADAPTIVE_OBB_EFFECT)"
              >
                选择
              </button>
            </div>

            <div class="effect-mode-toggle" role="group" aria-label="视角推进参数模式">
              <button
                type="button"
                class="direction-button"
                :class="{ active: selectedRevealAdaptive }"
                @click="setRevealAdaptiveMode(true)"
              >
                自适应
              </button>
              <button
                type="button"
                class="direction-button"
                :class="{ active: !selectedRevealAdaptive }"
                @click="setRevealAdaptiveMode(false)"
              >
                手动
              </button>
            </div>

            <div class="reveal-param-grid">
              <label v-for="field in getObbRevealNumberFields()" :key="field.key" class="box-field">
                <span class="box-label">{{ field.label }}</span>
                <div class="duration-row">
                  <input
                    :value="getObbRevealNumberParam(field.key)"
                    type="range"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-slider"
                    @input="setObbRevealNumberParam(field, $event)"
                  />
                  <input
                    :value="getObbRevealNumberParam(field.key)"
                    type="number"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-input"
                    @change="setObbRevealNumberParam(field, $event)"
                  />
                </div>
              </label>
            </div>

            <div class="reveal-color-grid">
              <div v-for="field in getObbRevealColorFields()" :key="field.key" class="box-field">
                <span class="box-label">{{ field.label }}</span>
                <div class="color-control-row">
                  <input
                    :value="getObbRevealColorHex(field.key)"
                    type="color"
                    class="color-input"
                    @input="setObbRevealColorHex(field.key, $event)"
                  />
                  <label class="color-intensity-row">
                    <span class="color-intensity-label">强度</span>
                    <input
                      :value="getObbRevealColorIntensity(field.key)"
                      type="range"
                      min="0"
                      :max="COLOR_INTENSITY_MAX"
                      :step="COLOR_INTENSITY_STEP"
                      class="duration-slider"
                      @input="setObbRevealColorIntensity(field.key, $event)"
                    />
                    <input
                      :value="getObbRevealColorIntensity(field.key)"
                      type="number"
                      min="0"
                      :max="COLOR_INTENSITY_MAX"
                      :step="COLOR_INTENSITY_STEP"
                      class="duration-input"
                      @change="setObbRevealColorIntensity(field.key, $event)"
                    />
                  </label>
                </div>
              </div>
            </div>
          </section>
        </div>
      </SceneGroupCard>

      <SceneGroupCard
        :title="cameraTimelineCardTitle"
        :subtitle="cameraTimelineCardSubtitle"
        :badge="cameraTimelineCardBadge"
      >
        <div v-if="cameraMotionPlan" class="motion-plan-preview">
          <div class="motion-plan-summary-grid">
            <div class="status-item">
              <span class="status-label">叶子运镜</span>
              <strong class="status-value">{{ cameraMotionPlan.diagnostics.leafCount }} 个</strong>
            </div>
            <div class="status-item">
              <span class="status-label">独立片段</span>
              <strong class="status-value">{{ cameraMotionPlan.segments.length }} 个</strong>
            </div>
            <div class="status-item">
              <span class="status-label">运动时长</span>
              <strong class="status-value">
                {{ formatNumber(cameraMotionPlan.motionDuration, 2) }}s
              </strong>
            </div>
            <div class="status-item">
              <span class="status-label">计划总时长</span>
              <strong class="status-value">{{ formatNumber(cameraMotionPlan.duration, 2) }}s</strong>
            </div>
          </div>

          <p class="panel-note motion-plan-note">
            组合计划由多个独立 clip 构成，片段边界保持硬切。这里展示的是只读运行时求解结果；
            如需修改，请调整上方组合运镜参数后重新点击“应用运镜”。
          </p>

          <div class="motion-plan-segment-list">
            <button
              v-for="(segment, index) in cameraMotionPlan.segments"
              :key="`${segment.templateId}-${segment.startTime}-${index}`"
              class="motion-plan-segment-button"
              :class="{ active: index === selectedCameraMotionPlanSegmentIndex }"
              type="button"
              @click="selectCameraMotionPlanSegment(index)"
            >
              <span class="motion-plan-segment-heading">
                <strong>片段 {{ index + 1 }}</strong>
                <span>{{ formatCameraMotionPlanSegmentKind(segment) }}</span>
              </span>
              <span>{{ segment.clip.name }}</span>
              <span>
                {{ formatNumber(segment.startTime, 2) }}s →
                {{ formatNumber(segment.endTime, 2) }}s ·
                {{ segment.keyframes.length }} 帧 ·
                {{ formatCameraMotionPlanSegmentInterpolation(segment) }}
              </span>
            </button>
          </div>

          <div
            v-if="selectedCameraMotionPlanSegment"
            class="motion-plan-segment-detail"
          >
            <div class="motion-plan-detail-header">
              <div>
                <span class="choice-section-title">当前片段局部关键帧</span>
                <strong>
                  片段 {{ selectedCameraMotionPlanSegmentIndex + 1 }} ·
                  {{ selectedCameraMotionPlanSegment.clip.name }}
                </strong>
              </div>
              <span>{{ selectedCameraMotionPlanSegment.keyframes.length }} 帧</span>
            </div>
            <div class="motion-plan-keyframe-list">
              <div
                v-for="(frame, index) in selectedCameraMotionPlanSegment.keyframes"
                :key="`${selectedCameraMotionPlanSegment.templateId}-${frame.time}-${index}`"
                class="motion-plan-keyframe-row"
              >
                <div>
                  <strong>关键帧 {{ index + 1 }}</strong>
                  <span>
                    全局 t={{
                      formatNumber(selectedCameraMotionPlanSegment.startTime + frame.time, 2)
                    }}s · 局部 t={{ formatNumber(frame.time, 2) }}s
                  </span>
                  <span>
                    P({{ formatNumber(frame.position[0], 2) }},
                    {{ formatNumber(frame.position[1], 2) }},
                    {{ formatNumber(frame.position[2], 2) }}) ·
                    T({{ formatNumber(frame.target[0], 2) }},
                    {{ formatNumber(frame.target[1], 2) }},
                    {{ formatNumber(frame.target[2], 2) }})
                  </span>
                  <span>
                    FOV {{ formatNumber(frame.fov, 1) }}° · Roll
                    {{ formatNumber(frame.roll, 2) }}
                  </span>
                </div>
                <button
                  class="small-icon-button"
                  type="button"
                  title="切换到该组合关键帧"
                  @click="applyCameraMotionPlanKeyframe(frame)"
                >
                  定位
                </button>
              </div>
            </div>
          </div>
        </div>

        <template v-else>
        <div class="keyframe-toolbar">
          <div class="keyframe-toolbar-main">
            <span class="choice-section-title">Clip 关键帧</span>
            <span class="panel-note">时间必须保持递增</span>
          </div>
          <div class="keyframe-toolbar-actions">
            <button
              class="panel-button panel-button-subtle clear-keyframes-button"
              type="button"
              :disabled="cameraKeyframes.length === 0"
              @click="clearKeyframes"
            >
              <span class="panel-button-label">清空</span>
            </button>
            <button
              class="panel-button icon-action-button"
              type="button"
              :disabled="!engine"
              @click="addKeyframeFromCamera"
            >
              <span class="panel-button-label">+</span>
            </button>
          </div>
        </div>

        <div class="camera-fov-editor">
          <div class="camera-fov-header">
            <span class="choice-section-title">当前镜头 FOV</span>
            <strong>{{ formatNumber(currentCameraFov, 1) }}°</strong>
          </div>
          <div class="camera-fov-controls">
            <input
              v-model.number="currentCameraFov"
              class="panel-range"
              type="range"
              :min="CAMERA_FOV_MIN"
              :max="CAMERA_FOV_MAX"
              step="0.1"
              @input="updateCurrentCameraFov"
            />
            <input
              v-model.number="currentCameraFov"
              class="keyframe-input fov-number-input"
              type="number"
              :min="CAMERA_FOV_MIN"
              :max="CAMERA_FOV_MAX"
              step="0.1"
              @change="updateCurrentCameraFov"
            />
          </div>
        </div>

        <div v-if="selectedKeyframe" class="selected-keyframe-editor">
          <div class="selected-keyframe-header">
            <div>
              <span class="choice-section-title">当前选中关键帧</span>
              <strong>第 {{ selectedKeyframeIndex + 1 }} 帧</strong>
            </div>
            <button
              class="small-icon-button"
              type="button"
              title="切换到该关键帧"
              @click="applyKeyframeToCamera(selectedKeyframe)"
            >
              定位
            </button>
          </div>
          <label class="keyframe-time-field selected-keyframe-time">
            <span>时间</span>
            <input
              v-model.number="selectedKeyframe.time"
              class="keyframe-input"
              type="number"
              min="0"
              step="0.01"
              @change="normalizeKeyframeTime(selectedKeyframeIndex)"
            />
          </label>
          <div class="keyframe-vector-grid">
            <div class="keyframe-vector-editor">
              <span class="keyframe-vector-title">位置</span>
              <label
                v-for="axis in CAMERA_VECTOR_AXES"
                :key="`selected-position-${axis.label}`"
                class="keyframe-axis-field"
              >
                <span>{{ axis.label }}</span>
                <input
                  v-model.number="selectedKeyframe.position[axis.axis]"
                  class="keyframe-input"
                  type="number"
                  step="0.001"
                  @change="normalizeKeyframeVector(selectedKeyframe, 'position', axis.axis)"
                />
              </label>
            </div>

            <div class="keyframe-vector-editor">
              <span class="keyframe-vector-title">目标</span>
              <label
                v-for="axis in CAMERA_VECTOR_AXES"
                :key="`selected-target-${axis.label}`"
                class="keyframe-axis-field"
              >
                <span>{{ axis.label }}</span>
                <input
                  v-model.number="selectedKeyframe.target[axis.axis]"
                  class="keyframe-input"
                  type="number"
                  step="0.001"
                  @change="normalizeKeyframeVector(selectedKeyframe, 'target', axis.axis)"
                />
              </label>
            </div>

            <label class="keyframe-fov-field">
              <span class="keyframe-vector-title">FOV</span>
              <input
                v-model.number="selectedKeyframe.fov"
                class="keyframe-input"
                type="number"
                :min="CAMERA_FOV_MIN"
                :max="CAMERA_FOV_MAX"
                step="0.1"
                @change="normalizeKeyframeFov(selectedKeyframe)"
              />
            </label>
          </div>
        </div>

        <div class="keyframe-list">
          <p v-if="cameraKeyframes.length === 0" class="panel-note empty-keyframe-note">
            暂无关键帧，可点击右上角加号从当前镜头捕获一帧。
          </p>
          <div
            v-for="(frame, index) in cameraKeyframes"
            :key="frame.id"
            class="keyframe-row"
            :class="{ active: frame.id === selectedKeyframeId }"
          >
            <div class="keyframe-row-header">
              <button class="keyframe-summary-button" type="button" @click="selectKeyframe(frame)">
                <strong>关键帧 {{ index + 1 }}</strong>
                <span>t={{ formatNumber(frame.time, 2) }}s</span>
                <span>
                  P({{ formatNumber(frame.position[0], 2) }},
                  {{ formatNumber(frame.position[1], 2) }},
                  {{ formatNumber(frame.position[2], 2) }})
                </span>
                <span>FOV {{ formatNumber(frame.fov, 1) }}°</span>
              </button>
              <div class="keyframe-actions">
                <button
                  class="small-icon-button"
                  type="button"
                  title="切换到该关键帧"
                  @click="applyKeyframeToCamera(frame)"
                >
                  定位
                </button>
                <button
                  class="small-icon-button danger"
                  type="button"
                  title="删除关键帧"
                  :disabled="cameraKeyframes.length <= 1"
                  @click="removeKeyframe(index)"
                >
                  -
                </button>
              </div>
            </div>
          </div>
        </div>
        </template>
      </SceneGroupCard>
    </SceneControlPanel>

    <div
      v-if="isVideoExporting"
      class="video-export-viewport-freeze"
      aria-live="polite"
    >
      <img
        v-if="videoExportViewportSnapshotUrl"
        class="video-export-viewport-snapshot"
        :src="videoExportViewportSnapshotUrl"
        alt=""
      />
      <div class="video-export-viewport-status">
        <strong>{{ videoExportProgressPercent }}%</strong>
        <span>{{ videoExportMessage || '正在导出视频' }}</span>
        <div class="video-export-progress-bar">
          <span :style="{ width: `${videoExportProgressPercent}%` }"></span>
        </div>
        <button class="video-export-cancel-button" type="button" @click="cancelVideoExport">
          取消
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.effect-animation-page {
  --panel-gap: 0px;
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(340px, 3fr);
  gap: var(--panel-gap);
  padding: var(--panel-gap);
  box-sizing: border-box;
  align-items: stretch;
  background: #02050a;
  color: #eef4ff;
}

.render-container {
  grid-column: 1;
  grid-row: 1;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  position: relative;
  z-index: 0;
  overflow: hidden;
}

.render-container :deep(canvas) {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.render-container-exporting {
  pointer-events: none;
}

.video-export-viewport-freeze {
  grid-column: 1;
  grid-row: 1;
  position: relative;
  z-index: 30;
  display: grid;
  place-items: center;
  overflow: hidden;
  pointer-events: auto;
  background: #02050a;
}

.video-export-viewport-snapshot {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #02050a;
}

.video-export-viewport-status {
  position: relative;
  z-index: 1;
  width: min(360px, calc(100% - 48px));
  display: grid;
  gap: 10px;
  padding: 16px;
  border: 1px solid rgba(151, 169, 197, 0.24);
  border-radius: 8px;
  background: rgba(4, 10, 18, 0.82);
  box-shadow: 0 18px 54px rgba(0, 0, 0, 0.36);
  color: #eef4ff;
  text-align: center;
  backdrop-filter: blur(10px);
}

.video-export-viewport-status strong {
  font-size: 28px;
  line-height: 1;
}

.video-export-viewport-status span {
  min-height: 18px;
  color: #c7d5e6;
  font-size: 13px;
  line-height: 1.4;
}

.video-export-cancel-button {
  justify-self: center;
  min-width: 96px;
  height: 34px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  color: #eef4ff;
  font-weight: 700;
  cursor: pointer;
}

.video-export-cancel-button:hover {
  background: rgba(255, 255, 255, 0.14);
}

.preset-file-input {
  display: none;
}

.preset-io-message {
  margin: 0;
}

.video-export-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.video-export-progress {
  display: grid;
  gap: 7px;
}

.video-export-progress-bar {
  height: 8px;
  overflow: hidden;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
}

.video-export-progress-bar span {
  display: block;
  width: 0;
  height: 100%;
  border-radius: inherit;
  background: #43dcc4;
  transition: width 120ms ease;
}

.video-export-download {
  text-decoration: none;
}

.choice-section,
.effect-groups,
.effect-group,
.box-controls,
.reveal-param-panel,
.keyframe-list,
.selected-keyframe-editor,
.keyframe-toolbar-main,
.easing-group-list,
.easing-group {
  display: grid;
}

.choice-section,
.effect-groups {
  gap: 12px;
}

.effect-group,
.box-controls,
.reveal-param-panel,
.keyframe-list,
.selected-keyframe-editor,
.easing-group-list {
  gap: 10px;
}

.choice-section-title {
  color: #9fb0c6;
  font-size: 12px;
  font-weight: 700;
}

.easing-group {
  gap: 7px;
}

.easing-group-title {
  color: #c3d6e4;
  font-size: 12px;
  font-weight: 700;
}

:deep(.easing-grid) {
  gap: 8px;
}

:deep(.easing-button) {
  min-height: 56px;
  padding: 8px 9px;
}

:deep(.easing-button .panel-button-label) {
  font-size: 13px;
}

:deep(.easing-button .panel-button-meta) {
  font-size: 11px;
}

.effect-group-header,
.reveal-panel-header,
.keyframe-toolbar,
.camera-fov-header,
.camera-fov-controls,
.selected-keyframe-header,
.keyframe-row-header,
.keyframe-actions,
.keyframe-toolbar-actions {
  display: flex;
  align-items: center;
}

.effect-group-header,
.keyframe-toolbar,
.keyframe-row-header,
.camera-fov-header {
  justify-content: space-between;
  gap: 10px;
}

.effect-category {
  color: #f5fbff;
  font-size: 13px;
  font-weight: 700;
}

.effect-mode {
  color: rgba(210, 236, 245, 0.72);
  font-size: 12px;
}

.effect-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.effect-button {
  min-height: 46px;
  border: 1px solid rgba(137, 205, 225, 0.35);
  border-radius: 6px;
  background: rgba(45, 123, 153, 0.26);
  color: #f5fbff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: grid;
  align-content: center;
  gap: 3px;
  padding: 7px 10px;
  text-align: left;
}

.effect-button.active {
  border-color: rgba(110, 230, 255, 0.82);
  background: rgba(38, 150, 188, 0.46);
}

.effect-button.reveal-apply-button {
  min-height: 34px;
  padding: 6px 10px;
  text-align: center;
}

.effect-name,
.effect-detail {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.effect-detail {
  color: rgba(217, 239, 247, 0.7);
  font-size: 12px;
  font-weight: 500;
}

.box-field {
  display: grid;
  gap: 7px;
  min-width: 0;
}

.box-label {
  color: rgba(217, 239, 247, 0.76);
  font-size: 12px;
  font-weight: 600;
}

.effect-select {
  width: 100%;
  min-width: 0;
  height: 40px;
  border: 1px solid rgba(137, 205, 225, 0.28);
  border-radius: 8px;
  background: rgba(6, 10, 16, 0.66);
  color: #f3f8fc;
  font-size: 13px;
  outline: none;
  padding: 0 10px;
}

.selected-effect-summary {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: rgba(217, 239, 247, 0.72);
  font-size: 12px;
}

.selected-effect-summary strong {
  color: #f5fbff;
  font-weight: 700;
}

.motion-param-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.motion-meta-grid {
  display: grid;
  gap: 8px;
}

.effect-select-export-button {
  width: 100%;
}

.direction-actions {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 6px;
}

.effect-mode-toggle {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.direction-button {
  min-width: 0;
  min-height: 32px;
  border: 1px solid rgba(137, 205, 225, 0.28);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.06);
  color: #f5fbff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.direction-button.active {
  border-color: rgba(110, 230, 255, 0.82);
  background: rgba(38, 150, 188, 0.46);
}

.duration-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 68px;
  gap: 8px;
  align-items: center;
}

.duration-slider,
.panel-range {
  width: 100%;
  min-width: 0;
  accent-color: #43dcc4;
}

.duration-input,
.keyframe-input {
  min-width: 0;
  border: 1px solid rgba(152, 191, 214, 0.18);
  border-radius: 10px;
  background: rgba(6, 10, 16, 0.52);
  color: #f3f8fc;
  outline: none;
}

.duration-input {
  height: 32px;
  padding: 0 6px;
  font-size: 12px;
}

.box-color-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.color-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.color-input {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid rgba(137, 205, 225, 0.28);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

.color-swatch {
  flex: 1 1 0;
  min-width: 18px;
  height: 24px;
  border: 1px solid rgba(255, 255, 255, 0.26);
  border-radius: 6px;
  cursor: pointer;
}

.color-swatch.active {
  border-color: rgba(255, 255, 255, 0.92);
  box-shadow: 0 0 0 2px rgba(110, 230, 255, 0.34);
}

.reveal-param-panel + .reveal-param-panel {
  padding-top: 12px;
  border-top: 1px solid rgba(137, 205, 225, 0.18);
}

.reveal-panel-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 74px;
  gap: 10px;
  align-items: center;
}

.reveal-panel-title {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.reveal-param-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.reveal-color-grid {
  display: grid;
  gap: 10px;
}

.color-control-row {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
}

.color-intensity-row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 68px;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.color-intensity-label {
  color: rgba(217, 239, 247, 0.72);
  font-size: 11px;
}

.motion-plan-preview,
.motion-plan-segment-list,
.motion-plan-segment-detail,
.motion-plan-keyframe-list,
.motion-plan-keyframe-row > div {
  display: grid;
  gap: 10px;
}

.motion-plan-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.motion-plan-note {
  margin: 0;
  padding: 10px;
  border: 1px solid rgba(125, 211, 252, 0.18);
  border-radius: 10px;
  background: rgba(14, 165, 233, 0.06);
  line-height: 1.55;
}

.motion-plan-segment-button {
  display: grid;
  gap: 5px;
  width: 100%;
  min-width: 0;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: rgba(2, 8, 14, 0.36);
  color: #dceaf4;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 160ms ease,
    background-color 160ms ease,
    transform 160ms ease;
}

.motion-plan-segment-button:hover,
.motion-plan-segment-button.active {
  border-color: rgba(125, 211, 252, 0.48);
  background: rgba(14, 165, 233, 0.12);
}

.motion-plan-segment-button:hover {
  transform: translateY(-1px);
}

.motion-plan-segment-button > span:not(.motion-plan-segment-heading) {
  overflow: hidden;
  color: #90aabe;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.motion-plan-segment-heading,
.motion-plan-detail-header,
.motion-plan-keyframe-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.motion-plan-segment-heading strong,
.motion-plan-detail-header strong,
.motion-plan-keyframe-row strong {
  color: #f7fbff;
  font-size: 13px;
}

.motion-plan-segment-heading span,
.motion-plan-detail-header > span {
  color: #9fb0c6;
  font-size: 12px;
}

.motion-plan-segment-detail {
  padding: 12px;
  border: 1px solid rgba(125, 211, 252, 0.16);
  border-radius: 14px;
  background: rgba(14, 165, 233, 0.06);
}

.motion-plan-detail-header > div {
  display: grid;
  gap: 4px;
}

.motion-plan-keyframe-list {
  max-height: 420px;
  padding-right: 3px;
  overflow-y: auto;
}

.motion-plan-keyframe-row {
  padding: 9px 10px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  background: rgba(2, 8, 14, 0.36);
}

.motion-plan-keyframe-row > div {
  gap: 4px;
  min-width: 0;
}

.motion-plan-keyframe-row span {
  overflow: hidden;
  color: #90aabe;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.keyframe-toolbar-main {
  gap: 3px;
  min-width: 0;
}

.keyframe-toolbar-actions {
  gap: 8px;
  flex: 0 0 auto;
}

.clear-keyframes-button {
  min-height: 38px;
  padding: 8px 12px;
}

:deep(.icon-action-button) {
  width: 42px;
  min-height: 38px;
  padding: 8px;
  text-align: center;
}

:deep(.icon-action-button .panel-button-label) {
  font-size: 20px;
  line-height: 1;
}

.camera-fov-editor {
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.03);
}

.camera-fov-header strong {
  color: #f7fbff;
  font-size: 14px;
}

.camera-fov-controls {
  gap: 10px;
}

.fov-number-input {
  width: 72px;
  flex: 0 0 auto;
}

.empty-keyframe-note {
  margin: 0;
  padding: 12px;
  border-radius: 12px;
  border: 1px dashed rgba(152, 191, 214, 0.18);
  background: rgba(255, 255, 255, 0.03);
}

.keyframe-row {
  display: grid;
  padding: 8px 10px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(2, 8, 14, 0.36);
  content-visibility: auto;
  contain-intrinsic-size: 52px;
}

.keyframe-row.active {
  border-color: rgba(125, 211, 252, 0.46);
  background: rgba(14, 165, 233, 0.12);
}

.keyframe-row-header strong {
  min-width: 72px;
  color: #f7fbff;
  font-size: 13px;
}

.keyframe-summary-button {
  display: grid;
  grid-template-columns: minmax(76px, auto) repeat(3, minmax(0, auto));
  gap: 8px;
  align-items: center;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #90aabe;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.keyframe-summary-button span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
}

.selected-keyframe-editor {
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(125, 211, 252, 0.16);
  background: rgba(14, 165, 233, 0.06);
}

.selected-keyframe-header {
  justify-content: space-between;
  gap: 10px;
}

.selected-keyframe-header > div {
  display: grid;
  gap: 4px;
}

.selected-keyframe-header strong {
  color: #f7fbff;
  font-size: 13px;
}

.keyframe-time-field,
.keyframe-axis-field,
.keyframe-fov-field {
  display: grid;
  gap: 4px;
}

.keyframe-time-field {
  width: 92px;
}

.selected-keyframe-time {
  width: auto;
}

.keyframe-time-field span,
.keyframe-axis-field span {
  color: #90aabe;
  font-size: 11px;
  font-weight: 700;
}

.keyframe-actions {
  gap: 6px;
}

.small-icon-button {
  min-height: 32px;
  padding: 6px 9px;
  border: 1px solid rgba(141, 179, 201, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  color: #eef6fb;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.small-icon-button:hover:not(:disabled) {
  border-color: rgba(160, 209, 238, 0.42);
  background: rgba(124, 188, 220, 0.12);
}

.small-icon-button.danger {
  min-width: 32px;
  color: #ffd5d5;
}

.small-icon-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.keyframe-vector-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
}

.keyframe-vector-editor {
  display: grid;
  grid-template-columns: 42px repeat(3, minmax(0, 1fr));
  gap: 7px;
  align-items: end;
}

.keyframe-vector-title {
  align-self: center;
  color: #c3d6e4;
  font-size: 12px;
  font-weight: 700;
}

.keyframe-fov-field {
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: center;
}

.keyframe-input {
  width: 100%;
  min-height: 34px;
  padding: 7px 8px;
  font-size: 12px;
}

.keyframe-input:focus {
  border-color: rgba(121, 201, 197, 0.55);
  box-shadow: 0 0 0 2px rgba(121, 201, 197, 0.14);
}

.particle-advanced-controls {
  padding: 10px 12px;
  border: 1px solid rgba(141, 179, 201, 0.16);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.025);
}

.particle-advanced-controls summary {
  color: #c3d6e4;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.particle-advanced-controls[open] summary {
  margin-bottom: 8px;
}

.particle-phase-weight-hint {
  margin: 0 0 12px;
  color: #8fa8ba;
  font-size: 11px;
  line-height: 1.5;
}

@media (max-width: 900px) {
  /* 面板在移动端已改为悬浮展示（见 SceneControlPanel），画布铺满整页即可 */
  .effect-animation-page {
    grid-template-columns: 1fr;
  }

  .reveal-param-grid,
  .box-color-grid,
  .video-export-grid,
  .motion-plan-summary-grid,
  .effect-actions {
    grid-template-columns: 1fr;
  }
}
</style>
